import React, { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "lightbox2/dist/css/lightbox.min.css";
import "lightbox2";
import "./RaiseComplaint.css";
import "./ServiceRequestQueryHistory.css";
import FileUploadPanelLayout from "../components/FileUploadPanelLayout";
import { complaintAPI, masterDataAPI, workflowAPI } from "../services/api";
import { getUser } from "../utils/auth";
import Swal from "sweetalert2";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import ReplyIcon from "@mui/icons-material/Reply";
import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/Check";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import PrintIcon from "@mui/icons-material/Print";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useSidebar } from "../contexts/SidebarContext";
import { hasUserType, isBcmUser, isDosAdminUser } from "../utils/auth";
import { escapeHtml, escapeHtmlAttr, safeCssColor } from "../utils/xss";
import { htmlFromPrintResponse, openTrustedPrintHtml } from "../utils/printHtml";
import { materialRowShowsComponentDetailsIcon } from "../utils/componentDetailsBusinessVertical";

/**
 * ViewApproverServiceRequest - View-only page for approvers to view Service Request details
 * This page displays all SR information filled by SE in read-only mode.
 * Sections displayed:
 * - Basic Information (SR Number, Status, Category, Created Date, Raised By, Assigned To)
 * - Invoice Details
 * - Customer Details (including End Consumer, if B2C)
 * - Product / Material Details
 * - Complaint Quantity & Value Details
 * - Sample Information (if applicable)
 * - Checklist Responses (product-wise)
 * - Uploaded Attachments / Images
 * - Previous Complaints (if exists)
 * - Approval History & Workflow Journey
 * - Query History (threaded view, read-only)
 * - Audit Trail (read-only)
 */

function resolveJourneyIconTatDays(step) {
  const values = [
    step?.HistoryTATDays ?? step?.historyTATDays,
    step?.tat,
    step?.TATDays ?? step?.tatDays,
    step?.ConfigTATDays ?? step?.configTATDays,
  ];
  let fallback = null;
  for (const v of values) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (!Number.isFinite(n)) continue;
    if (fallback == null) fallback = n;
    if (n > 0) return String(n);
  }
  return fallback != null ? String(fallback) : "0";
}

function resolveJourneyIconActualTat(step) {
  const raw =
    step?.ActualTAT ??
    step?.actualTAT ??
    step?.actualTat ??
    step?.ActualTATDays ??
    step?.actualTATDays ??
    null;
  if (raw == null || raw === "" || String(raw).toUpperCase() === "N/A") return "0";
  return String(raw);
}

function readJourneyIconTipFromEl(el, tat, actualTat, slaTip) {
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return {
    left: rect.left + rect.width / 2,
    top: rect.top,
    tat,
    actualTat,
    slaTip: slaTip || "",
  };
}

function JourneyIconTatTooltip({ tip }) {
  if (!tip || typeof document === "undefined" || !document.body) return null;
  return createPortal(
    <div
      className={`journey-icon-tat-tooltip${tip.slaTip ? " is-breached" : ""}`}
      style={{ left: tip.left, top: tip.top }}
    >
      <div className="journey-icon-tat-tooltip-shine" />
      <div className="journey-icon-tat-tooltip-grid">
        <div className="journey-icon-tat-tooltip-col">
          <span className="journey-icon-tat-tooltip-label">TAT</span>
          <span className="journey-icon-tat-tooltip-value">{tip.tat}</span>
        </div>
        <div className="journey-icon-tat-tooltip-col">
          <span className="journey-icon-tat-tooltip-label">Actual TAT</span>
          <span className="journey-icon-tat-tooltip-value actual">{tip.actualTat}</span>
        </div>
      </div>
      {tip.slaTip ? <div className="journey-icon-tat-tooltip-note">{tip.slaTip}</div> : null}
    </div>,
    document.body
  );
}

const ViewApproverServiceRequest = () => {
  const isDosAdminApprover = isDosAdminUser() || hasUserType("UT0000024");
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { collapseSidebar, restoreSidebar } = useSidebar();
  const childSrCodeFromQuery =
    searchParams.get("childSrCode")?.trim() || "";

  const currentUserIdCode = (() => {
    try {
      const user = getUser();
      return (
        user?.userIdCode ||
        user?.UserIdCode ||
        user?.userId ||
        user?.UserId ||
        user?.userCode ||
        user?.UserCode ||
        user?.email ||
        user?.id ||
        ""
      );
    } catch {
      return "";
    }
  })();
  const [loading, setLoading] = useState(true);
  const [printDownloading, setPrintDownloading] = useState(false);
  const [printProgress, setPrintProgress] = useState(null); // percent (0-100) or null when unknown
  const [printMenuOpen, setPrintMenuOpen] = useState(false);
  const [srData, setSrData] = useState(null);
  const [journeyIconTip, setJourneyIconTip] = useState(null);
  const showMaterialComponentDetailsColumn = useMemo(() => {
    const raw = srData?.products;
    if (!Array.isArray(raw)) return false;
    return raw.some((row) => materialRowShowsComponentDetailsIcon(row));
  }, [srData?.products]);
  const productTableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const journeyStatusTableRef = useRef(null);
  const journeyStatusDataTableRef = useRef(null);
  const productDataRef = useRef({});
  const historyTableRef = useRef(null);
  const historyDataTableInstanceRef = useRef(null);
  const [showEndCustomer, setShowEndCustomer] = useState(false);
  
  // Previous Complaints States
  const [previousComplaintsCount, setPreviousComplaintsCount] = useState(0);
  const [previousComplaintsData, setPreviousComplaintsData] = useState([]);
  const [showPreviousComplaintsTable, setShowPreviousComplaintsTable] = useState(false);
  const [showPreviousComplaintsDetailModal, setShowPreviousComplaintsDetailModal] = useState(false);
  const [selectedComplaintIndex, setSelectedComplaintIndex] = useState(null);
  
  // Product Details Modal States
  const [productDetailsOpen, setProductDetailsOpen] = useState(false);
  const [selectedProductData, setSelectedProductData] = useState(null);
  const productDetailsModalRef = useRef(null);
  const prevProductDetailsOpenRef = useRef(productDetailsOpen);

  // Component Details Modal States
  const [componentDetailsOpen, setComponentDetailsOpen] = useState(false);
  const [selectedComponentProductKey, setSelectedComponentProductKey] = useState(null);
  const componentDetailsModalRef = useRef(null);
  const componentDetailsRef = useRef({});
  const prevComponentDetailsOpenRef = useRef(componentDetailsOpen);
  const [componentDetailsData, setComponentDetailsData] = useState({
    materialGroupDesc: "",
    componentCategory: "",
    componentList: "",
    componentQty: "",
    componentIssue: "",
    doorMaterialCode: "",
    componentIssueRemarks: "",
  });

  // SR Checklist Modal States
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistData, setChecklistData] = useState({
    category: "",
    productName: "",
    productCode: "",
    orderNo: "",
    quantity: "",
  });
  const newLine = () => ({ id: Date.now(), type: "", qty: "" });
  const [complaintLines, setComplaintLines] = useState([newLine()]);
  const [complaintTypes, setComplaintTypes] = useState([]);
  const [loadingComplaintTypes, setLoadingComplaintTypes] = useState(false);
  const checklistModalRef = useRef(null);
  const modalInstanceRef = useRef(null);

  // Raise Query Modal States
  const [raiseQueryOpen, setRaiseQueryOpen] = useState(false);
  const [raiseQueryData, setRaiseQueryData] = useState({
    name: "",
    query: "",
  });
  const [nameOptions, setNameOptions] = useState([]);
  const [raiseQuerySubmitting, setRaiseQuerySubmitting] = useState(false);
  const raiseQuerySubmittingRef = useRef(false);
  raiseQuerySubmittingRef.current = raiseQuerySubmitting;
  const raiseQueryModalRef = useRef(null);
  const raiseQueryModalInstanceRef = useRef(null);

  // Query Response Modal States (for SE)
  const [responseQueryOpen, setResponseQueryOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [responseQueryData, setResponseQueryData] = useState({
    responseText: "",
    attachments: [],
  });
  const [queries, setQueries] = useState([]);
  const [loadingQueries, setLoadingQueries] = useState(false);
  const [queryHistoryFilter, setQueryHistoryFilter] = useState(null);
  const openQueryHistoryDrilldownRef = useRef(null);
  const responseQueryModalRef = useRef(null);
  const responseQueryModalInstanceRef = useRef(null);
  const [responseQuerySubmitting, setResponseQuerySubmitting] = useState(false);
  const responseQuerySubmittingRef = useRef(false);
  responseQuerySubmittingRef.current = responseQuerySubmitting;

  // Approve/Reject Modal States
  const [approveRejectOpen, setApproveRejectOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveData, setApproveData] = useState({
    items: [],
    remarks: "",
    factoryIntentNo: "",
    factoryIntentDate: "",
    settlementType: "",
  });
  const approveModalRef = useRef(null);
  const approveModalInstanceRef = useRef(null);
  const approveRejectModalRef = useRef(null);
  const approveRejectModalInstanceRef = useRef(null);
  const approveRejectLastFocusRef = useRef(null);

  // Format date to dd-mm-yyyy
  const formatDate = (dateString) => {
    const normalized =
      dateString === null || dateString === undefined ? "" : String(dateString);

    if (!normalized || normalized === "" || normalized === "-") {
      return normalized || "-";
    }

    try {
      // Try to parse the date
      const date = new Date(normalized);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        // If it's not a valid date object, try to parse manually
        // Handle formats like "23 Jul 2024" or "26-06-2024"
        const parts = normalized.trim().split(/[\s-/]+/);

        if (parts.length === 3) {
          // Check if it's already in dd-mm-yyyy or similar format
          if (
            parts[0].length <= 2 &&
            parts[1].length <= 2 &&
            parts[2].length === 4
          ) {
            return normalized; // Already in correct format
          }

          // Handle "23 Jul 2024" format
          const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
          const monthIndex = monthNames.findIndex((m) =>
            normalized.includes(m)
          );

          if (monthIndex !== -1) {
            const day = parts[0].padStart(2, "0");
            const month = String(monthIndex + 1).padStart(2, "0");
            const year = parts[2];
            return `${day}-${month}-${year}`;
          }
        }

        return normalized; // Return as-is if we can't parse it
      }

      // Format as dd-mm-yyyy
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      return normalized; // Return normalized string if any error occurs
    }
  };

  // Prevent runtime crashes if backend returns unexpected object values.
  const renderSafeText = (value, fallback = "") => {
    if (value === null || value === undefined) return fallback;
    const t = typeof value;
    if (t === "string" || t === "number" || t === "boolean") return String(value);
    return fallback;
  };

  // Ensure we never pass an object/array as a React child (fixes "Objects are not valid as a React child").
  const safeChild = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
    if (Array.isArray(value)) return value.map(safeChild).filter(Boolean).join(", ");
    if (typeof value === "object") return "";
    return String(value);
  };

  const getRoleCode = (roleNameOrCode) => {
    const role = String(roleNameOrCode || "").trim();
    if (!role) return "";
    if (role.length <= 4 && /^[A-Za-z]+$/.test(role)) return role.toUpperCase();
    const parts = role.split(/\s+/).filter(Boolean);
    return parts.map((p) => p[0]).join("").toUpperCase();
  };

  const isInitiatorSEStep = (step) => {
    const designation = String(step?.designation || "").toUpperCase();
    const status = String(step?.status || "").toUpperCase();
    const roleCode = getRoleCode(step?.designation || step?.status || "");
    return (
      designation === "SE" ||
      status === "SE" ||
      roleCode === "SE" ||
      designation.includes("INITIATOR (SE)") ||
      status.includes("INITIATOR (SE)") ||
      designation.includes("SALES EXECUTIVE") ||
      status.includes("SALES EXECUTIVE")
    );
  };

  // Build a display-only query object with ONLY primitives (no spread, no API objects in JSX).
  const toDisplayQuery = (q) => {
    if (!q || typeof q !== "object") return null;
    const safe = (v, fallback = "") =>
      typeof v === "string" || typeof v === "number" || typeof v === "boolean"
        ? String(v)
        : fallback;
    const safeNum = (v) => (typeof v === "number" ? v : null);
    let raisedOnDate = null;
    let respondedOnDate = null;
    if (q.raisedOn instanceof Date && !isNaN(q.raisedOn.getTime())) raisedOnDate = q.raisedOn;
    else if (typeof q.raisedOn === "string" || typeof q.raisedOn === "number") {
      const d = new Date(q.raisedOn);
      if (!isNaN(d.getTime())) raisedOnDate = d;
    }
    if (q.respondedOn instanceof Date && !isNaN(q.respondedOn.getTime())) respondedOnDate = q.respondedOn;
    else if (typeof q.respondedOn === "string" || typeof q.respondedOn === "number") {
      const d = new Date(q.respondedOn);
      if (!isNaN(d.getTime())) respondedOnDate = d;
    }
    const formattedQueryDate = raisedOnDate
      ? formatDate(raisedOnDate.toISOString().split("T")[0])
      : "N/A";
    const formattedQueryTime = raisedOnDate
      ? raisedOnDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
      : "N/A";
    const formattedResponseDate = respondedOnDate
      ? formatDate(respondedOnDate.toISOString().split("T")[0])
      : null;
    const formattedResponseTime = respondedOnDate
      ? respondedOnDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
      : null;
    const formattedRaisedOnForModal = raisedOnDate
      ? raisedOnDate.toLocaleString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A";
    const status = safe(q.status, "Pending");
    return {
      queryId: safeNum(q.queryId),
      queryText: safe(q.queryText, ""),
      raisedByUserId: safe(q.raisedBy ?? q.RaisedBy ?? q.raisedByUserId ?? "", ""),
      raisedByName: safe(q.raisedByName, ""),
      raisedToUserId: safe(q.raisedToUserId, ""),
      raisedToUserName: safe(q.raisedToUserName, ""),
      raisedToRoleName: safe(q.raisedToRoleName, ""),
      responseText: safe(q.responseText, ""),
      respondedByName: safe(q.respondedByName, ""),
      status,
      isResolved: typeof q.isResolved === "boolean" ? q.isResolved : Boolean(q.isResolved),
      formattedQueryDate,
      formattedQueryTime,
      formattedResponseDate,
      formattedResponseTime,
      formattedRaisedOnForModal,
    };
  };

  const expandQueryHistorySection = () => {
    const el = document.getElementById("collapseQueryHistory");
    if (!el) return;
    try {
      const Collapse = window.bootstrap?.Collapse;
      if (Collapse) {
        const instance = Collapse.getOrCreateInstance(el, { toggle: false });
        instance.show();
      } else {
        el.classList.add("show");
      }
    } catch {
      el.classList.add("show");
    }
    const toggle = document.querySelector('[data-bs-target="#collapseQueryHistory"]');
    if (toggle) toggle.setAttribute("aria-expanded", "true");
    setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  openQueryHistoryDrilldownRef.current = (userId, userName) => {
    setQueryHistoryFilter({
      userId: String(userId || "").trim(),
      userName: String(userName || "").trim(),
    });
    expandQueryHistorySection();
  };

  const clearQueryHistoryFilter = () => setQueryHistoryFilter(null);

  const visibleQueryHistory = (() => {
    if (!queryHistoryFilter) return queries;
    const filterId = String(queryHistoryFilter.userId || "").trim().toUpperCase();
    const filterName = String(queryHistoryFilter.userName || "").trim().toLowerCase();
    return queries.filter((q) => {
      const byId = String(q.raisedByUserId || "").trim().toUpperCase();
      const byName = String(q.raisedByName || "").trim().toLowerCase();
      if (filterId && byId && byId === filterId) return true;
      if (filterName && byName && byName === filterName) return true;
      return false;
    });
  })();

  const setQueriesSafe = (maybeArray) => {
    const arr = Array.isArray(maybeArray) ? maybeArray : [];
    const displayList = [];
    for (let i = 0; i < arr.length; i++) {
      const d = toDisplayQuery(arr[i]);
      if (d) displayList.push(d);
    }
    setQueries(displayList);
  };

  // SR Checklist Modal Functions
  const fetchComplaintTypes = async () => {
    setLoadingComplaintTypes(true);
    try {
      const marketCode = srData?.MarketCode || srData?.marketCode || "";
      const marketName = srData?.MarketName || srData?.marketName || "";
      let market = null;

      if (marketCode || marketName) {
        const codeUpper = marketCode.toUpperCase();
        const nameUpper = marketName.toUpperCase();

        if (
          codeUpper === "M001" ||
          codeUpper.includes("DOM") ||
          nameUpper.includes("DOMESTIC") ||
          codeUpper === "DOMESTIC" ||
          nameUpper === "DOMESTIC"
        ) {
          market = "Domestic";
        } else if (
          codeUpper === "M002" ||
          codeUpper.includes("EXP") ||
          nameUpper.includes("EXPORT") ||
          codeUpper === "EXPORT" ||
          nameUpper === "EXPORT"
        ) {
          market = "Export";
        }
      }

      const response = await masterDataAPI.getComplaintTypes(market);

      if (response && response.success && response.data?.complaintTypes) {
        const types =
          response.data.complaintTypes || response.data.ComplaintTypes || [];
        const activeTypes = types.filter((type) => {
          const isActive =
            type.isActive !== undefined ? type.isActive : type.IsActive;
          return isActive === true;
        });

        const normalizedTypes = activeTypes.map((type) => ({
          ComplaintTypeId:
            type.complaintTypeId || type.ComplaintTypeId || type.id,
          ComplaintTypeName:
            type.complaintTypeName ||
            type.ComplaintTypeName ||
            type.name ||
            "Unknown",
          ComplaintTypeCode:
            type.complaintTypeCode || type.ComplaintTypeCode || type.code,
          Description: type.description || type.Description,
          IsForDomestic:
            type.isForDomestic !== undefined
              ? type.isForDomestic
              : type.IsForDomestic !== undefined
              ? type.IsForDomestic
              : false,
          IsForExport:
            type.isForExport !== undefined
              ? type.isForExport
              : type.IsForExport !== undefined
              ? type.IsForExport
              : false,
          IsActive:
            type.isActive !== undefined
              ? type.isActive
              : type.IsActive !== undefined
              ? type.IsActive
              : true,
        }));

        setComplaintTypes(normalizedTypes);
      } else {
        setComplaintTypes([]);
      }
    } catch (err) {
      console.error("Error fetching complaint types:", err);
      setComplaintTypes([]);
    } finally {
      setLoadingComplaintTypes(false);
    }
  };

  const openChecklist = async (productKey) => {
    const products = srData?.products || [];
    const matched = products.find(
      (p) => p?.ProductCode === productKey || p?.productCode === productKey
    );

    setChecklistData({
      category: matched?.ProductCategory || matched?.productCategory || "",
      productName: matched?.ProductName || matched?.productName || "",
      productCode:
        matched?.ProductCode || matched?.productCode || productKey || "",
      orderNo: matched?.OrderNo || matched?.orderNo || srData?.orderNo || "",
      quantity: matched?.Quantity || matched?.quantity || "",
    });

    setComplaintLines([newLine()]);

    // Fetch complaint types when popup opens
    await fetchComplaintTypes();

    setChecklistOpen(true);
  };

  const handleProductDetailsOpen = (productData) => {
    setSelectedProductData(productData);
    setProductDetailsOpen(true);
  };

  const handleProductDetailsClose = () => {
    requestAnimationFrame(() => {
      setProductDetailsOpen(false);
      setSelectedProductData(null);
    });
  };

  useEffect(() => {
    if (!journeyIconTip) return undefined;
    const hideTip = () => setJourneyIconTip(null);
    window.addEventListener("scroll", hideTip, true);
    window.addEventListener("resize", hideTip);
    return () => {
      window.removeEventListener("scroll", hideTip, true);
      window.removeEventListener("resize", hideTip);
    };
  }, [journeyIconTip]);

  // Handle ESC key to close product details modal and sidebar collapse/restore
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && productDetailsOpen) {
        handleProductDetailsClose();
      }
    };

    // Only update sidebar if state actually changed
    const prevState = prevProductDetailsOpenRef.current;
    const currentState = productDetailsOpen;

    if (currentState && !prevState) {
      // Modal just opened
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      // Modal just closed
      document.body.style.overflow = "";
      restoreSidebar();
    }

    // Update ref for next render
    prevProductDetailsOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      // Only restore body overflow, don't restore sidebar here
      // Sidebar restoration is handled by the state change above
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetailsOpen]); // Only depend on productDetailsOpen, functions are stable

  // Cleanup on unmount - restore sidebar if modal was open
  useEffect(() => {
    return () => {
      if (productDetailsOpen) {
        restoreSidebar();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on unmount

  // Component Details Modal Functions
  const handleComponentDetailsOpen = (productKey) => {
    setSelectedComponentProductKey(productKey);
    // Load component details from product data or use dummy data
    const productData = productDataRef.current[productKey];
    // Check if we have saved component details, otherwise use dummy data
    const savedData = componentDetailsRef.current[productKey] || {
      materialGroupDesc: productData?.MaterialGroupDesc || productData?.materialGroupDesc || "Wood Panel Group",
      componentCategory: productData?.ComponentCategory || productData?.componentCategory || "Door Components",
      componentList: productData?.ComponentList || productData?.componentList || "Hinges, Handles, Locks",
      componentQty: productData?.ComponentQty || productData?.componentQty || "5",
      componentIssue: productData?.ComponentIssue || productData?.componentIssue || "Loose hinges and misaligned handles",
      doorMaterialCode: productData?.DoorMaterialCode || productData?.doorMaterialCode || "DMC-001",
      componentIssueRemarks: productData?.ComponentIssueRemarks || productData?.componentIssueRemarks || "Components need replacement due to manufacturing defect. Quality inspection revealed issues with alignment and fastening.",
    };
    setComponentDetailsData(savedData);
    setComponentDetailsOpen(true);
  };

  const handleComponentDetailsClose = () => {
    requestAnimationFrame(() => {
      setComponentDetailsOpen(false);
      setSelectedComponentProductKey(null);
    });
  };

  // Handle ESC key to close component details modal and sidebar collapse/restore
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && componentDetailsOpen) {
        handleComponentDetailsClose();
      }
    };

    // Only update sidebar if state actually changed
    const prevState = prevComponentDetailsOpenRef.current;
    const currentState = componentDetailsOpen;

    if (currentState && !prevState) {
      // Modal just opened
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      // Modal just closed
      document.body.style.overflow = "";
      restoreSidebar();
    }

    // Update ref for next render
    prevComponentDetailsOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentDetailsOpen]);

  // Cleanup on unmount - restore sidebar if component details modal was open
  useEffect(() => {
    return () => {
      if (componentDetailsOpen) {
        restoreSidebar();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on unmount

  const handleChecklistClose = () => {
    if (modalInstanceRef.current) {
      try {
        modalInstanceRef.current.hide();
      } catch (error) {
        console.error("Error closing modal:", error);
      }
    }

    requestAnimationFrame(() => {
      setChecklistOpen(false);
    });
  };

  // Raise Query Modal Functions
  const handleRaiseQueryOpen = async () => {
    setRaiseQueryData({
      name: "",
      query: "",
    });

    Swal.fire({
      title: "Please wait",
      text: "Loading users…",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    // Populate dropdown from down-level users (USERMASTER hierarchy)
    try {
      const user = getUser();
      const currentUserIdCode =
        user?.userIdCode ||
        user?.UserIdCode ||
        user?.userCode ||
        user?.UserCode ||
        user?.id ||
        "";

      if (currentUserIdCode) {
        const res =
          await workflowAPI.getDownLevelUsersForRaiseQuery(
            currentUserIdCode,
            childSrCodeFromQuery || null
          );

        const list = res?.success ? res?.data : [];
        if (Array.isArray(list)) {
          // SP returns one row per workflow level; same user can appear twice
          // (e.g. BCM at two levels) — keep a single option per UserIdCode.
          const seen = new Set();
          const unique = [];
          for (const u of list) {
            const id = String(
              u.UserIdCode || u.userIdCode || u.id || u.UserId || ""
            ).trim();
            const name = String(
              u.Name ||
                u.name ||
                u.DisplayName ||
                u.FullName ||
                u.userName ||
                u.UserName ||
                ""
            ).trim();
            const dedupeKey = (id || name).toLowerCase();
            if (!dedupeKey || seen.has(dedupeKey)) continue;
            // Cannot raise a query to yourself
            if (
              id &&
              currentUserIdCode &&
              id.toLowerCase() === String(currentUserIdCode).trim().toLowerCase()
            ) {
              continue;
            }
            seen.add(dedupeKey);
            unique.push({
              id,
              name,
              userRole:
                u.UserTypeName ||
                u.userTypeName ||
                u.UserRole ||
                u.userRole ||
                u.RoleName ||
                u.roleName ||
                u.UserTypeCode ||
                u.userTypeCode ||
                "",
            });
          }
          setNameOptions(unique);
        } else {
          setNameOptions([]);
        }
      } else {
        setNameOptions([]);
      }
    } catch (e) {
      console.error("Error fetching down-level users:", e);
      setNameOptions([]);
    } finally {
      Swal.close();
    }

    setRaiseQueryOpen(true);

    // Show modal using Bootstrap if available
    if (raiseQueryModalInstanceRef.current) {
      try {
        raiseQueryModalInstanceRef.current.show();
      } catch (error) {
        console.debug("Error showing Raise Query modal:", error);
      }
    }
  };

  const handleRaiseQueryClose = () => {
    if (raiseQuerySubmittingRef.current) {
      return;
    }
    if (raiseQueryModalInstanceRef.current) {
      try {
        raiseQueryModalInstanceRef.current.hide();
      } catch (error) {
        console.error("Error closing Raise Query modal:", error);
      }
    }

    requestAnimationFrame(() => {
      setRaiseQueryOpen(false);
      setRaiseQuerySubmitting(false);
    });
  };

  const handleRaiseQuerySubmit = async () => {
    const queryText = (raiseQueryData?.query || "").trim();
    // Use state first; fallback to current select value (Select2 can leave state out of sync)
    const selectedFromState = (raiseQueryData?.name || "").trim();
    const selectEl = document.getElementById("raiseQuerySelect");
    const selectedFromDom = selectEl ? (selectEl.value || "").trim() : "";
    const raisedToUserIdCode = selectedFromState || selectedFromDom;

    if (!raisedToUserIdCode) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please select a user to raise the query.",
      });
      return;
    }

    if (!queryText) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Query text is mandatory.",
      });
      return;
    }

    const user = getUser();
    const raisedByUserIdCode =
      user?.userIdCode ||
      user?.UserIdCode ||
      user?.userCode ||
      user?.UserCode ||
      user?.id ||
      "";

    if (
      raisedByUserIdCode &&
      String(raisedToUserIdCode).trim().toLowerCase() ===
        String(raisedByUserIdCode).trim().toLowerCase()
    ) {
      Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "You cannot raise a query to yourself.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }

    const parentSRCode = srData?.srCode || srData?.SRCode || id;
    const childSRCode =
      srData?.childSRNumber || srData?.ChildSRNumber || null;

    setRaiseQuerySubmitting(true);
    try {
      const payload = {
        SRCode: parentSRCode,
        ChildSRCode: childSRCode,
        QueryText: queryText,
        RaisedToUserIdCode: raisedToUserIdCode,
      };

      const res = await workflowAPI.raiseTicketQueryV2(
        payload,
        raisedByUserIdCode
      );

      setRaiseQuerySubmitting(false);
      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Query raised successfully.",
        });
        handleRaiseQueryClose();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.message || "Failed to raise query.",
        });
      }
    } catch (e) {
      setRaiseQuerySubmitting(false);
      console.error("Error raising ticket query:", e);
      const message =
        e?.response?.data?.message ||
        "An error occurred while raising the query.";
      const isValidation =
        message.toLowerCase().includes("query limit reached") ||
        message.toLowerCase().includes("maximum allowed per sr");
      Swal.fire({
        icon: isValidation ? "warning" : "error",
        title: isValidation ? "Validation" : "Error",
        text: message,
      });
    }
  };

  // Query Response Functions (for SE)
  const getCurrentUserId = () => {
    try {
      const user = getUser();
      return user?.userId || user?.id || user?.UserId || user?.Id || 1;
    } catch {
      return 1;
    }
  };

  const fetchQueries = async () => {
    if (!id) return;
    
    setLoadingQueries(true);
    try {
      const isNumeric = id && !isNaN(Number(id));

      if (isNumeric) {
        const complaintId = parseInt(id);
        const response = await workflowAPI.getQueries({
          complaintId,
          status: "Pending" // Only fetch pending queries for SE to respond
        });

        if (response && response.success && response.data) {
          const currentUserId = getCurrentUserId();
          const seQueries = response.data.filter(
            (query) =>
              query.status === "Pending" &&
              (String(query.raisedToUserId ?? "") === String(currentUserId ?? "") ||
                String(query.raisedToUserId ?? "") === String(currentUserIdCode ?? ""))
          );
          setQueriesSafe(seQueries);
        } else {
          setQueries([]);
        }
      } else {
        const parentSrCode = srData?.srCode || srData?.SRCode || id;
        const childSrCode =
          srData?.childSRNumber || srData?.ChildSRNumber || childSrCodeFromQuery || null;

        const response = await workflowAPI.getTicketQueriesBySR({
          srCode: parentSrCode,
          childSrCode
        });

        if (response && response.success && response.data) {
          const currentUserId = getCurrentUserId();
          const seQueries = response.data.filter(
            (query) =>
              query.status === "Pending" &&
              (String(query.raisedToUserId ?? "") === String(currentUserId ?? "") ||
                String(query.raisedToUserId ?? "") === String(currentUserIdCode ?? ""))
          );
          setQueriesSafe(seQueries);
        } else {
          setQueries([]);
        }
      }
    } catch (error) {
      console.error("Error fetching queries:", error);
      setQueries([]);
    } finally {
      setLoadingQueries(false);
    }
  };

  const handleResponseQueryOpen = async (query) => {
    setSelectedQuery(query);
    setResponseQueryData({
      responseText: "",
      attachments: [],
    });
    setResponseQueryOpen(true);

    // Show modal using Bootstrap if available
    if (responseQueryModalInstanceRef.current) {
      try {
        responseQueryModalInstanceRef.current.show();
      } catch (error) {
        console.debug("Error showing Response Query modal:", error);
      }
    }
  };

  const handleResponseQueryClose = () => {
    if (responseQuerySubmittingRef.current) {
      return;
    }
    if (responseQueryModalInstanceRef.current) {
      try {
        responseQueryModalInstanceRef.current.hide();
      } catch (error) {
        console.error("Error closing Response Query modal:", error);
      }
    }

    requestAnimationFrame(() => {
      setResponseQueryOpen(false);
      setSelectedQuery(null);
      setResponseQueryData({
        responseText: "",
        attachments: [],
      });
      setResponseQuerySubmitting(false);
    });
  };

  const handleResponseQuerySubmit = async () => {
    if (!selectedQuery) return;

    const currentUserId = getCurrentUserId();
    const assignedTo = String(selectedQuery?.raisedToUserId ?? "").trim();
    const isAssignedToCurrentUser =
      assignedTo !== "" &&
      (assignedTo === String(currentUserId ?? "").trim() ||
        assignedTo === String(currentUserIdCode ?? "").trim());

    if (!isAssignedToCurrentUser) {
      Swal.fire({
        icon: "error",
        title: "Not allowed",
        text: "You can respond only to queries assigned to you.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }

    if (!responseQueryData.responseText || responseQueryData.responseText.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Response field is mandatory. Please enter your response.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }

    setResponseQuerySubmitting(true);
    try {
      const uploadedAttachmentMeta = [];
      const responseAttachments = Array.isArray(responseQueryData.attachments)
        ? responseQueryData.attachments
        : [];

      for (const item of responseAttachments) {
        const fileToUpload = item instanceof File ? item : item?.file;
        if (fileToUpload) {
          const uploadRes = await complaintAPI.uploadInvoiceAttachment(
            fileToUpload,
            item?.copyFor || "Query Response"
          );
          const uploaded = uploadRes?.data || {};
          uploadedAttachmentMeta.push({
            fileName: uploaded.FileName || uploaded.fileName || item?.name || fileToUpload.name || "",
            filePath: uploaded.FilePath || uploaded.filePath || "",
            fileSize: uploaded.FileSize ?? uploaded.fileSize ?? item?.size ?? fileToUpload.size ?? null,
            fileType: uploaded.FileType || uploaded.fileType || item?.type || fileToUpload.type || "",
            copyFor: uploaded.CopyFor || uploaded.copyFor || item?.copyFor || "Query Response",
          });
        } else if (item?.path || item?.filePath) {
          uploadedAttachmentMeta.push({
            fileName: item?.name || item?.fileName || "",
            filePath: item?.path || item?.filePath || "",
            fileSize: item?.size || item?.fileSize || null,
            fileType: item?.type || item?.fileType || "",
            copyFor: item?.copyFor || "Query Response",
          });
        }
      }

      const parentSrCode = srData?.srCode || srData?.SRCode || id;
      const childSr =
        srData?.childSRNumber || srData?.ChildSRNumber || childSrCodeFromQuery || null;

      const respondPayload = {
        queryId: selectedQuery.queryId,
        responseText: responseQueryData.responseText.trim(),
        srCode: parentSrCode || null,
        childSrCode: childSr,
        businessVertical: srData?.BusinessVertical || srData?.businessVertical || null,
        categoryCode: srData?.CategoryCode || srData?.categoryCode || null,
        marketCode: srData?.MarketCode || srData?.marketCode || null,
        submittedBy: currentUserIdCode || String(currentUserId || "") || null,
        attachments: uploadedAttachmentMeta,
      };

      const response = await workflowAPI.respondToQuery(
        selectedQuery.queryId,
        respondPayload,
        currentUserIdCode || String(currentUserId || "").trim() || null
      );

      setResponseQuerySubmitting(false);
      if (response && response.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Query response submitted successfully. The approver will be notified.",
          confirmButtonColor: "#0b6bff",
        });

        await fetchQueries();
        handleResponseQueryClose();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to submit query response. Please try again.",
          confirmButtonColor: "#0b6bff",
        });
      }
    } catch (error) {
      setResponseQuerySubmitting(false);
      console.error("Error submitting query response:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while submitting the response. Please try again.",
        confirmButtonColor: "#0b6bff",
      });
    }
  };

  const handleFileAttachment = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const withIds = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}-${file.name}`,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
    }));
    setResponseQueryData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...withIds],
    }));
    e.target.value = "";
  };

  const removeAttachment = (item) => {
    const ref = item?.file ?? item;
    setResponseQueryData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((a) => {
        if (a instanceof File) return a !== ref;
        return a.file !== ref;
      }),
    }));
  };

  // Fetch all queries (including history) for display
  const fetchAllQueries = async () => {
    if (!id) {
      setQueries([]);
      return;
    }
    setLoadingQueries(true);
    try {
      const isNumeric = id && !isNaN(Number(id));
      if (isNumeric) {
        const complaintId = parseInt(id);
        const response = await workflowAPI.getQueries({ complaintId });
        if (response && response.success && response.data && response.data.length > 0) {
          setQueriesSafe(response.data);
        } else {
          setQueries([]);
        }
      } else {
        const parentSrCode = srData?.srCode || srData?.SRCode || id;
        const childSrCode =
          srData?.childSRNumber || srData?.ChildSRNumber || childSrCodeFromQuery || null;

        const response = await workflowAPI.getTicketQueriesBySR({
          srCode: parentSrCode,
          childSrCode
        });

        if (response && response.success && Array.isArray(response.data)) {
          setQueriesSafe(response.data);
        } else {
          setQueries([]);
        }
      }
    } catch (error) {
      console.error("Error fetching all queries:", error);
      setQueries([]);
    } finally {
      setLoadingQueries(false);
    }
  };

  // Approve Modal Functions
  const handleApproveOpen = () => {
    // Extract complaint items from srData.products
    const items = (srData?.products || []).map((product, index) => {
      const compQty = parseFloat(
        product?.ComplaintQty || product?.complaintQty || 0
      );
      const unitPrice = parseFloat(product?.Price || product?.price || 0);
      // Calculate Comp Value if not available: Comp Qty * Unit Price
      const compValue =
        product?.ComplaintValue ||
        product?.complaintValue ||
        (compQty * unitPrice).toFixed(2);

      return {
        id: product?.ProductCode || product?.productCode || `item-${index}`,
        itemCategory:
          product?.ProductCategory || product?.productCategory || "N/A",
        compQty: compQty.toString(),
        compValue: compValue.toString(),
        changeValue: "",
      };
    });

    setApproveData({
      items: items,
      remarks: "",
      factoryIntentNo: "",
      factoryIntentDate: "",
      settlementType: "",
    });
    setApproveOpen(true);
    // Show modal using Bootstrap if available
    if (approveModalInstanceRef.current) {
      try {
        approveModalInstanceRef.current.show();
      } catch (error) {
        console.debug("Error showing Approve modal:", error);
      }
    }
  };

  const handleApproveClose = () => {
    if (approveModalInstanceRef.current) {
      try {
        approveModalInstanceRef.current.hide();
      } catch (error) {
        console.error("Error closing Approve modal:", error);
      }
    }
    setTimeout(() => {
      setApproveOpen(false);
    }, 300);
  };

  const handleChangeValueUpdate = (itemId, value) => {
    setApproveData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === itemId ? { ...item, changeValue: value } : item
      ),
    }));
  };

  // Approve/Reject Modal Functions
  const handleApproveRejectOpen = () => {
    // Save focus so we can restore it after modal closes (prevents aria-hidden/focus warnings)
    try {
      approveRejectLastFocusRef.current = document.activeElement;
    } catch (e) {
      approveRejectLastFocusRef.current = null;
    }
    // Extract all product data from srData.products to match Material Section grid
    const items = (srData?.products || []).map((product, index) => {
      const compQty = parseFloat(
        product?.ComplaintQty || product?.complaintQty || 0
      );
      const unitPrice = parseFloat(product?.Price || product?.price || 0);
      const invoiceQty = parseFloat(product?.Quantity || product?.quantity || 0);
      const approvedQty = parseFloat(
        product?.ApprovedQty || product?.approvedQty || product?.ApprovedQuantity || product?.approvedQuantity || 0
      );
      
      // Calculate values
      const compValue =
        product?.ComplaintValue ||
        product?.complaintValue ||
        (compQty * unitPrice).toFixed(2);
      const totalPrice = (invoiceQty * unitPrice).toFixed(2);
      const invoiceValueTotal = totalPrice;
      const approvedValue = (approvedQty * unitPrice).toFixed(2);
      const srValue = (compQty * unitPrice).toFixed(2);

      // Use default values for prototype if the value is 0 or not available
      const finalCompQty =
        compQty === 0 || isNaN(compQty) ? "1" : compQty.toString();
      const finalCompValue =
        parseFloat(compValue) === 0 || isNaN(parseFloat(compValue))
          ? "100.00"
          : compValue.toString();

      // Get product category with mapping
      let category =
        product?.ProductCategory ||
        product?.productCategory ||
        product?.Productcategory ||
        "";
      const normalizedCategory = (category || "").trim().toLowerCase();
      if (normalizedCategory === "makasaply" || normalizedCategory === "mikasaply") {
        category = "Plain Particle Board";
      } else if (normalizedCategory === "floors") {
        category = "Prelam";
      }

      // Get plant name
      let plantName =
        product?.PlantName ||
        product?.plantName ||
        product?.Plantname ||
        product?.plant_name ||
        "";
      if (!plantName) {
        const dummyPlantNames = [
          "Default Plant",
          "Plant A",
          "Plant B",
          "Manufacturing Plant 1",
          "Production Facility",
        ];
        plantName = dummyPlantNames[index % dummyPlantNames.length];
      }

      return {
        id: product?.ProductCode || product?.productCode || `item-${index}`,
        sno: index + 1,
        productCategory: category,
        plantName: plantName,
        itemDescription: product?.ProductName || product?.productName || product?.Productname || "",
        invoiceQty: invoiceQty.toString(),
        uom: "PCS",
        invoiceUnitPrice: unitPrice.toFixed(2),
        invoiceValueTotal,
        totalPrice: totalPrice,
        srCategory: product?.Category || product?.category || product?.SRCategory || product?.srCategory || "",
        srQty: finalCompQty,
        srValue: srValue,
        approvedQty: approvedQty > 0 ? approvedQty.toString() : "",
        approvedValue: approvedValue,
        itemCategory: category,
        compQty: finalCompQty,
        compValue: finalCompValue,
        changeValue: "",
      };
    });

    const seedRow = (srData?.products || [])[0] || {};
    const toDateInput = (raw) => {
      if (raw == null || raw === "") return "";
      try {
        const d = raw instanceof Date ? raw : new Date(raw);
        if (!Number.isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${y}-${m}-${day}`;
        }
      } catch (e) {
        /* ignore */
      }
      const s = String(raw);
      return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
    };
    const getTodayDateInput = () => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };
    const seededFactoryIntentDate = toDateInput(
      seedRow.FactoryIntentDate ?? seedRow.factoryIntentDate
    );

    setApproveData({
      items: items,
      remarks: "",
      factoryIntentNo: (seedRow.FactoryIntentNo ?? seedRow.factoryIntentNo ?? "").toString(),
      factoryIntentDate: seededFactoryIntentDate || getTodayDateInput(),
      settlementType: (seedRow.SettlementType ?? seedRow.settlementType ?? "").toString(),
    });
    setApproveRejectOpen(true);
    if (approveRejectModalInstanceRef.current) {
      try {
        approveRejectModalInstanceRef.current.show();
        // Initialize Bootstrap tooltips after modal is shown
        setTimeout(() => {
          const tooltipTriggerList = [].slice.call(
            document.querySelectorAll('[data-bs-toggle="tooltip"]')
          );
          tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new window.bootstrap.Tooltip(tooltipTriggerEl);
          });
        }, 300);
      } catch (error) {
        console.debug("Error showing Approve/Reject modal:", error);
      }
    }
  };

  const handleApproveRejectClose = () => {
    // Ensure focus is not trapped on an element that may become aria-hidden
    try {
      if (document.activeElement && typeof document.activeElement.blur === "function") {
        document.activeElement.blur();
      }
    } catch (e) {
      // ignore
    }
    if (approveRejectModalInstanceRef.current) {
      try {
        approveRejectModalInstanceRef.current.hide();
      } catch (error) {
        console.error("Error closing Approve/Reject modal:", error);
      }
    }
    setTimeout(() => {
      setApproveRejectOpen(false);
      setApproveData({
        items: [],
        remarks: "",
        factoryIntentNo: "",
        factoryIntentDate: "",
        settlementType: "",
      });
      // Restore focus back to the trigger button/link when possible
      try {
        const el = approveRejectLastFocusRef.current;
        if (el && typeof el.focus === "function") el.focus();
      } catch (e) {
        // ignore
      } finally {
        approveRejectLastFocusRef.current = null;
      }
    }, 300);
  };

  const handleApproveSubmit = () => {
    const remarksTrimmed = (approveData.remarks || "").trim();
    if (!remarksTrimmed) {
      Swal.fire({
        icon: "warning",
        title: "Remarks required",
        text: "Please enter remarks before approving.",
      });
      return;
    }
    // Add approve submit logic here
    console.log("Approve Submit clicked", approveData);
    handleApproveRejectClose();
  };

  const handleRejectSubmit = () => {
    const remarksTrimmed = (approveData.remarks || "").trim();
    if (!remarksTrimmed) {
      Swal.fire({
        icon: "warning",
        title: "Remarks required",
        text: "Please enter remarks before rejecting.",
      });
      return;
    }
    // Add reject submit logic here
    console.log("Reject Submit clicked", approveData);
    handleApproveRejectClose();
  };

  const addComplaintLine = () => {
    setComplaintLines((prev) => [...prev, newLine()]);
  };

  const removeComplaintLine = (id) => {
    setComplaintLines((prev) =>
      prev.length > 1 ? prev.filter((l) => l.id !== id) : prev
    );
  };

  const updateComplaintLine = (id, field, value) => {
    setComplaintLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, [field]: value } : line))
    );
  };

  // Initialize Bootstrap modal instance for SR Checklist
  useEffect(() => {
    if (!checklistModalRef.current) return;

    const modalElement = checklistModalRef.current;

    const initBootstrapModal = () => {
      if (typeof window === "undefined") return;

      let BootstrapModal = null;
      if (window.bootstrap && window.bootstrap.Modal) {
        BootstrapModal = window.bootstrap.Modal;
      }

      if (BootstrapModal && !modalInstanceRef.current) {
        try {
          modalInstanceRef.current =
            BootstrapModal.getInstance(modalElement) ||
            new BootstrapModal(modalElement, {
              backdrop: false,
              keyboard: true,
            });

          const handleHidden = () => {
            setChecklistOpen(false);
          };

          modalElement.addEventListener("hidden.bs.modal", handleHidden);
          modalElement._handleHidden = handleHidden;
        } catch (error) {
          console.debug("Bootstrap Modal not available, using CSS fallback");
        }
      }
    };

    setTimeout(initBootstrapModal, 100);

    return () => {
      if (modalElement && modalElement._handleHidden) {
        modalElement.removeEventListener(
          "hidden.bs.modal",
          modalElement._handleHidden
        );
        delete modalElement._handleHidden;
      }
    };
  }, []);

  // Handle modal state and sidebar toggling
  const prevChecklistOpenRef = useRef(checklistOpen);
  const modalOpenRef = useRef(checklistOpen);

  useEffect(() => {
    modalOpenRef.current = checklistOpen;
  }, [checklistOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && checklistOpen) {
        handleChecklistClose();
      }
    };

    const prevState = prevChecklistOpenRef.current;
    const currentState = checklistOpen;

    if (currentState && !prevState) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      document.body.style.overflow = "";
      restoreSidebar();
    }

    prevChecklistOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [checklistOpen, collapseSidebar, restoreSidebar]);

  useEffect(() => {
    return () => {
      if (modalOpenRef.current) {
        restoreSidebar();
      }
    };
  }, [restoreSidebar]);

  // Initialize Select2 on modal selects when modal opens
  useEffect(() => {
    if (checklistOpen) {
      const timer = setTimeout(() => {
        $(".checklist-modal select.form-select").each(function () {
          const $select = $(this);

          if ($select.data("select2")) {
            $select.select2("destroy");
          }

          $select.select2({
            width: "100%",
            placeholder: "Select an option",
            allowClear: false,
            dropdownParent: $(".checklist-modal"),
            minimumResultsForSearch: 5,
          });

          $select.on("change", function () {
            const event = new Event("change", { bubbles: true });
            this.dispatchEvent(event);
          });
        });
      }, 100);

      return () => {
        clearTimeout(timer);
        $(".checklist-modal select.form-select").each(function () {
          const $select = $(this);
          if ($select.data("select2")) {
            $select.off("change");
            $select.select2("destroy");
          }
        });
      };
    }
  }, [checklistOpen, complaintLines.length, complaintTypes.length]);

  // Initialize Bootstrap modal instance for Raise Query
  useEffect(() => {
    if (!raiseQueryModalRef.current) return;

    const modalElement = raiseQueryModalRef.current;

    const initBootstrapModal = () => {
      if (typeof window === "undefined") return;

      let BootstrapModal = null;
      if (window.bootstrap && window.bootstrap.Modal) {
        BootstrapModal = window.bootstrap.Modal;
      }

      if (BootstrapModal && !raiseQueryModalInstanceRef.current) {
        try {
          raiseQueryModalInstanceRef.current =
            BootstrapModal.getInstance(modalElement) ||
            new BootstrapModal(modalElement, {
              backdrop: false,
              keyboard: true,
            });

          const handleHidden = () => {
            setRaiseQueryOpen(false);
          };

          modalElement.addEventListener("hidden.bs.modal", handleHidden);
          modalElement._handleHidden = handleHidden;
        } catch (error) {
          console.debug("Bootstrap Modal not available, using CSS fallback");
        }
      }
    };

    setTimeout(initBootstrapModal, 100);

    return () => {
      if (modalElement && modalElement._handleHidden) {
        modalElement.removeEventListener(
          "hidden.bs.modal",
          modalElement._handleHidden
        );
        delete modalElement._handleHidden;
      }
    };
  }, []);

  // Initialize Bootstrap modal instance for Response Query
  useEffect(() => {
    if (!responseQueryModalRef.current) return;

    const modalElement = responseQueryModalRef.current;

    const initBootstrapModal = () => {
      if (typeof window === "undefined") return;

      let BootstrapModal = null;
      if (window.bootstrap && window.bootstrap.Modal) {
        BootstrapModal = window.bootstrap.Modal;
      }

      if (BootstrapModal && !responseQueryModalInstanceRef.current) {
        try {
          responseQueryModalInstanceRef.current =
            BootstrapModal.getInstance(modalElement) ||
            new BootstrapModal(modalElement, {
              backdrop: false,
              keyboard: true,
            });

          const handleHidden = () => {
            setResponseQueryOpen(false);
          };

          modalElement.addEventListener("hidden.bs.modal", handleHidden);
          modalElement._handleHidden = handleHidden;
        } catch (error) {
          console.debug("Bootstrap Modal not available, using CSS fallback");
        }
      }
    };

    setTimeout(initBootstrapModal, 100);

    return () => {
      if (modalElement && modalElement._handleHidden) {
        modalElement.removeEventListener(
          "hidden.bs.modal",
          modalElement._handleHidden
        );
        delete modalElement._handleHidden;
      }
    };
  }, []);

  // Initialize Bootstrap modal instance for Approve
  useEffect(() => {
    if (!approveModalRef.current) return;

    const modalElement = approveModalRef.current;

    const initBootstrapModal = () => {
      if (typeof window === "undefined") return;

      let BootstrapModal = null;
      if (window.bootstrap && window.bootstrap.Modal) {
        BootstrapModal = window.bootstrap.Modal;
      }

      if (BootstrapModal && !approveModalInstanceRef.current) {
        try {
          approveModalInstanceRef.current =
            BootstrapModal.getInstance(modalElement) ||
            new BootstrapModal(modalElement, {
              backdrop: false,
              keyboard: true,
            });

          const handleHidden = () => {
            setApproveOpen(false);
          };

          modalElement.addEventListener("hidden.bs.modal", handleHidden);
          modalElement._handleHidden = handleHidden;
        } catch (error) {
          console.debug("Bootstrap Modal not available, using CSS fallback");
        }
      }
    };

    setTimeout(initBootstrapModal, 100);

    return () => {
      if (modalElement && modalElement._handleHidden) {
        modalElement.removeEventListener(
          "hidden.bs.modal",
          modalElement._handleHidden
        );
        delete modalElement._handleHidden;
      }
    };
  }, []);

  // Initialize Bootstrap modal instance for Approve/Reject
  useEffect(() => {
    if (!approveRejectModalRef.current) return;

    const modalElement = approveRejectModalRef.current;

    const initBootstrapModal = () => {
      if (typeof window === "undefined") return;

      let BootstrapModal = null;
      if (window.bootstrap && window.bootstrap.Modal) {
        BootstrapModal = window.bootstrap.Modal;
      }

      if (BootstrapModal && !approveRejectModalInstanceRef.current) {
        try {
          approveRejectModalInstanceRef.current =
            BootstrapModal.getInstance(modalElement) ||
            new BootstrapModal(modalElement, {
              backdrop: false,
              keyboard: true,
            });

          const handleHidden = () => {
            setApproveRejectOpen(false);
          };

          modalElement.addEventListener("hidden.bs.modal", handleHidden);
          modalElement._handleHidden = handleHidden;
        } catch (error) {
          console.debug("Bootstrap Modal not available, using CSS fallback");
        }
      }
    };

    setTimeout(initBootstrapModal, 100);

    return () => {
      if (modalElement && modalElement._handleHidden) {
        modalElement.removeEventListener(
          "hidden.bs.modal",
          modalElement._handleHidden
        );
        delete modalElement._handleHidden;
      }
    };
  }, []);

  // Initialize tooltips and event handlers for Approve/Reject modal action buttons
  useEffect(() => {
    if (!approveRejectOpen || !approveRejectModalRef.current) return;

    const modalElement = approveRejectModalRef.current;
    let tooltipList = [];

    // Initialize Bootstrap tooltips
    const initTooltips = () => {
      if (typeof window !== "undefined" && window.bootstrap && window.bootstrap.Tooltip) {
        const tooltipTriggerList = [].slice.call(
          modalElement.querySelectorAll('[data-bs-toggle="tooltip"]')
        );
        tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
          // Dispose existing tooltip if any
          const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
          if (existingTooltip) {
            existingTooltip.dispose();
          }
          return new window.bootstrap.Tooltip(tooltipTriggerEl);
        });
      }
    };

    // Add event handler for approve/reject button
    const handleApproveRejectClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const itemId = e.currentTarget.getAttribute("data-item-id");
      console.log("Approve/Reject item:", itemId);
      // Add approve/reject logic here - could open a sub-modal or show options
    };

    // Wait for modal to be fully rendered
    const timeoutId = setTimeout(() => {
      initTooltips();
      
      // Attach event handlers
      const approveRejectButtons = Array.from(modalElement.querySelectorAll(".approve-reject-item-btn"));
      
      approveRejectButtons.forEach((btn) => {
        btn.addEventListener("click", handleApproveRejectClick);
      });
    }, 300);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      
      // Dispose tooltips
      tooltipList.forEach((tooltip) => {
        if (tooltip) tooltip.dispose();
      });
      
      // Remove event listeners
      const approveRejectButtons = Array.from(modalElement.querySelectorAll(".approve-reject-item-btn"));
      approveRejectButtons.forEach((btn) => {
        btn.removeEventListener("click", handleApproveRejectClick);
      });
    };
  }, [approveRejectOpen]);

  // Handle Raise Query modal state and sidebar toggling
  const prevRaiseQueryOpenRef = useRef(raiseQueryOpen);
  const raiseQueryModalOpenRef = useRef(raiseQueryOpen);

  useEffect(() => {
    raiseQueryModalOpenRef.current = raiseQueryOpen;
  }, [raiseQueryOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && raiseQueryOpen) {
        handleRaiseQueryClose();
      }
    };

    const prevState = prevRaiseQueryOpenRef.current;
    const currentState = raiseQueryOpen;

    if (currentState && !prevState) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      document.body.style.overflow = "";
      restoreSidebar();
    }

    prevRaiseQueryOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [raiseQueryOpen, collapseSidebar, restoreSidebar]);

  useEffect(() => {
    return () => {
      if (raiseQueryModalOpenRef.current) {
        restoreSidebar();
      }
    };
  }, [restoreSidebar]);

  // Handle Approve/Reject modal state and sidebar toggling
  const prevApproveRejectOpenRef = useRef(approveRejectOpen);
  const approveRejectModalOpenRef = useRef(approveRejectOpen);

  useEffect(() => {
    approveRejectModalOpenRef.current = approveRejectOpen;
  }, [approveRejectOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && approveRejectOpen) {
        handleApproveRejectClose();
      }
    };

    const prevState = prevApproveRejectOpenRef.current;
    const currentState = approveRejectOpen;

    if (currentState && !prevState) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      document.body.style.overflow = "";
      restoreSidebar();
    }

    prevApproveRejectOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [approveRejectOpen, collapseSidebar, restoreSidebar]);

  useEffect(() => {
    return () => {
      if (approveRejectModalOpenRef.current) {
        restoreSidebar();
      }
    };
  }, [restoreSidebar]);

  // Handle Approve modal state and sidebar toggling
  const prevApproveOpenRef = useRef(approveOpen);
  const approveModalOpenRef = useRef(approveOpen);

  useEffect(() => {
    approveModalOpenRef.current = approveOpen;
  }, [approveOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && approveOpen) {
        handleApproveClose();
      }
    };

    const prevState = prevApproveOpenRef.current;
    const currentState = approveOpen;

    if (currentState && !prevState) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      document.body.style.overflow = "";
      restoreSidebar();
    }

    prevApproveOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [approveOpen, collapseSidebar, restoreSidebar]);

  useEffect(() => {
    return () => {
      if (approveModalOpenRef.current) {
        restoreSidebar();
      }
    };
  }, [restoreSidebar]);

  // Initialize Select2 on Raise Query modal select when modal opens or options load
  useEffect(() => {
    if (!raiseQueryOpen) return;

    const timer = setTimeout(() => {
      const selectElement = document.getElementById("raiseQuerySelect");
      if (!selectElement || !raiseQueryModalRef.current) return;

      const $select = $(selectElement);
      const $modal = $(raiseQueryModalRef.current);

      // Destroy existing Select2 instance if any
      if ($select.data("select2")) {
        $select.off("select2:select select2:clear");
        $select.select2("destroy");
      }

      // Initialize Select2 (after options are in DOM so selection stays in sync)
      $select.select2({
        width: "100%",
        placeholder: "Search for name...",
        allowClear: true,
        dropdownParent: $modal,
        minimumResultsForSearch: 0,
        containerCssClass: "raise-query-select2-container-left",
        dropdownCssClass: "raise-query-select2-dropdown-left",
      });

      // Handle change event so state stays in sync
      $select.on("select2:select select2:clear", function () {
        const selectedValue = $(this).val() || "";
        setRaiseQueryData((prev) => ({
          ...prev,
          name: selectedValue,
        }));
      });
    }, nameOptions.length > 0 ? 100 : 400);

    return () => {
      clearTimeout(timer);
      const selectElement = document.getElementById("raiseQuerySelect");
      if (selectElement) {
        const $select = $(selectElement);
        if ($select.data("select2")) {
          $select.off("select2:select select2:clear");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raiseQueryOpen, nameOptions.length]);

  useEffect(() => {
    if (!raiseQueryOpen) return;
    try {
      const el = document.getElementById("raiseQuerySelect");
      if (!el) return;
      const $select = $(el);
      if ($select.data("select2")) {
        $select.prop("disabled", raiseQuerySubmitting);
      }
    } catch {
      /* ignore */
    }
  }, [raiseQuerySubmitting, raiseQueryOpen]);

  // Handle collapse icon rotation
  useEffect(() => {
    const collapseElements = document.querySelectorAll(".collapse");

    const handleShown = (event) => {
      const collapseId = event.target.id;
      const toggle = document.querySelector(
        `[data-bs-target="#${collapseId}"]`
      );
      if (toggle) {
        toggle.setAttribute("aria-expanded", "true");
      }
    };

    const handleHidden = (event) => {
      const collapseId = event.target.id;
      const toggle = document.querySelector(
        `[data-bs-target="#${collapseId}"]`
      );
      if (toggle) {
        toggle.setAttribute("aria-expanded", "false");
      }
    };

    collapseElements.forEach((element) => {
      element.addEventListener("shown.bs.collapse", handleShown);
      element.addEventListener("hidden.bs.collapse", handleHidden);
    });

    return () => {
      collapseElements.forEach((element) => {
        element.removeEventListener("shown.bs.collapse", handleShown);
        element.removeEventListener("hidden.bs.collapse", handleHidden);
      });
    };
  }, [srData]);

  // Helper function to transform ApprovalHistory to journeyStatus format
  const transformApprovalHistoryToJourneyStatus = (approvalHistory, approvalLevels) => {
    if ((!approvalHistory || !Array.isArray(approvalHistory) || approvalHistory.length === 0) &&
        (!approvalLevels || !Array.isArray(approvalLevels) || approvalLevels.length === 0)) {
      return [];
    }

    // If we have approval levels, use them as the base structure
    if (approvalLevels && Array.isArray(approvalLevels) && approvalLevels.length > 0) {
      // Sort approval levels by sequence
      const sortedLevels = [...approvalLevels].sort((a, b) => a.levelSequence - b.levelSequence);
      
      // Create a map of approval history by level sequence
      const historyMap = new Map();
      if (approvalHistory && Array.isArray(approvalHistory)) {
        approvalHistory.forEach(history => {
          historyMap.set(history.levelSequence, history);
        });
      }

      // Build journeyStatus array from approval levels
      const journeyStatus = sortedLevels.map(level => {
        const history = historyMap.get(level.levelSequence);
        
        return {

          
          status: history?.actionType || "Pending",
          date: history?.actionOn ? formatDate(history.actionOn) : "",
          designation: level.roleName || level.roleCode || "",
          name: history?.actionByName || "",
          approved: history?.actionType === "Approved",
          rejected: history?.actionType === "Rejected",






          // SR Journey Status Details table values:
          // - SLA/TAT from HistorySlaDays/HistoryTATDays
          // - SR Qty/Value from ActualSRQty/ActualSRValue
          sla: history?.HistorySlaDays ?? history?.historySlaDays ?? null,
          tat: history?.HistoryTATDays ?? history?.historyTATDays ?? null,
          srQty: history?.ActualSRQty ?? history?.actualSrQty ?? undefined,
          srValue: history?.ActualSRValue ?? history?.actualSrValue ?? undefined,
          approvedQty: history?.actionType === "Approved" && history?.approvedValue ? String(history.approvedValue) : undefined,
          approvedValue: history?.actionType === "Approved" && history?.approvedValue ? String(history.approvedValue) : undefined,
          comment: history?.comments || "",
        };
      });

      return journeyStatus;
    }
    
    // If we only have approval history (no levels), build from history directly
    if (approvalHistory && Array.isArray(approvalHistory) && approvalHistory.length > 0) {
      // Sort by level sequence
      const sortedHistory = [...approvalHistory].sort((a, b) => a.levelSequence - b.levelSequence);
      
      return sortedHistory.map(history => ({
        status: history.actionType || "Pending",
        date: history.actionOn ? formatDate(history.actionOn) : "",
        designation: history.roleName || "",
        name: history.actionByName || "",
        approved: history.actionType === "Approved",
        rejected: history.actionType === "Rejected",
        sla: history?.HistorySlaDays ?? history?.historySlaDays ?? null,
        tat: history?.HistoryTATDays ?? history?.historyTATDays ?? null,
        srQty: history?.ActualSRQty ?? history?.actualSrQty ?? undefined,
        srValue: history?.ActualSRValue ?? history?.actualSrValue ?? undefined,
        approvedQty: history.actionType === "Approved" && history.approvedValue ? String(history.approvedValue) : undefined,
        approvedValue: history.actionType === "Approved" && history.approvedValue ? String(history.approvedValue) : undefined,
        comment: history.comments || "",
      }));
    }

    return [];
  };

  const filterSkippedJourneySteps = (steps) => {
    if (!Array.isArray(steps)) return [];
    return steps.filter((step) => {
      const activity = (step?.Activity || step?.activity || step?.status || "")
        .toString()
        .trim()
        .toUpperCase();
      return activity !== "SKIPPED";
    });
  };

  // Expected TAT for journey details: prefer stamped history when > 0, else ConfigTATDays
  // (API often coerces NULL HistoryTATDays → 0, which must not hide configured TAT).
  const resolveExpectedTatDays = (...values) => {
    let fallback = null;
    for (const v of values) {
      if (v == null || v === "") continue;
      const n = Number(v);
      if (!Number.isFinite(n)) continue;
      if (fallback == null) fallback = n;
      if (n > 0) return String(n);
    }
    return fallback != null ? String(fallback) : "0";
  };

  // Initialize DataTable for SR Journey Status Details (row-wise grid)
  useEffect(() => {
    const statuses = srData?.journeyStatus;
    if (journeyStatusTableRef.current && Array.isArray(statuses)) {
      if (!$.fn.DataTable) {
        DataTable($);
      }

      if (journeyStatusDataTableRef.current) {
        journeyStatusDataTableRef.current.destroy();
        journeyStatusDataTableRef.current = null;
      }

      const normalize = (val) => val?.toUpperCase().trim();
      const computeTat = (current, next) => {
        const from = normalize(current?.designation);
        const to = normalize(next?.designation);
        if (!from || !to) return null;
        if (from === "SE" && to === "BCM") return "2";
        if (from === "BCM" && to === "ZH") return "2";
        if (from === "ZH" && (to === "PLANT QC" || to === "PLANTQC")) return "3";
        if ((from === "PLANT QC" || from === "PLANTQC") && to === "QC HEAD") return "1";
        if (from === "QC HEAD" && (to === "MFG. HEAD" || to === "MFG HEAD")) return "6";
        if ((from === "MFG. HEAD" || from === "MFG HEAD") && to === "CH") return "1";
        return null;
      };

      // Sort statuses: Approved first, then Pending, then Rejected
      const sortedStatuses = [...statuses].sort((a, b) => {
        // Get status priority: Approved = 1, Pending = 2, Rejected = 3
        const getStatusPriority = (step) => {
          if (step.approved) return 1;
          if (step.rejected) return 3;
          return 2; // Pending
        };
        
        const priorityA = getStatusPriority(a);
        const priorityB = getStatusPriority(b);
        
        // Sort by priority (lower number = higher priority)
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
        
        // Within same status group, maintain original order
        return 0;
      });

      const tableData = sortedStatuses.map((step, index) => {
        const nextStep = sortedStatuses[index + 1];
        const statusLabel = step.rejected
          ? "Rejected"
          : step.approved
          ? "Approved"
          : "Pending";

        // Get status priority for sorting: Approved = 1, Pending = 2, Rejected = 3
        const statusPriority = step.approved ? 1 : step.rejected ? 3 : 2;
        
        return {
          statusPriority: statusPriority, // Hidden column for sorting
          role: step.designation || "N/A",
          actionBy: step.name || "N/A",
          date: step.date ? formatDate(step.date) : "N/A",
          status: statusLabel,
          statusColor: step.rejected ? "#dc3545" : step.approved ? "#28a745" : "#ff8c00", // Orange for pending
          sla:
            step?.HistorySlaDays ??
            step?.historySlaDays ??
            step?.sla ??
            step?.configSlaDays ??
            "N/A",
          tat: resolveExpectedTatDays(
            step?.HistoryTATDays ?? step?.historyTATDays,
            step?.tat,
            step?.ConfigTATDays ?? step?.configTATDays
          ),
          actualTat:
            step?.ActualTAT ??
            step?.actualTAT ??
            step?.actualTat ??
            (step?.ActualTATDays != null || step?.actualTATDays != null
              ? String(step?.ActualTATDays ?? step?.actualTATDays)
              : "N/A"),
          actualTatColor: (() => {
            const actualRaw = step?.ActualTATDays ?? step?.actualTATDays;
            const tatRaw = resolveExpectedTatDays(
              step?.HistoryTATDays ?? step?.historyTATDays,
              step?.tat,
              step?.ConfigTATDays ?? step?.configTATDays
            );
            if (actualRaw == null || actualRaw === "") return null;
            const actualDays = Number(actualRaw);
            const tatDays = Number(tatRaw);
            if (!Number.isFinite(actualDays) || !Number.isFinite(tatDays)) return null;
            return actualDays <= tatDays ? "#28a745" : "#dc3545";
          })(),
          userIdCode: String(
            step?.UserIdCode ?? step?.userIdCode ?? step?.userId ?? ""
          ).trim(),
          queries: (() => {
            const raw = step?.Queries ?? step?.queries;
            if (raw == null || raw === "") return "0";
            const n = Number(raw);
            return Number.isFinite(n) ? String(n) : "0";
          })(),
          srQty: step?.ActualSRQty ?? step?.actualSrQty ?? step?.srQty ?? "N/A",
          srValue: step?.ActualSRValue ?? step?.actualSrValue ?? step?.srValue ?? "N/A",
          approvedQty: step.approved ? (step.approvedQty || "0") : "N/A",
          approvedValue: step.approved ? (step.approvedValue || "0") : "N/A",
          comment: step.comment || "No comments",
        };
      });

      journeyStatusDataTableRef.current = $(journeyStatusTableRef.current).DataTable({
        data: tableData,
        paging: false,
        searching: false,
        info: false,
        lengthChange: false,
        lengthMenu: [5, 10, 25, 50, 100],
        pageLength: 5,
        dom: "rt",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        fixedColumns: false,
        order: [[0, "asc"]], // Sort by statusPriority column (hidden) ascending
        columnDefs: [
          { targets: 0, className: "text-start" }, // hidden sort column
          { targets: 1, className: "text-start" }, // Role
          { targets: 2, className: "text-start" }, // Action Taken By
          { targets: 3, className: "text-center" }, // Date
          { targets: 4, className: "text-center" }, // Status
          { targets: 5, className: "text-center" }, // SLA
          { targets: 6, className: "text-center" }, // TAT
          { targets: 7, className: "text-center" }, // Actual TAT
          { targets: 8, className: "text-center" }, // Queries (count)
          { targets: [9, 10, 11, 12], className: "text-end" }, // numeric
          { targets: 13, className: "text-start" }, // Comments
        ],
        columns: [
          { 
            title: "", 
            data: "statusPriority", 
            visible: false, 
            orderable: true 
          }, // Hidden sort column
          { title: "Role", data: "role", width: "220px" },
          { title: "Action Taken By", data: "actionBy", width: "200px" },
          { title: "Date Of Action", data: "date", width: "120px" },
          {
            title: "Status",
            data: null,
            width: "110px",
            render: (_d, _t, row) =>
              `<span style="color:${safeCssColor(row.statusColor)}; font-weight:600;">${escapeHtml(row.status)}</span>`,
          },
          { title: "SLA", data: "sla", className: "text-center", width: "70px" },
          { title: "TAT", data: "tat", className: "text-center", width: "70px" },
          {
            title: "Actual TAT",
            data: "actualTat",
            className: "text-center",
            width: "100px",
            render: (_d, _t, row) => {
              const text = String(row.actualTat ?? "").trim() || "N/A";
              const color = safeCssColor(row.actualTatColor || "#6c757d", "#6c757d");
              const weight = row.actualTatColor ? "600" : "400";
              return `<span style="color:${color}; font-weight:${weight};">${escapeHtml(text)}</span>`;
            },
          },
          {
            title: "Queries",
            data: "queries",
            className: "text-center",
            width: "70px",
            render: (_d, _t, row) => {
              const count = Number(row.queries);
              const safeCount = Number.isFinite(count) ? count : 0;
              if (safeCount <= 0) return `<span>0</span>`;
              const uid = escapeHtmlAttr(row.userIdCode || "");
              const uname = escapeHtmlAttr(row.actionBy || "");
              return `<a href="#collapseQueryHistory" class="js-journey-queries-link" data-user-id="${uid}" data-user-name="${uname}" style="color:#0d6efd; text-decoration:underline; font-weight:600; cursor:pointer;">${safeCount}</a>`;
            },
          },
          { title: "SR QTY", data: "srQty", className: "text-end", width: "90px" },
          { title: "SR VALUE", data: "srValue", className: "text-end", width: "100px" },
          { title: "Approved Qty", data: "approvedQty", className: "text-end", width: "110px" },
          { title: "Cal. Approved Value", data: "approvedValue", className: "text-end", width: "130px" },
          { title: "Comments", data: "comment", width: "350px" },
        ],
      });

      $(journeyStatusTableRef.current)
        .off("click.journeyQueries")
        .on("click.journeyQueries", ".js-journey-queries-link", function (e) {
          e.preventDefault();
          e.stopPropagation();
          const userId = $(this).attr("data-user-id") || "";
          const userName = $(this).attr("data-user-name") || "";
          openQueryHistoryDrilldownRef.current?.(userId, userName);
        });
    }

    return () => {
      if (journeyStatusTableRef.current) {
        $(journeyStatusTableRef.current).off("click.journeyQueries");
      }
      if (journeyStatusDataTableRef.current) {
        journeyStatusDataTableRef.current.destroy();
        journeyStatusDataTableRef.current = null;
      }
    };
  }, [srData]);

  useEffect(() => {
    const fetchServiceRequest = async () => {
      setLoading(true);
      try {

        // Fetch service request data from API
        try {
          const complaintResponse = await complaintAPI.getById(id);
          const complaintData = complaintResponse.data || complaintResponse;
          
          // Fetch approval workflow, levels, and history
          let approvalLevels = [];
          let approvalHistory = [];
          
          try {
            // Get workflow for the complaint
            const workflowResponse = await workflowAPI.getApprovalWorkflow(id);
            const workflow = workflowResponse.data || workflowResponse;
            
            if (workflow && workflow.workflowId) {
              // Get approval levels
              const levelsResponse = await workflowAPI.getApprovalLevels(workflow.workflowId);
              approvalLevels = levelsResponse.data || levelsResponse || [];
              
              // Get approval history
              const historyResponse = await workflowAPI.getApprovalHistory(id);
              approvalHistory = historyResponse.data || historyResponse || [];
            }
          } catch (error) {
            console.warn("Could not fetch workflow data:", error);
            // Continue with complaint data even if workflow data fails
          }

          // Transform approval history to journeyStatus format
          const journeyStatus = transformApprovalHistoryToJourneyStatus(approvalHistory, approvalLevels);

          const apiJourneyStatus = filterSkippedJourneySteps(complaintData.journeyStatus || []);
          const hasSpMetrics =
            Array.isArray(apiJourneyStatus) &&
            apiJourneyStatus.some(
              (x) =>
                x?.HistorySlaDays != null ||
                x?.historySlaDays != null ||
                x?.HistoryTATDays != null ||
                x?.historyTATDays != null ||
                x?.ActualSRQty != null ||
                x?.actualSrQty != null ||
                x?.ActualSRValue != null ||
                x?.actualSrValue != null
            );

          // Merge complaint data with journeyStatus
          const data = {
            ...complaintData,
            // If SP already provides journeyStatus with metrics, keep it (needed for SR Journey Status Details grid)
            journeyStatus: hasSpMetrics ? apiJourneyStatus : (journeyStatus.length > 0 ? journeyStatus : apiJourneyStatus),
          };

          setSrData(data);
          
          // Show End Consumer by default if AddCustomerType is B2C
          if (data && (data.addCustomerType || data.AddCustomerType || data.customerType) === "B2C") {
            setShowEndCustomer(true);
          }
          setLoading(false);
        } catch (apiError) {
          console.error("Error fetching from API:", apiError);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching service request:", error);
        setLoading(false);
      }
    };

    fetchServiceRequest();
  }, [id]);

  // Initialize previous complaints data when srData changes
  useEffect(() => {
    if (srData && srData.previousComplaints && Array.isArray(srData.previousComplaints)) {
      setPreviousComplaintsCount(srData.previousComplaints.length);
      setPreviousComplaintsData(srData.previousComplaints);
      if (srData.previousComplaints.length === 1) {
        setShowPreviousComplaintsTable(true);
      }
    } else {
      setPreviousComplaintsCount(0);
      setPreviousComplaintsData([]);
      setShowPreviousComplaintsTable(false);
    }
  }, [srData]);

  // Fetch queries when component loads or id changes
  useEffect(() => {
    const isNumeric = id && !isNaN(Number(id));
    if (!id) return;

    if (isNumeric) {
      fetchAllQueries();
      return;
    }

    // SR pages: use SRCode + childSrCode (from URL and/or srData)
      const parentSrCode = srData?.srCode || srData?.SRCode || id;
    if (parentSrCode) {
      fetchAllQueries();
    }
  }, [id, srData]);

  // Initialize Previous Complaints DataTable
  useEffect(() => {
    // Destroy previous instance
    if (historyDataTableInstanceRef.current) {
      historyDataTableInstanceRef.current.destroy();
      historyDataTableInstanceRef.current = null;
    }

    if (historyTableRef.current && previousComplaintsData.length > 0) {
      // Ensure DataTable plugin is registered on jQuery
      if (!$.fn.DataTable) {
        DataTable($);
      }

      // Transform the data from backend to match DataTable format
      const historyData = previousComplaintsData.map((complaint, index) => ({
        originalIndex: index, // Store original index for View button
        srNo: complaint.srNumber || "",
        srDate: complaint.srDate || "",
        productsInvolved: complaint.productsInvolved || "",
        srQty: complaint.srQuantity || 0,
        srValue: complaint.srValue || 0,
        status: complaint.statusName || "",
        actionTaken: complaint.actionTaken || "",
        latestComment: complaint.latestComment || "",
      }));

      historyDataTableInstanceRef.current = $(
        historyTableRef.current
      ).DataTable({
        data: historyData,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        pageLength: 10,
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        fixedColumns: false,
        order: [[1, "desc"]], // Order by SR Date descending
        columns: [
          {
            title: "SL No",
            data: null,
            orderable: false,
            render: (data, type, row, meta) => {
              return (meta.row + 1).toString();
            },
            className: "text-center",
          },
          {
            title: "SR No",
            data: "srNo",
            render: (data, type, row) => {
              if (type === "display") {
                return `<a href="#" class="sr-link-view" data-original-index="${escapeHtmlAttr(row.originalIndex)}" style="color: #007bff; text-decoration: none; cursor: pointer;">${escapeHtml(data || "")}</a>`;
              }
              return data || "";
            },
            className: "text-center",
          },
          {
            title: "SR Date",
            data: "srDate",
            render: (data) => {
              if (!data) return "";
              try {
                const date = new Date(data);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
              } catch {
                return data;
              }
            },
            className: "text-center",
          },
          {
            title: "Product(s) involved",
            data: "productsInvolved",
            className: "text-start",
          },
          {
            title: "SR QTY",
            data: "srQty",
            render: (data) => {
              return data ? data.toString() : "0";
            },
            className: "text-end",
          },
          {
            title: "SR VALUE",
            data: "srValue",
            render: (data) => {
              if (!data) return "₹0.00";
              return `₹${parseFloat(data).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`;
            },
            className: "text-end",
          },
          {
            title: "Action taken / Resolution status",
            data: "actionTaken",
            className: "text-center",
          },
          {
            title: "Action",
            data: null,
            orderable: false,
            render: (data, type, row) => {
              if (type === "display") {
                return `<button class="btn btn-sm btn-primary btn-view-complaint" data-original-index="${row.originalIndex}" style="padding: 4px 12px; font-size: 12px;">View</button>`;
              }
              return "";
            },
            className: "text-center",
          },
        ],
        initComplete: function (settings, json) {
          try {
            // Attach click handler for View button
            $(historyTableRef.current).off("click", ".btn-view-complaint");
            $(historyTableRef.current).on("click", ".btn-view-complaint", function (e) {
              e.preventDefault();
              const originalIndex = parseInt($(this).data("original-index"));
              setSelectedComplaintIndex(originalIndex);
              setShowPreviousComplaintsDetailModal(true);
            });
          
            // Attach click handler for SR Number link
            $(historyTableRef.current)
              .off("click", ".sr-link-view")
              .on("click", ".sr-link-view", function (e) {
                e.preventDefault();
                const originalIndex = parseInt($(this).data("original-index"));
                setSelectedComplaintIndex(originalIndex);
                setShowPreviousComplaintsDetailModal(true);
              });
          
            // Sync scroll head width with body width
            syncScrollHeadWidth(historyTableRef);
          } catch (e) {
            console.error("Error in Previous Complaints DataTable initComplete:", e);
          }
        },
        drawCallback: function (settings) {
          // Re-attach click handlers after redraw
          $(historyTableRef.current).off("click", ".btn-view-complaint");
          $(historyTableRef.current).on("click", ".btn-view-complaint", function (e) {
            e.preventDefault();
            const originalIndex = parseInt($(this).data("original-index"));
            setSelectedComplaintIndex(originalIndex);
            setShowPreviousComplaintsDetailModal(true);
          });
        
          $(historyTableRef.current).off("click", ".sr-link-view");
          $(historyTableRef.current).on("click", ".sr-link-view", function (e) {
            e.preventDefault();
            const originalIndex = parseInt($(this).data("original-index"));
            setSelectedComplaintIndex(originalIndex);
            setShowPreviousComplaintsDetailModal(true);
          });

          // Sync scroll head width with body width
          syncScrollHeadWidth(historyTableRef);
        },
      });

      // Add ResizeObserver for this specific table with debouncing
      let resizeObserver = null;
      let resizeTimeout = null;
      if (window.ResizeObserver) {
        const tableContainer = $(historyTableRef.current).closest(
          ".card-panel, .table-responsive"
        );
        if (tableContainer.length > 0) {
          resizeObserver = new ResizeObserver(() => {
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              if (historyDataTableInstanceRef.current) {
                try {
                  historyDataTableInstanceRef.current.columns.adjust();
                  syncScrollHeadWidth(historyTableRef);
                } catch (e) {
                  console.debug("Error adjusting history table columns:", e);
                }
              }
            }, 100);
          });
          resizeObserver.observe(tableContainer[0]);
        }
      }

      // Store observer and timeout in ref for cleanup
      if (historyTableRef.current) {
        historyTableRef.current._resizeObserver = resizeObserver;
        historyTableRef.current._resizeTimeout = resizeTimeout;
      }

      // Cleanup function
      return () => {
        if (historyTableRef.current) {
          if (historyTableRef.current._resizeObserver) {
            historyTableRef.current._resizeObserver.disconnect();
          }
          if (historyTableRef.current._resizeTimeout) {
            clearTimeout(historyTableRef.current._resizeTimeout);
          }
        }
        if (historyDataTableInstanceRef.current) {
          historyDataTableInstanceRef.current.destroy();
          historyDataTableInstanceRef.current = null;
        }
      };
    }
  }, [previousComplaintsData, showPreviousComplaintsTable]);

  // Function to sync dt-scroll-headInner width with dt-scroll-body width
  const syncScrollHeadWidth = (tableRef) => {
    if (!tableRef?.current) return;

    try {
      const $table = $(tableRef.current);
      const $wrapper = $table.closest(".dataTables_wrapper");
      if (!$wrapper.length) return;

      const $headInner = $wrapper.find(".dt-scroll-headInner");
      const $body = $wrapper.find(".dt-scroll-body");

      if ($headInner.length && $body.length) {
        // Use scrollWidth to get the actual content width, not just visible width
        const bodyElement = $body[0];
        const bodyWidth = bodyElement?.scrollWidth || $body.width() || 0;

        if (bodyWidth > 0) {
          const currentHeadWidth = $headInner.width() || 0;
          // Only update if widths differ to avoid unnecessary DOM updates
          if (Math.abs(currentHeadWidth - bodyWidth) > 1) {
            $headInner.css("width", bodyWidth + "px");
          }
        }
      }
    } catch (e) {
      // Silently handle errors - table might not be fully initialized
      console.debug("Error syncing scroll head width:", e);
    }
  };

  // Initialize DataTable for Product Details
  useEffect(() => {
    const products = srData?.products;
    if (
      productTableRef.current &&
      Array.isArray(products) &&
      products.length > 0
    ) {
      // Ensure DataTable plugin is registered on jQuery
      if (!$.fn.DataTable) {
        DataTable($);
      }

      // Destroy existing DataTable instance if it exists
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }

      const showComponentDetailsColumn = products.some((row) =>
        materialRowShowsComponentDetailsIcon(row)
      );

      // Determine if it's Export based on MarketCode/MarketName
      const marketCode = srData?.MarketCode || srData?.marketCode || "";
      const marketName = srData?.MarketName || srData?.marketName || "";
      let isExport = false;
      
      if (marketCode || marketName) {
        const codeUpper = marketCode.toUpperCase();
        const nameUpper = marketName.toUpperCase();
        
        if (
          codeUpper === "M002" ||
          codeUpper.includes("EXP") ||
          nameUpper.includes("EXPORT") ||
          codeUpper === "EXPORT" ||
          nameUpper === "EXPORT"
        ) {
          isExport = true;
        }
      }

      // Inject CSS for numeric column headers
      const styleId = "dt-numeric-header-style";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          .dt-numeric-col .dt-column-header {
            flex-direction: row !important;
          }
        `;
        document.head.appendChild(style);
      }

      // Build columns array conditionally
      const baseColumns = [
        {
          title: "SNo.",
          data: null,
          orderable: false,
          render: (data, type, row, meta) => {
            // In DataTable, when data is an array of objects, 'row' is the full row object (the product)
            return (meta.row + 1).toString();
          },
          className: "text-center",
        },
        {
          title: "Product Category",
          data: null,
          render: (data, type, row) => {
            // 'row' contains the full product object from the data array
            // Handle both camelCase and PascalCase property names
            let category =
              row?.ProductCategory ||
              row?.productCategory ||
              row?.Productcategory ||
              "";
            
            // Only apply mapping for display, filter, and type render modes
            if (type === "display" || type === "filter" || type === "type") {
              // Normalize category for comparison (trim and lowercase)
              const normalizedCategory = (category || "").trim().toLowerCase();
              
              // Map category values (case-insensitive matching)
              if (normalizedCategory === "makasaply" || normalizedCategory === "mikasaply") {
                category = "Plain Particle Board";
              } else if (normalizedCategory === "floors") {
                category = "Prelam";
              }
            }
            
            return category;
          },
        },
      ];

      baseColumns.push({
        title: "PlantName",
        data: null,
        render: (data, type, row, meta) => {
          // Handle both camelCase and PascalCase property names
          let plantName =
            row?.PlantName ||
            row?.plantName ||
            row?.Plantname ||
            row?.plant_name ||
            "";
          
          if (!plantName) plantName = "-";
          
          return plantName;
        },
      },
      {
        title: "Item Description",
        data: null,
        render: (data, type, row, meta) => {
          let itemDescription =
            row?.ProductName || row?.productName || row?.Productname || "";
          
          // For SNo 1 (first row, index 0), change "Plain Particle Board 8mm" to "Prelam Board 8mm"
          if (meta.row === 0 && itemDescription === "Plain Particle Board 8mm") {
            itemDescription = "Prelam Board 8mm";
          }
          
          return itemDescription;
        },
      },
      {
        title: "Product Details",
        data: null,
        orderable: false,
        render: (data, type, row) => {
          if (type === "display") {
            // Store row data in ref using product code as key
            const productKey =
              row?.ProductCode ||
              row?.productCode ||
              row?.Productcode ||
              `row_${Date.now()}_${Math.random()}`;
            productDataRef.current[productKey] = row;
            return `<div class="product-details-icon" data-product-key="${productKey}" style="cursor: pointer; display: inline-block; text-align: center;">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1976d2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle;">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </div>`;
          }
          return "";
        },
        className: "text-center",
      },
      {
        title: "Invoice Quantity",
        data: null,
        render: (data, type, row) => {
          const qty = row?.Quantity ?? row?.quantity ?? row?.Quantity ?? 0;
          return qty.toString();
        },
        className: "text-end dt-numeric-col",
      },
      {
        title: "UOM",
        data: null,
        render: () => "PCS",
        className: "text-center",
      },
      {
        title: "Invoice Unit Price",
        data: null,
        render: (data, type, row) => {
          const price = row?.Price ?? row?.price ?? row?.Price ?? 0;
          return parseFloat(price).toFixed(2);
        },
        className: "text-end dt-numeric-col",
      },
      {
        title: "Invoice value Total",
        data: null,
        render: (data, type, row) => {
          const qty = parseFloat(
            row?.Quantity ?? row?.quantity ?? row?.Quantity ?? 0
          );
          const price = parseFloat(
            row?.Price ?? row?.price ?? row?.Price ?? 0
          );
          return (qty * price).toFixed(2);
        },
        className: "text-end dt-numeric-col",
      },
      {
        title: "Total Price",
        data: null,
        render: (data, type, row) => {
          const qty = parseFloat(
            row?.Quantity ?? row?.quantity ?? row?.Quantity ?? 0
          );
          const price = parseFloat(
            row?.Price ?? row?.price ?? row?.Price ?? 0
          );
          return (qty * price).toFixed(2);
        },
        className: "text-end dt-numeric-col",
      },
      {
        title: "Component Details",
        data: null,
        orderable: false,
        visible: showComponentDetailsColumn,
        render: (data, type, row, meta) => {
          if (type === "display") {
            if (!materialRowShowsComponentDetailsIcon(row)) {
              return "";
            }
            const key =
              row?.ProductCode ||
              row?.productCode ||
              row?.Productcode ||
              `row-${meta.row}`;
            return `
              <div class="component-details-icon" data-product-key="${key}" style="cursor: pointer; display: inline-block; text-align: center;">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1976d2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle;">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </div>
            `;
          }
          return "";
        },
        className: "text-center",
      },
      {
        title: "SR Category",
        data: null,
        orderable: false,
        render: (data, type, row, meta) => {
          // 'row' contains the full product object
          const key =
            row?.ProductCode ||
            row?.productCode ||
            row?.Productcode ||
            `row-${meta.row}`;
          const val = row?.Category || row?.category || row?.SRCategory || row?.srCategory || "";
          const options = [
            { value: "Packaging / Dispatch", text: "Packaging / Dispatch" },
            { value: "Quality", text: "Quality" },
            { value: "Installation", text: "Installation" },
            { value: "Commercial", text: "Commercial" },
          ];
          const optionsHtml = options
            .map(
              (opt) =>
                `<option value="${opt.value}" ${
                  val === opt.value ? "selected" : ""
                }>${opt.text}</option>`
            )
            .join("");
          return `
            <select
              class="form-select form-select-sm category-select"
              data-key="${key}"
              style="min-width: 150px;"
              disabled
              readonly
            >
              <option value="">Select SR Category</option>
              ${optionsHtml}
            </select>
          `;
        },
        className: "text-center category-cell",
      },
      {
        title: "SR Qty",
        data: null,
        orderable: false,
        render: (data, type, row, meta) => {
          const key =
            row?.ProductCode ||
            row?.productCode ||
            row?.Productcode ||
            `row-${meta.row}`;
          const complaintQty = row?.ComplaintQty ?? row?.complaintQty ?? "";
          return `
            <input
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              class="form-control form-control-sm complaint-qty-input"
              data-key="${key}"
              value="${complaintQty}"
              style="max-width: 120px; text-align: right; background-color: #e9ecef;"
              disabled
              readonly
            />
          `;
        },
        className: "text-end complaint-qty-cell",
      },
      {
        title: "SR Value",
        data: null,
        render: (data, type, row) => {
          // Calculate complaint value based on complaint qty and price
          const key =
            row?.ProductCode || row?.productCode || row?.Productcode || "";
          const complaintQty = parseFloat(
            row?.ComplaintQty ?? row?.complaintQty ?? 0
          );
          const price = parseFloat(
            row?.Price ?? row?.price ?? row?.Price ?? 0
          );
          return (complaintQty * price).toFixed(2);
        },
        className: "text-end dt-numeric-col",
      },
      {
        title: "Approve Qty",
        data: null,
        orderable: false,
        render: (data, type, row, meta) => {
          const key =
            row?.ProductCode ||
            row?.productCode ||
            row?.Productcode ||
            `row-${meta.row}`;
          const approvedQty = row?.ApprovedQty ?? row?.approvedQty ?? row?.ApprovedQuantity ?? row?.approvedQuantity ?? "";
          return `
            <input
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              class="form-control form-control-sm approve-qty-input"
              data-key="${key}"
              value="${approvedQty}"
              style="max-width: 120px; text-align: right; background-color: #fff;"
            />
          `;
        },
        className: "text-end approve-qty-cell",
      },
      {
        title: "Approve Value",
        data: null,
        orderable: false,
        render: (data, type, row, meta) => {
          const key =
            row?.ProductCode ||
            row?.productCode ||
            row?.Productcode ||
            `row-${meta.row}`;
          const approvedQty = parseFloat(
            row?.ApprovedQty ?? row?.approvedQty ?? row?.ApprovedQuantity ?? row?.approvedQuantity ?? 0
          );
          const price = parseFloat(
            row?.Price ?? row?.price ?? row?.Price ?? 0
          );
          const calculatedValue = (approvedQty * price).toFixed(2);
          const approvedValue = row?.ApprovedValue ?? row?.approvedValue ?? row?.ApprovedVal ?? row?.approvedVal ?? calculatedValue;
          return `
            <input
              type="text"
              inputmode="numeric"
              pattern="[0-9.]*"
              class="form-control form-control-sm approve-value-input"
              data-key="${key}"
              value="${approvedValue}"
              style="max-width: 120px; text-align: right; background-color: #fff;"
            />
          `;
        },
        className: "text-end approve-value-cell",
      },
      {
        title: "Action",
        data: null,
        orderable: false,
        render: (data, type, row, meta) => {
          if (type === "display") {
            const key =
              row?.ProductCode ||
              row?.productCode ||
              row?.Productcode ||
              `row-${meta.row}`;
            return `
              <button
                class="btn btn-sm approve-reject-btn"
                data-product-key="${key}"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Approve / Reject"
                style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); 
                       border: none; 
                       color: white; 
                       padding: 8px 10px; 
                       border-radius: 6px;">
                <i class="bi bi-check-circle-fill"></i>
              </button>
            `;
          }
          return "";
        },
        className: "text-center",
      });

      dataTableInstanceRef.current = $(productTableRef.current).DataTable({
        data: products,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        pageLength: 5,
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        fixedColumns: false,
        order: [[3, "asc"]],
        columns: baseColumns,
        initComplete: function (settings, json) {
          try {
            // Wrap dt-length and dt-search into a single top filter wrapper
            const wrapper = $(productTableRef.current).closest(
              ".dataTables_wrapper, .dt-container"
            );
            if (wrapper.length > 0) {
              const lengthEl = wrapper.find(".dt-length");
              const searchEl = wrapper.find(".dt-search");
              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              // Wrap dt-info and dt-paging into a single bottom filter wrapper
              const infoEl = wrapper.find(".dt-info");
              const pagingEl = wrapper.find(".dt-paging");
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            }

            // Wrap dt-scroll with an inner wrapper and ensure outer wrapper has dtScrlWrpr
            const dtScrollDiv = $(productTableRef.current).closest(
              ".dt-scroll"
            );
            if (dtScrollDiv.length > 0) {
              if (!dtScrollDiv.parent().hasClass("dtScrlWrprInner")) {
                dtScrollDiv.wrap('<div class="dtScrlWrprInner"></div>');
              }
              const outer = dtScrollDiv.parent().parent();
              if (outer.length > 0) outer.addClass("dtScrlWrpr");
            } else {
              const container = $(productTableRef.current).closest(
                ".table-responsive"
              );
              if (container.length > 0) {
                const dtScroll = container.find(".dt-scroll");
                if (dtScroll.length > 0) {
                  dtScroll.each(function () {
                    const $this = $(this);
                    if (!$this.parent().hasClass("dtScrlWrprInner")) {
                      $this.wrap('<div class="dtScrlWrprInner"></div>');
                    }
                    const outer = $this.parent().parent();
                    if (outer.length > 0) outer.addClass("dtScrlWrpr");
                  });
                }
              }
            }

            // Initial column adjustment
            setTimeout(() => {
              if (dataTableInstanceRef.current) {
                dataTableInstanceRef.current.columns.adjust();
                // Sync scroll head width with body width after a slight delay to ensure DOM is updated
                setTimeout(() => {
                  syncScrollHeadWidth(productTableRef);
                }, 50);
              }
            }, 100);

            // Attach click handler for product details icon
            $(productTableRef.current).off("click", ".product-details-icon");
            $(productTableRef.current).on(
              "click",
              ".product-details-icon",
              function (e) {
                e.preventDefault();
                e.stopPropagation();
                const productKey = $(this).data("product-key");
                const productData = productDataRef.current[productKey];
                if (productData) {
                  handleProductDetailsOpen(productData);
                }
              }
            );

            // Attach click handler for component details icon
            $(productTableRef.current).off("click", ".component-details-icon");
            $(productTableRef.current).on(
              "click",
              ".component-details-icon",
              function (e) {
                e.preventDefault();
                e.stopPropagation();
                const productKey = $(this).data("product-key");
                if (productKey) {
                  handleComponentDetailsOpen(productKey);
                }
              }
            );

            // Attach click handler for Approve/Reject button
            $(productTableRef.current).off("click", ".approve-reject-btn");
            $(productTableRef.current).on(
              "click",
              ".approve-reject-btn",
              function (e) {
                e.preventDefault();
                e.stopPropagation();
                // Open the Approve/Reject modal
                handleApproveRejectOpen();
              }
            );

            // Initialize Bootstrap tooltips
            setTimeout(() => {
              const tooltipTriggerList = [].slice.call(
                $(productTableRef.current).find('[data-bs-toggle="tooltip"]')
              );
              tooltipTriggerList.forEach(function (tooltipTriggerEl) {
                // Destroy existing tooltip if any
                const existingTooltip = window.bootstrap?.Tooltip?.getInstance(tooltipTriggerEl);
                if (existingTooltip) {
                  existingTooltip.dispose();
                }
                // Create new tooltip
                if (window.bootstrap?.Tooltip) {
                  new window.bootstrap.Tooltip(tooltipTriggerEl);
                }
              });
            }, 100);

            // Attach change handler for Approve Qty input
            $(productTableRef.current).off("input change", ".approve-qty-input");
            $(productTableRef.current).on(
              "input change",
              ".approve-qty-input",
              function (e) {
                const key = $(this).data("key");
                const approvedQty = parseFloat($(this).val()) || 0;
                
                // Find the corresponding row and get the price
                const row = dataTableInstanceRef.current.row($(this).closest("tr"));
                const rowData = row.data();
                const price = parseFloat(rowData?.Price ?? rowData?.price ?? rowData?.Price ?? 0);
                
                // Auto-calculate Approve Value
                const calculatedValue = (approvedQty * price).toFixed(2);
                const approveValueInput = $(this).closest("tr").find(".approve-value-input");
                if (approveValueInput.length > 0) {
                  approveValueInput.val(calculatedValue);
                }
                
                // Update the row data
                if (rowData) {
                  rowData.ApprovedQty = approvedQty;
                  rowData.ApprovedValue = parseFloat(calculatedValue);
                }
              }
            );

            // Attach change handler for Approve Value input
            $(productTableRef.current).off("input change", ".approve-value-input");
            $(productTableRef.current).on(
              "input change",
              ".approve-value-input",
              function (e) {
                const key = $(this).data("key");
                const approvedValue = parseFloat($(this).val()) || 0;
                
                // Update the row data
                const row = dataTableInstanceRef.current.row($(this).closest("tr"));
                const rowData = row.data();
                if (rowData) {
                  rowData.ApprovedValue = approvedValue;
                }
              }
            );
          } catch (e) {
            console.error("Error in DataTable initComplete wrapper:", e);
          }
        },
        drawCallback: function (settings) {
          // Sync scroll head width with body width on each draw
          // Use setTimeout to ensure DOM is fully updated
          setTimeout(() => {
            syncScrollHeadWidth(productTableRef);
          }, 10);

          // Re-attach click handler for product details icon after redraw
          $(productTableRef.current).off("click", ".product-details-icon");
          $(productTableRef.current).on(
            "click",
            ".product-details-icon",
            function (e) {
              e.preventDefault();
              e.stopPropagation();
              const productKey = $(this).data("product-key");
              const productData = productDataRef.current[productKey];
              if (productData) {
                handleProductDetailsOpen(productData);
              }
            }
          );

          // Re-attach click handler for component details icon after redraw
          $(productTableRef.current).off("click", ".component-details-icon");
          $(productTableRef.current).on(
            "click",
            ".component-details-icon",
            function (e) {
              e.preventDefault();
              e.stopPropagation();
              const productKey = $(this).data("product-key");
              if (productKey) {
                handleComponentDetailsOpen(productKey);
              }
            }
          );

          // Re-attach click handler for Approve/Reject button after redraw
          $(productTableRef.current).off("click", ".approve-reject-btn");
          $(productTableRef.current).on(
            "click",
            ".approve-reject-btn",
            function (e) {
              e.preventDefault();
              e.stopPropagation();
              // Open the Approve/Reject modal
              handleApproveRejectOpen();
            }
          );

          // Re-initialize Bootstrap tooltips after redraw
          setTimeout(() => {
            const tooltipTriggerList = [].slice.call(
              $(productTableRef.current).find('[data-bs-toggle="tooltip"]')
            );
            tooltipTriggerList.forEach(function (tooltipTriggerEl) {
              // Destroy existing tooltip if any
              const existingTooltip = window.bootstrap?.Tooltip?.getInstance(tooltipTriggerEl);
              if (existingTooltip) {
                existingTooltip.dispose();
              }
              // Create new tooltip
              if (window.bootstrap?.Tooltip) {
                new window.bootstrap.Tooltip(tooltipTriggerEl);
              }
            });
          }, 100);

          // Re-attach change handler for Approve Qty input after redraw
          $(productTableRef.current).off("input change", ".approve-qty-input");
          $(productTableRef.current).on(
            "input change",
            ".approve-qty-input",
            function (e) {
              const key = $(this).data("key");
              const approvedQty = parseFloat($(this).val()) || 0;
              
              // Find the corresponding row and get the price
              const row = dataTableInstanceRef.current.row($(this).closest("tr"));
              const rowData = row.data();
              const price = parseFloat(rowData?.Price ?? rowData?.price ?? rowData?.Price ?? 0);
              
              // Auto-calculate Approve Value
              const calculatedValue = (approvedQty * price).toFixed(2);
              const approveValueInput = $(this).closest("tr").find(".approve-value-input");
              if (approveValueInput.length > 0) {
                approveValueInput.val(calculatedValue);
              }
              
              // Update the row data
              if (rowData) {
                rowData.ApprovedQty = approvedQty;
                rowData.ApprovedValue = parseFloat(calculatedValue);
              }
            }
          );

          // Re-attach change handler for Approve Value input after redraw
          $(productTableRef.current).off("input change", ".approve-value-input");
          $(productTableRef.current).on(
            "input change",
            ".approve-value-input",
            function (e) {
              const key = $(this).data("key");
              const approvedValue = parseFloat($(this).val()) || 0;
              
              // Update the row data
              const row = dataTableInstanceRef.current.row($(this).closest("tr"));
              const rowData = row.data();
              if (rowData) {
                rowData.ApprovedValue = approvedValue;
              }
            }
          );
        },
      });

      // Add ResizeObserver for this specific table with debouncing
      let resizeObserver = null;
      let resizeTimeout = null;
      if (window.ResizeObserver) {
        const tableContainer = $(productTableRef.current).closest(
          ".card-panel, .table-responsive"
        )[0];
        if (tableContainer) {
          resizeObserver = new ResizeObserver((entries) => {
            // Debounce the resize adjustments to prevent rapid firing
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              if (dataTableInstanceRef.current) {
                try {
                  // Use requestAnimationFrame to ensure smooth rendering
                  requestAnimationFrame(() => {
                    if (dataTableInstanceRef.current) {
                      dataTableInstanceRef.current.columns.adjust().draw(false);
                      // Sync scroll head width with body width after resize
                      setTimeout(() => {
                        syncScrollHeadWidth(productTableRef);
                      }, 50);
                    }
                  });
                } catch (e) {
                  // Silently handle errors - ResizeObserver errors are usually benign
                  if (!e.message.includes("ResizeObserver")) {
                    console.error("Error in ResizeObserver adjust:", e);
                  }
                }
              }
            }, 150); // 150ms debounce
          });
          resizeObserver.observe(tableContainer);
        }
      }

      // Store resizeObserver and timeout for cleanup
      if (productTableRef.current) {
        productTableRef.current._resizeObserver = resizeObserver;
        productTableRef.current._resizeTimeout = resizeTimeout;
      }
    }

    // Cleanup on unmount
    return () => {
      // Cleanup ResizeObserver and timeout
      if (productTableRef.current) {
        if (productTableRef.current._resizeTimeout) {
          clearTimeout(productTableRef.current._resizeTimeout);
          delete productTableRef.current._resizeTimeout;
        }
        if (productTableRef.current._resizeObserver) {
          productTableRef.current._resizeObserver.disconnect();
          delete productTableRef.current._resizeObserver;
        }
      }
      if (dataTableInstanceRef.current) {
        try {
          dataTableInstanceRef.current.destroy();
          dataTableInstanceRef.current = null;
        } catch (err) {
          console.error("Error destroying DataTable:", err);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srData?.products]);

  const getStatusColor = (status) => {
    if (status === "Open") return "#1976d2"; // blue
    if (status === "Pending") return "#ff9800"; // orange
    if (status === "Closed") return "#4caf50"; // green
    return "#000000"; // default black
  };

  const getPriorityColor = (priority) => {
    if (priority === "High") return "#f44336"; // red
    if (priority === "Medium") return "#ff9800"; // orange
    if (priority === "Low") return "#4caf50"; // green
    return "#000000"; // default black
  };

  if (loading) {
    return (
      <div className="p-3">
        <p>Loading...</p>
      </div>
    );
  }

  if (!srData) {
    return (
      <div className="p-3">
        <p>Service Request not found</p>
        <button
          className="btn btn-primary mt-2"
          onClick={() => navigate("/complaints")}
          style={{
            background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
            border: "none",
            color: "white",
            fontWeight: 500,
            letterSpacing: "0.5px",
            fontSize: "11px",
            boxShadow: "0 4px 15px rgba(245, 87, 108, 0.4)",
            borderRadius: "4px",
            textTransform: "uppercase",
            padding: "8px 24px",
            transition: "all 0.3s ease",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(245, 87, 108, 0.6)";
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "0 4px 15px rgba(245, 87, 108, 0.4)";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <ArrowBackIcon style={{ fontSize: "16px" }} />
          Back to List
        </button>
      </div>
    );
  }

  return (
    <div className="viewServReqPg">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <i
            className="bi bi-arrow-left"
            style={{
              fontSize: "24px",
              cursor: "pointer",
              marginRight: "15px",
              color: "#333333",
            }}
            onClick={() => navigate("/complaints")}
            title="Back to View SR"
          ></i>
          <h1
            className="service-request-heading"
            style={{ color: "#333333", fontWeight: 600, margin: 0 }}
          >
            Service Request Details
          </h1>
        </div>
        <div className="d-flex gap-2">
              {/* Combined Approve/Reject Button */}
              <button
                className="btn"
                style={{
                  background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  boxShadow: "0 4px 15px rgba(40, 167, 69, 0.4)",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  padding: "8px 24px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(40, 167, 69, 0.6)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(40, 167, 69, 0.4)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                onClick={handleApproveRejectOpen}
              >
                <CheckCircleIcon style={{ fontSize: "16px" }} />
                {isDosAdminApprover ? "Assign" : "Approve / Reject"}
              </button>
              {/* Raise Query Button */}
              <button
                className="btn"
                style={{
                  background: "linear-gradient(135deg, #ff8c00 0%, #ff6b00 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  boxShadow: "0 4px 15px rgba(255, 140, 0, 0.4)",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  padding: "8px 24px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(255, 140, 0, 0.6)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(255, 140, 0, 0.4)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                onClick={handleRaiseQueryOpen}
              >
                <QuestionAnswerIcon style={{ fontSize: "16px" }} />
                Raise Query
              </button>
              {/* Print — All Print or Checklist Print */}
              <div style={{ position: "relative" }}>
              <button
                className="btn"
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  padding: "8px 24px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(102, 126, 234, 0.6)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.4)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                onClick={() => {
                  if (!printDownloading) setPrintMenuOpen((open) => !open);
                }}
                disabled={printDownloading}
              >
                <PrintIcon style={{ fontSize: "16px" }} />
                {printDownloading
                  ? `Downloading${typeof printProgress === "number" ? `… ${printProgress}%` : "…"}`
                  : "Print"}
                {!printDownloading && (
                  <KeyboardArrowDownIcon
                    style={{
                      fontSize: "18px",
                      marginLeft: "2px",
                      transform: printMenuOpen ? "rotate(180deg)" : "none",
                      transition: "transform 0.2s ease",
                    }}
                  />
                )}
              </button>
              {printMenuOpen && !printDownloading && (
                <>
                <div
                  onClick={() => setPrintMenuOpen(false)}
                  style={{ position: "fixed", inset: 0, zIndex: 29 }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    right: 0,
                    zIndex: 30,
                    width: "280px",
                    background: "#fff",
                    border: "1px solid #e6eaf2",
                    borderRadius: "10px",
                    boxShadow: "0 12px 32px rgba(26, 32, 44, 0.16)",
                    overflow: "hidden",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      padding: "10px 14px 8px",
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      color: "#64748b",
                      textTransform: "uppercase",
                      borderBottom: "1px solid #eef2f7",
                    }}
                  >
                    Print options
                  </div>
                  {[
                    {
                      scope: "all",
                      label: "All Print",
                      hint: "Complete service request",
                      iconBg: "#eef2ff",
                      iconColor: "#4f46e5",
                    },
                    {
                      scope: "checklist",
                      label: "Checklist Print",
                      hint: "Basic information, invoice and checklist",
                      iconBg: "#ecfdf5",
                      iconColor: "#047857",
                    },
                  ].map((option) => (
                    <button
                      key={option.scope}
                      type="button"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        borderBottom: option.scope === "all" ? "1px solid #eef2f7" : "none",
                        background: "#fff",
                        color: "#0f172a",
                        padding: "12px 14px",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f8fafc";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#fff";
                      }}
                      onClick={async () => {
                        setPrintMenuOpen(false);
                        try {
                          setPrintDownloading(true);
                          setPrintProgress(0);
                          const parentSrCode = (id || "").toString().trim();
                          const childSrCode =
                            (childSrCodeFromQuery || srData?.ChildSRNumber || srData?.childSRNumber || srData?.childSrCode || "")
                              .toString()
                              .trim();
                          const resp = await complaintAPI.getComplaintPrintPdf({
                            srCode: parentSrCode,
                            childSrCode,
                            scope: option.scope,
                            onProgress: ({ percent }) => {
                              if (typeof percent === "number") setPrintProgress(percent);
                            },
                          });
                          const html = await htmlFromPrintResponse(resp);
                          openTrustedPrintHtml(html);
                        } catch (e) {
                          console.error("Failed to open print PDF:", e);
                          const msg =
                            (e && (e.message || e.toString && e.toString())) ||
                            "Failed to generate/open print PDF. Please try again.";
                          alert(msg);
                        } finally {
                          setPrintDownloading(false);
                          setPrintProgress(null);
                        }
                      }}
                    >
                      <span
                        style={{
                          width: "34px",
                          height: "34px",
                          borderRadius: "8px",
                          background: option.iconBg,
                          color: option.iconColor,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {option.scope === "all" ? (
                          <DescriptionOutlinedIcon style={{ fontSize: "18px" }} />
                        ) : (
                          <FactCheckOutlinedIcon style={{ fontSize: "18px" }} />
                        )}
                      </span>
                      <span style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 650, letterSpacing: "0.01em" }}>
                          {option.label}
                        </span>
                        <span style={{ fontSize: "11px", fontWeight: 500, color: "#64748b" }}>
                          {option.hint}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
                </>
              )}
              </div>
              {printDownloading && (
                <div style={{ width: "220px", marginLeft: "12px" }}>
                  <div
                    style={{
                      height: "6px",
                      background: "rgba(255,255,255,0.35)",
                      borderRadius: "6px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "6px",
                        width: `${typeof printProgress === "number" ? printProgress : 10}%`,
                        background: "rgba(255,255,255,0.9)",
                        transition: "width 120ms linear",
                      }}
                    />
                  </div>
                </div>
              )}
        </div>
      </div>

      <div>
        {/* Basic Information - Collapsible */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapseBasicInfo"
                aria-expanded="true"
                aria-controls="collapseBasicInfo"
              >
                <span>Basic Information</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div className="collapse show viewDetCardPnlOutr" id="collapseBasicInfo">
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 0 }}>
                <div className="p-3">
                  <div className="row">
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">SR Number</div>
                        <div
                          className="detail-value"
                          style={{ fontWeight: 600 }}
                        >
                          {srData.srNumber}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Status</div>
                        <div
                          className="detail-value"
                          style={{
                            color: getStatusColor(srData.statusName),
                            fontWeight: 600,
                          }}
                        >
                          {safeChild(srData?.statusName)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">SR Category</div>
                        <div className="detail-value">
                          {srData.complaintTypeName}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Product Category</div>
                        <div className="detail-value">
                          {safeChild(srData?.productCategory)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Created Date</div>
                        <div className="detail-value">
                          {formatDate(srData.createdDate)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Raised By</div>
                        <div className="detail-value">
                          {safeChild(srData?.createdByName)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Assigned To</div>
                        <div className="detail-value">
                          {safeChild(srData?.assignedToName)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SR Journey Status - Collapsible */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapseJourneyStatus"
                aria-expanded="true"
                aria-controls="collapseJourneyStatus"
              >
                <span>SR Journey Status</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div
            className="collapse show viewDetCardPnlOutr"
            id="collapseJourneyStatus"
          >
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 0, paddingBottom: "8px" }}>
                <div className="px-2 pt-0 pb-0">
                  {srData.journeyStatus &&
                    (() => {
                      // Find the index of the last approved step (SE is by default approved)
                      let lastApprovedIndex = -1;
                      for (
                        let i = srData.journeyStatus.length - 1;
                        i >= 0;
                        i--
                      ) {
                        const s = srData.journeyStatus[i];
                        const isApproved = isInitiatorSEStep(s) || s.approved;
                        if (isApproved) {
                          lastApprovedIndex = i;
                          break;
                        }
                      }

                      // TAT (Days): always use ConfigTATDays (from backend configuration)
                      // We read it from the "to/next" step since config is defined per level.
                      const getConfigTatDays = (fromStep, toStep) => {
                        const raw =
                          toStep?.ConfigTATDays ??
                          toStep?.configTATDays ??
                          fromStep?.ConfigTATDays ??
                          fromStep?.configTATDays ??
                          null;
                        if (raw === "" || raw == null) return null;
                        const n = Number(raw);
                        return Number.isFinite(n) ? n : null;
                      };

                      const getConfigTatLabel = (fromStep, toStep) => {
                        const n = getConfigTatDays(fromStep, toStep);
                        return n == null ? "N/A" : String(n);
                      };

                      // Helper function to calculate actual days between two steps
                      const calculateActualDays = (fromStep, toStep) => {
                        if (!fromStep?.date || !toStep?.date) return null;
                        
                        try {
                          // Parse date strings - handles both "23 Jul 2024" and "2025-12-18" formats
                          const parseDate = (dateStr) => {
                            if (!dateStr) return null;
                            
                            // Try ISO format first (YYYY-MM-DD)
                            if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                              return new Date(dateStr);
                            }
                            
                            // Try format like "23 Jul 2024"
                            const months = {
                              'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
                              'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
                            };
                            const parts = dateStr.trim().split(' ');
                            if (parts.length === 3) {
                              const day = parseInt(parts[0]);
                              const month = months[parts[1]];
                              const year = parseInt(parts[2]);
                              if (!isNaN(day) && month !== undefined && !isNaN(year)) {
                                return new Date(year, month, day);
                              }
                            }
                            return null;
                          };
                          
                          const fromDate = parseDate(fromStep.date);
                          const toDate = parseDate(toStep.date);
                          
                          if (!fromDate || !toDate || isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) return null;
                          
                          const diffTime = Math.abs(toDate - fromDate);
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          return diffDays;
                        } catch (error) {
                          return null;
                        }
                      };

                      // Helper function to get color for day count based on TAT
                      const getDayCountColor = (fromDesignation, toDesignation, fromStep, toStep) => {
                        const expectedTAT = getConfigTatDays(fromStep, toStep);
                        if (!expectedTAT) {
                          // If no expected TAT, use default logic
                          const from = fromDesignation?.toUpperCase().trim();
                          const to = toDesignation?.toUpperCase().trim();
                          if (from === "QC HEAD" && (to === "MFG. HEAD" || to === "MFG HEAD")) return "#dc3545";
                          return "#007bff";
                        }
                        
                        // Calculate actual days
                        const actualDays = calculateActualDays(fromStep, toStep);
                        if (actualDays === null) {
                          // If can't calculate actual days, use default logic
                          const from = fromDesignation?.toUpperCase().trim();
                          const to = toDesignation?.toUpperCase().trim();
                          if (from === "QC HEAD" && (to === "MFG. HEAD" || to === "MFG HEAD")) return "#dc3545";
                          return "#007bff";
                        }
                        
                        // If actual days <= expected TAT (right time), use blue
                        if (actualDays <= expectedTAT) {
                          return "#007bff";
                        }
                        
                        // If actual days > expected TAT, use red
                        return "#dc3545";
                      };

                      return (
                        <>
                        <JourneyIconTatTooltip tip={journeyIconTip} />
                        <div
                          className="journey-status-scroll-container"
                          style={{
                            overflowX: "auto",
                            overflowY: "visible",
                            width: "100%",
                            paddingBottom: 0,
                            WebkitOverflowScrolling: "touch",
                            scrollbarWidth: "thin",
                            scrollbarColor: "#667eea #f1f1f1",
                          }}
                          onScroll={() => setJourneyIconTip(null)}
                        >
                          <div
                          className="journey-status-wrapper"
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "flex-start",
                            position: "relative",
                            minWidth: "max-content",
                            padding: "10px 4px 4px",
                          }}
                          >
                          {srData.journeyStatus && srData.journeyStatus.map((step, index) => {
                            // SE is by default approved for display
                            const isSE = isInitiatorSEStep(step);
                            const isApproved = isSE || step.approved;
                            const isRejected = isSE ? false : step.rejected;
                            // Check if this is the immediate next step after the last approved step
                            const isNextPendingAfterApproved =
                              lastApprovedIndex >= 0 &&
                              index === lastApprovedIndex + 1 &&
                              !isApproved &&
                              !isRejected;
                            const iconTat = resolveJourneyIconTatDays(step);
                            const iconActualTat = resolveJourneyIconActualTat(step);

                            return (
                              <React.Fragment key={index}>
                                {/* Step Item */}
                                <div
                                  className="journey-step-item"
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    flex: 1,
                                    position: "relative",
                                    zIndex: 1,
                                    minWidth: "90px",
                                  }}
                                >
                                  {/* Circle */}
                                  <div
                                    className="journey-step-icon-wrap"
                                    aria-label={`TAT: ${iconTat}. Actual TAT: ${iconActualTat}`}
                                    tabIndex={0}
                                    onMouseEnter={(e) =>
                                      setJourneyIconTip(
                                        readJourneyIconTipFromEl(
                                          e.currentTarget,
                                          iconTat,
                                          iconActualTat,
                                          ""
                                        )
                                      )
                                    }
                                    onMouseLeave={() => setJourneyIconTip(null)}
                                    onFocus={(e) =>
                                      setJourneyIconTip(
                                        readJourneyIconTipFromEl(
                                          e.currentTarget,
                                          iconTat,
                                          iconActualTat,
                                          ""
                                        )
                                      )
                                    }
                                    onBlur={() => setJourneyIconTip(null)}
                                  >
                                  <div
                                    className={`journey-step-circle ${
                                      isApproved
                                        ? "journey-step-approved"
                                        : ""
                                    } ${
                                      isRejected
                                        ? "journey-step-rejected"
                                        : ""
                                    } ${
                                      !isApproved && !isRejected
                                        ? "journey-step-pending"
                                        : ""
                                    }`}
                                    style={{
                                      width: isApproved || isRejected ? "26px" : "24px",
                                      height: isApproved || isRejected ? "26px" : "24px",
                                      borderRadius: "50%",
                                      background: isRejected
                                        ? "linear-gradient(135deg, #dc3545 0%, #c82333 100%)"
                                        : isApproved
                                        ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                                        : isNextPendingAfterApproved
                                        ? "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)"
                                        : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                                      border:
                                        isApproved || isRejected
                                          ? "none"
                                          : isNextPendingAfterApproved
                                          ? "2px solid #ff8c00"
                                          : "2px solid #dee2e6",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      marginBottom: "6px",
                                      position: "relative",
                                      zIndex: 2,
                                      boxShadow: isRejected
                                        ? "0 4px 12px rgba(220, 53, 69, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                                        : isApproved
                                        ? "0 4px 12px rgba(40, 167, 69, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                                        : isNextPendingAfterApproved
                                        ? "0 4px 12px rgba(255, 140, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                                        : "0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                                      transition: "all 0.3s ease",
                                      cursor: "help",
                                    }}
                                    onMouseEnter={(e) => {
                                      if (isApproved || isRejected) {
                                        e.currentTarget.style.transform = "scale(1.15)";
                                        if (isRejected) {
                                          e.currentTarget.style.boxShadow = "0 6px 20px rgba(220, 53, 69, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
                                        } else {
                                          e.currentTarget.style.boxShadow = "0 6px 20px rgba(40, 167, 69, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
                                        }
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (isApproved || isRejected) {
                                        e.currentTarget.style.transform = "scale(1)";
                                        if (isRejected) {
                                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(220, 53, 69, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
                                        } else {
                                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(40, 167, 69, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
                                        }
                                      }
                                    }}
                                  >
                                    {isApproved && (
                                      <i
                                        className="bi bi-check"
                                        style={{
                                          color: "white",
                                          fontSize: "16px",
                                          fontWeight: "bold",
                                        }}
                                      ></i>
                                    )}
                                    {isRejected && (
                                      <i
                                        className="bi bi-x"
                                        style={{
                                          color: "white",
                                          fontSize: "14px",
                                          fontWeight: "bold",
                                        }}
                                      ></i>
                                    )}
                                  </div>
                                  </div>

                                  {/* Designation */}
                                  <div
                                    className="journey-step-designation"
                                    style={{
                                      fontSize: "10px",
                                      fontWeight: 600,
                                      color:
                                        isApproved || isRejected
                                          ? "#495057"
                                          : "#adb5bd",
                                      textAlign: "center",
                                      marginBottom: "2px",
                                      lineHeight: "1.2",
                                    }}
                                  >
                                    {safeChild(step?.designation)}
                                  </div>

                                  {/* Name */}
                                  {safeChild(step?.name) ? (
                                    <div
                                      className="journey-step-name"
                                      style={{
                                        fontSize: "10px",
                                        color:
                                          isApproved || isRejected
                                            ? "#6c757d"
                                            : "#ced4da",
                                        textAlign: "center",
                                        marginBottom: "2px",
                                        lineHeight: "1.2",
                                      }}
                                    >
                                      {safeChild(step?.name)}
                                    </div>
                                  ) : null}
                                </div>

                                {/* Connector Line */}
                                {index < srData.journeyStatus.length - 1 && (
                                  <div
                                    style={{
                                      flex: "0 0 80px",
                                      position: "relative",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      minWidth: "80px",
                                      maxWidth: "80px",
                                    }}
                                  >
                                    {/* TAT (Days): always show ConfigTATDays */}
                                    {srData.journeyStatus[index + 1] && (
                                      (() => {
                                        const nextStep = srData.journeyStatus[index + 1];
                                        const dayCountColor = getDayCountColor(step.designation, nextStep.designation, step, nextStep);
                                        
                                        return (
                                          <div
                                            className="journey-day-count"
                                            style={{
                                              fontSize: "9px",
                                              color: "#ffffff",
                                              fontWeight: 700,
                                              marginBottom: "4px",
                                              textAlign: "center",
                                              zIndex: 1,
                                              background: dayCountColor === "#007bff"
                                                ? "linear-gradient(135deg, #007bff 0%, #0056b3 100%)"
                                                : dayCountColor === "#dc3545"
                                                ? "linear-gradient(135deg, #dc3545 0%, #c82333 100%)"
                                                : isApproved
                                                ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                                                : "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                                              padding: "3px 6px",
                                              borderRadius: "6px",
                                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                                              minWidth: "25px",
                                              display: "inline-block",
                                              transition: "all 0.3s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.transform = "scale(1.1)";
                                              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.transform = "scale(1)";
                                              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
                                            }}
                                          >
                                            {getConfigTatLabel(step, nextStep)}
                                          </div>
                                        );
                                      })()
                                    )}
                                    {/* Connector Line */}
                                    <div
                                      className={`journey-connector-line ${
                                        isApproved
                                          ? "journey-connector-completed"
                                          : "journey-connector-pending"
                                      }`}
                                    ></div>
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                          </div>
                        </div>
                        </>
                      );
                    })()}
                </div>

                {/* SR Journey Status Details - Collapsible (Grid like Product Details) */}
                {srData.journeyStatus && srData.journeyStatus.length > 0 && (
                  <div className="viewDetwrpr mb-3 contPdTop">
                    <div className="viewDetCardPnlWrpr">
                      <div className="card-panel journey-status-details-panel" style={{ margin: "30px 0 0 0", backgroundColor: "#eee" }}>
                        <div
                          className="section-heading d-flex justify-content-between align-items-center"
                          style={{ cursor: "pointer", backgroundColor: "#eee", color: "#000" }}
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseJourneyStatusDetails"
                          aria-expanded="false"
                          aria-controls="collapseJourneyStatusDetails"
                        >
                          <span>SR Journey Status Details</span>
                          <i className="bi bi-chevron-down" style={{ color: "#000" }}></i>
                        </div>
                      </div>
                    </div>
                    <div
                      className="collapse viewDetCardPnlOutr"
                      id="collapseJourneyStatusDetails"
                    >
                      {/* Ensure DataTables scroll header matches body width on View Approver Service Request page */}
                      <style>
                        {`
                          #collapseJourneyStatusDetails .dt-scroll-head .dt-scroll-headInner {
                            width: 100% !important;
                            min-width: 100% !important;
                          }
                          #collapseJourneyStatusDetails .dt-scroll-head .dt-scroll-headInner table {
                            width: 100% !important;
                            min-width: 100% !important;
                          }
                        `}
                      </style>
                      <div className="viewDetCardPnlWrpr">
                        <div className="card-panel" style={{ paddingTop: 0 }}>
                          <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                            <div className="table-responsive">
                              <table
                                ref={journeyStatusTableRef}
                                className="table table-striped table-bordered complaint-table"
                                style={{ width: "100%" }}
                              >
                                <thead>
                                  <tr>
                                    <th style={{ display: "none" }}></th>
                                    <th className="text-start">Role</th>
                                    <th className="text-start">Action Taken By</th>
                                    <th className="text-center">Date Of Action</th>
                                    <th className="text-center">Status</th>
                                    <th className="text-center">SLA</th>
                                    <th className="text-center">TAT</th>
                                    <th className="text-center">Actual TAT</th>
                                    <th className="text-center">Queries</th>
                                    <th className="text-end">SR QTY</th>
                                    <th className="text-end">SR VALUE</th>
                                    <th className="text-end">Approved Qty</th>
                                    <th className="text-end">Cal. Approved Value</th>
                                    <th className="text-start">Comments</th>
                                  </tr>
                                </thead>
                                <tbody></tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>

        {/* Invoice Details - Collapsible */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapseInvoiceDetails"
                aria-expanded="false"
                aria-controls="collapseInvoiceDetails"
              >
                <span>Invoice Details</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div
            className="collapse viewDetCardPnlOutr"
            id="collapseInvoiceDetails"
          >
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 0 }}>
                <div className="p-3">
                  <div className="row g-3">
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Invoice Number</div>
                        <div className="detail-value">
                          {srData.invoiceNo || 
                           srData.invoiceNumber || 
                           srData.InvoiceNumber || 
                           "N/A"}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Internal Reference No.</div>
                        <div className="detail-value">
                          {srData.internalReferenceNo ||
                           srData.InternalReferenceNo ||
                           "N/A"}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">SAP Order</div>
                        <div className="detail-value">
                          {srData.orderNo || 

                           "N/A"}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">ODN Number</div>
                        <div className="detail-value">
                          {srData.odnNumber || 
                           srData.ODNNumber || 
                           "N/A"}
                        </div>
                      </div>
                    </div>

                    {/* Invoice Copy Upload - View Only */}
                    <div className="col-12 mt-3">
                      <div className="upload-card">
                        {srData.invoiceFiles && srData.invoiceFiles.length > 0 ? (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(4, 1fr)",
                              gap: "15px",
                              marginTop: "20px",
                            }}
                          >
                            {srData.invoiceFiles.map((fileItem, index) => (
                              <div
                                key={fileItem.id || index}
                                style={{
                                  position: "relative",
                                  border: "1px solid #ddd",
                                  borderRadius: "8px",
                                  padding: "10px",
                                  backgroundColor: "#f9f9f9",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: "150px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginBottom: "8px",
                                    backgroundColor: "white",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                    cursor: fileItem.url ? "pointer" : "default",
                                  }}
                                  onClick={() => {
                                    if (fileItem.url) {
                                      window.open(fileItem.url, "_blank");
                                    }
                                  }}
                                >
                                  {fileItem.preview || fileItem.url ? (
                                    <img
                                      src={fileItem.preview || fileItem.url}
                                      alt={fileItem.name || `Invoice ${index + 1}`}
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain",
                                      }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        textAlign: "center",
                                        color: "#666",
                                      }}
                                    >
                                      <svg
                                        width="48"
                                        height="48"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                                          stroke="#666"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M14 2V8H20"
                                          stroke="#666"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          marginTop: "4px",
                                        }}
                                      >
                                        PDF
                        </div>
                      </div>
                                  )}
                    </div>
                                <div
                                  style={{
                                    fontSize: "0.75rem",
                                    color: "#333",
                                    wordBreak: "break-word",
                                    textAlign: "center",
                                    paddingTop: "4px",
                                  }}
                                  title={fileItem.name || `Invoice ${index + 1}`}
                                >
                                  {fileItem.name && fileItem.name.length > 20
                                    ? fileItem.name.substring(0, 20) + "..."
                                    : fileItem.name || `Invoice ${index + 1}`}
                        </div>
                      </div>
                            ))}
                    </div>
                        ) : (
                          <div className="upload-helper" style={{ color: "#666", fontStyle: "italic" }}>
                            No invoice files uploaded
                        </div>
                        )}
                      </div>
                        </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Details - Collapsible */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapseCustomerDetails"
                aria-expanded="false"
                aria-controls="collapseCustomerDetails"
              >
                <span>Customer Details</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div
            className="collapse viewDetCardPnlOutr"
            id="collapseCustomerDetails"
          >
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 0 }}>
                <div className="p-3">
                  <div className="row g-3">
                    {/* Customer Type - View Only (match /raise-complaint look) */}
                    <div className="col-12 mb-4">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          className="detail-label"
                          style={{
                            margin: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#333",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Customer Type <span className="text-danger">*</span>
                        </div>

                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "default",
                            margin: 0,
                            padding: "4px 8px",
                            backgroundColor:
                              (srData.addCustomerType ||
                                srData.AddCustomerType ||
                                srData.customerType) === "B2C"
                                ? "#e3f2fd"
                                : "transparent",
                            borderRadius: "6px",
                            border:
                              (srData.addCustomerType ||
                                srData.AddCustomerType ||
                                srData.customerType) === "B2C"
                                ? "1px solid #0b6bff"
                                : "1px solid #dee2e6",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <input
                            type="radio"
                            disabled
                            checked={
                              (srData.addCustomerType ||
                                srData.AddCustomerType ||
                                srData.customerType) === "B2C"
                            }
                            readOnly
                            style={{
                              marginRight: "8px",
                              width: "14px",
                              height: "14px",
                              accentColor: "#0b6bff",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight:
                                (srData.addCustomerType ||
                                  srData.AddCustomerType ||
                                  srData.customerType) === "B2C"
                                  ? "600"
                                  : "500",
                              color:
                                (srData.addCustomerType ||
                                  srData.AddCustomerType ||
                                  srData.customerType) === "B2C"
                                  ? "#0b6bff"
                                  : "#666",
                            }}
                          >
                            <strong>End Consumer</strong>
                          </span>
                        </label>

                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "default",
                            margin: 0,
                            padding: "4px 8px",
                            backgroundColor:
                              (srData.addCustomerType ||
                                srData.AddCustomerType ||
                                srData.customerType) === "B2B"
                                ? "#e3f2fd"
                                : "transparent",
                            borderRadius: "6px",
                            border:
                              (srData.addCustomerType ||
                                srData.AddCustomerType ||
                                srData.customerType) === "B2B"
                                ? "1px solid #0b6bff"
                                : "1px solid #dee2e6",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <input
                            type="radio"
                            disabled
                            checked={
                              (srData.addCustomerType ||
                                srData.AddCustomerType ||
                                srData.customerType) === "B2B"
                            }
                            readOnly
                            style={{
                              marginRight: "8px",
                              width: "14px",
                              height: "14px",
                              accentColor: "#0b6bff",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight:
                                (srData.addCustomerType ||
                                  srData.AddCustomerType ||
                                  srData.customerType) === "B2B"
                                  ? "600"
                                  : "500",
                              color:
                                (srData.addCustomerType ||
                                  srData.AddCustomerType ||
                                  srData.customerType) === "B2B"
                                  ? "#0b6bff"
                                  : "#666",
                            }}
                          >
                            <strong>Channel Partner</strong>
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Customer Name</div>
                        <div className="detail-value">
                          {srData.customerName || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Customer Contact Person</div>
                        <div className="detail-value">
                          {srData.customerContactPerson || 
                           srData.representativeName || 
                           "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Customer Address</div>
                        <div className="detail-value">
                          {srData.customerAddress || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Customer City</div>
                        <div className="detail-value">
                          {srData.customerCity || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Customer State</div>
                        <div className="detail-value">
                          {srData.customerState || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Customer Postal Code</div>
                        <div className="detail-value">
                          {srData.customerPinCode || 
                           srData.customerPostalCode || 
                           "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Mobile Number</div>
                        <div className="detail-value">
                          {srData.customerMobile || 
                           srData.customerMobileNo || 
                           "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Phone Number</div>
                        <div className="detail-value">
                          {srData.customerPhone || "N/A"}
                        </div>
                      </div>
                    </div>

                    {(srData.addCustomerType ||
                      srData.AddCustomerType ||
                      srData.customerType) === "B2B" && (
                      <div className="col-lg-6 col-md-12">
                        <div className="mb-2">
                          <div className="detail-label">Dealer/Distributor</div>
                          <div className="detail-value">
                            {srData.dealerDistributor ||
                              srData.DealerDistributor ||
                              "N/A"}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* View End Consumer Button */}
                    <div className="col-12 mt-3">
                      <button
                        type="button"
                        className="btn btn-primary endCustBtn"
                        onClick={() => setShowEndCustomer(!showEndCustomer)}
                        style={{
                          background: "linear-gradient(135deg, #2a9dff 0%, #0b6bff 100%)",
                          borderColor: "transparent",
                          color: "#ffffff",
                          boxShadow: "0 8px 16px rgba(11, 107, 255, 0.22)",
                          padding: "9px 18px",
                          borderRadius: "8px",
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.2s ease-in-out",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = "translateY(-1px)";
                          e.target.style.boxShadow = "0 10px 18px rgba(11, 107, 255, 0.28)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "0 8px 16px rgba(11, 107, 255, 0.22)";
                        }}
                      >
                        {showEndCustomer ? (
                          <>
                            <CloseIcon style={{ fontSize: "16px" }} />
                            HIDE END CONSUMER
                          </>
                        ) : (
                          <>
                            <AddCircleOutlineIcon style={{ fontSize: "16px" }} />
                            VIEW END CONSUMER
                          </>
                        )}
                      </button>
                    </div>

                    {/* End Consumer Details Section */}
                    {showEndCustomer && (
                      <div className="col-12 mt-3">
                        <div
                          className="card"
                          style={{
                            border: "1px solid #dee2e6",
                            borderRadius: "8px",
                          }}
                        >
                          <div
                            className="card-header"
                            style={{
                              backgroundColor: "#f8f9fa",
                              padding: "12px 20px",
                            }}
                          >
                            <h6
                              className="mb-0"
                              style={{ fontSize: "16px", fontWeight: "600" }}
                            >
                              End Consumer Details
                            </h6>
                          </div>
                          <div
                            className="card-body"
                            style={{ padding: "20px" }}
                          >
                            <div className="row g-3">
                              {/* Helper function to get end consumer field value */}
                              {(() => {
                                const endCustomer = srData?.endCustomer || {};
                                const getName = () =>
                                  srData?.endCustomerName ||
                                  srData?.endConsumerName ||
                                  endCustomer?.name ||
                                  "";
                                const getAddress = () =>
                                  srData?.endCustomerAddress ||
                                  srData?.endConsumerAddress ||
                                  endCustomer?.address ||
                                  "";
                                const getCountry = () =>
                                  srData?.endCustomerCountry ||
                                  srData?.endConsumerCountry ||
                                  endCustomer?.country ||
                                  endCustomer?.countryName ||
                                  "";
                                const getZone = () =>
                                  srData?.endCustomerZone ||
                                  srData?.endConsumerZone ||
                                  endCustomer?.zone ||
                                  "";
                                const getProvince = () =>
                                  srData?.endCustomerProvince ||
                                  srData?.endConsumerProvince ||
                                  endCustomer?.province ||
                                  "";
                                const getZoneProvince = () => {
                                  const zone = getZone();
                                  const province = getProvince();
                                  if (zone && province) return `${zone} / ${province}`;
                                  return zone || province || "";
                                };
                                const getState = () =>
                                  srData?.endCustomerState ||
                                  srData?.endConsumerState ||
                                  endCustomer?.state ||
                                  "";
                                const getCity = () =>
                                  srData?.endCustomerCity ||
                                  srData?.endConsumerCity ||
                                  endCustomer?.city ||
                                  "";
                                const getStateCity = () => {
                                  const state = getState();
                                  const city = getCity();
                                  if (state && city) return `${state} / ${city}`;
                                  return state || city || "";
                                };
                                const getPinCode = () =>
                                  srData?.endCustomerPinCode ||
                                  srData?.endConsumerPinCode ||
                                  srData?.endCustomerPostalCode ||
                                  srData?.endConsumerPostalCode ||
                                  endCustomer?.pinCode ||
                                  endCustomer?.postalCode ||
                                  "";
                                const getMobileNumber = () =>
                                  srData?.endCustomerMobileNumber ||
                                  srData?.endConsumerMobileNumber ||
                                  srData?.endCustomerContactNumber ||
                                  srData?.endConsumerContactNumber ||
                                  endCustomer?.mobileNumber ||
                                  endCustomer?.contactNumber ||
                                  "";

                                const consumerName = getName();
                                const consumerAddress = getAddress();
                                const country = getCountry();
                                const zoneProvince = getZoneProvince();
                                const stateCity = getStateCity();
                                const postalCode = getPinCode();
                                const contactNumber = getMobileNumber();

                                return (
                                  <>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                        Consumer Name
                                        </div>
                                        <div className="detail-value">
                                          {consumerName || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                        Consumer Address
                                        </div>
                                        <div className="detail-value">
                                          {consumerAddress || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">Country</div>
                                        <div className="detail-value">
                                          {country || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                        Zone/Province
                                        </div>
                                        <div className="detail-value">
                                          {zoneProvince || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                        State/City
                                        </div>
                                        <div className="detail-value">
                                          {stateCity || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                        Postal Code
                                        </div>
                                        <div className="detail-value">
                                          {postalCode || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                        Contact Number
                                    </div>
                                        <div className="detail-value">
                                          {contactNumber || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                          Project/Site Details
                                        </div>
                                        <div className="detail-value">
                                          {srData.projectSiteDetails ||
                                            srData.ProjectSiteDetails ||
                                            "N/A"}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Secondary Invoice Upload - View Only */}
                                    <div className="col-12 mt-3">
                                      <div className="upload-card">
                                        {srData.secondaryInvoiceFiles && srData.secondaryInvoiceFiles.length > 0 ? (
                                          <div
                                  style={{
                                              display: "grid",
                                              gridTemplateColumns: "repeat(4, 1fr)",
                                              gap: "15px",
                                              marginTop: "20px",
                                            }}
                                          >
                                            {srData.secondaryInvoiceFiles.map((fileItem, index) => (
                                              <div
                                                key={fileItem.id || index}
                                  style={{
                                                  position: "relative",
                                                  border: "1px solid #ddd",
                                                  borderRadius: "8px",
                                                  padding: "10px",
                                                  backgroundColor: "#f9f9f9",
                                                }}
                                              >
                                                <div
                                      style={{
                                                    width: "100%",
                                                    height: "150px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    marginBottom: "8px",
                                                    backgroundColor: "white",
                                                    borderRadius: "4px",
                                        overflow: "hidden",
                                                    cursor: fileItem.url ? "pointer" : "default",
                                                  }}
                                                  onClick={() => {
                                                    if (fileItem.url) {
                                                      window.open(fileItem.url, "_blank");
                                                    }
                                                  }}
                                                >
                                                  {fileItem.preview || fileItem.url ? (
                                                    <img
                                                      src={fileItem.preview || fileItem.url}
                                                      alt={fileItem.name || `Secondary Invoice ${index + 1}`}
                                        style={{
                                                        maxWidth: "100%",
                                                        maxHeight: "100%",
                                                        objectFit: "contain",
                                                      }}
                                                    />
                                                  ) : (
                                      <div
                                        style={{
                                          textAlign: "center",
                                                        color: "#666",
                                                      }}
                                                    >
                                                      <svg
                                                        width="48"
                                                        height="48"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                      >
                                                        <path
                                                          d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                                                          stroke="#666"
                                                          strokeWidth="2"
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                        />
                                                        <path
                                                          d="M14 2V8H20"
                                                          stroke="#666"
                                                          strokeWidth="2"
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                        />
                                                      </svg>
                                                      <div
                                                        style={{
                                                          fontSize: "10px",
                                                          marginTop: "4px",
                                                        }}
                                                      >
                                                        PDF
                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: "0.75rem",
                                                    color: "#333",
                                                    wordBreak: "break-word",
                                                    textAlign: "center",
                                                    paddingTop: "4px",
                                                  }}
                                                  title={fileItem.name || `Secondary Invoice ${index + 1}`}
                                                >
                                                  {fileItem.name && fileItem.name.length > 20
                                                    ? fileItem.name.substring(0, 20) + "..."
                                                    : fileItem.name || `Secondary Invoice ${index + 1}`}
                                                </div>
                                              </div>
                                  ))}
                                </div>
                                        ) : (
                                          <div className="upload-helper" style={{ color: "#666", fontStyle: "italic" }}>
                                            No secondary invoice files uploaded
                              </div>
                                        )}
                                      </div>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Billing Details - Collapsible */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapseCustomerBillingDetails"
                aria-expanded="false"
                aria-controls="collapseCustomerBillingDetails"
              >
                <span>Customer Billing Details</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div
            className="collapse viewDetCardPnlOutr"
            id="collapseCustomerBillingDetails"
          >
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 0 }}>
                <div className="p-3">
                  <div className="row g-3">
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Billing Invoice No.</div>
                        <div className="detail-value">
                          {srData.billingInvoiceNo || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Invoice Date</div>
                        <div className="detail-value">
                          {formatDate(srData.billingInvoiceDate) || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">PO / PI / SO No.</div>
                        <div className="detail-value">
                          {srData.billingPONo || srData.orderNo || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">PO / PI / SO No. Date</div>
                        <div className="detail-value">
                          {formatDate(srData.billingPODate) || formatDate(srData.orderDate) || "N/A"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details - Collapsible */}
        {srData.products && srData.products.length > 0 && (
          <div className="viewDetwrpr mb-3">
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ margin: 0 }}>
                <div
                  className="section-heading d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseProductDetails"
                  aria-expanded="false"
                  aria-controls="collapseProductDetails"
                >
                  <span>Material Section</span>
                  <i className="bi bi-chevron-down"></i>
                </div>
              </div>
            </div>
            <div
              className="collapse viewDetCardPnlOutr"
              id="collapseProductDetails"
            >
              <div className="viewDetCardPnlWrpr contPdTop">
                <div className="card-panel" style={{ paddingTop: 0 }}>
                  <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                    <div className="table-responsive">
                      <table
                        ref={productTableRef}
                        className="table table-striped table-bordered complaint-table"
                        style={{ width: "100%" }}
                      >
                        <thead>
                          <tr>
                            <th>SNo.</th>
                            <th>Product Category</th>
                            <th>PlantName</th>
                            <th>Item Description</th>
                            <th>Product Details</th>
                            <th>Invoice Quantity</th>
                            <th>UOM</th>
                            <th>Invoice Unit Price</th>
                            <th>Invoice value Total</th>
                            <th>Total Price</th>
                            <th style={{ display: showMaterialComponentDetailsColumn ? undefined : "none" }}>
                              Component Details
                            </th>
                            <th>SR Category</th>
                            <th>Comp Qty</th>
                            <th>Comp Value</th>
                          </tr>
                        </thead>
                        <tbody></tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* Material summary below grid */}
                  <div
                    className="mt-3 p-3"
                    style={{
                      borderTop: "1px solid #dee2e6",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "0 0 8px 8px",
                    }}
                  >
                    {(() => {
                      // Determine if it's Export based on MarketCode/MarketName (same logic as used elsewhere in the file)
                      const marketCode = srData?.MarketCode || srData?.marketCode || "";
                      const marketName = srData?.MarketName || srData?.marketName || "";
                      let isExport = false;
                      
                      if (marketCode || marketName) {
                        const codeUpper = marketCode.toUpperCase();
                        const nameUpper = marketName.toUpperCase();
                        
                        if (
                          codeUpper === "M002" ||
                          codeUpper.includes("EXP") ||
                          nameUpper.includes("EXPORT") ||
                          codeUpper === "EXPORT" ||
                          nameUpper === "EXPORT"
                        ) {
                          isExport = true;
                        }
                      }
                      const summaryLabelStyle = {
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#343a40",
                        marginBottom: "6px",
                        letterSpacing: "0.2px",
                      };
                      const summaryValueStyle = {
                        fontWeight: 700,
                        fontSize: "18px",
                        color: "#111827",
                        lineHeight: 1.25,
                      };
                      const summaryTileStyle = {
                        backgroundColor: "#ffffff",
                        border: "1px solid #dee2e6",
                        borderRadius: "8px",
                        padding: "12px 14px",
                        height: "100%",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                      };
                      return (
                        <div className="row g-3">
                          {isExport && (
                            <div className="col-md-3">
                              <div style={summaryTileStyle}>
                                <div className="detail-label" style={summaryLabelStyle}>Conversion Rate</div>
                                <div className="detail-value" style={summaryValueStyle}>
                                  {(() => {
                                    // Get conversion rate from first product or use default
                                    const products = srData?.products || [];
                                    if (products.length > 0) {
                                      const conversionRate = products[0]?.ConversionRate || 
                                                           products[0]?.conversionRate || 
                                                           products[0]?.Conversion_Rate ||
                                                           "EUR to USD - 1.0352";
                                      return conversionRate;
                                    }
                                    return "EUR to USD - 1.0352";
                                  })()}
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="col-md-3">
                            <div style={summaryTileStyle}>
                              <div className="detail-label" style={summaryLabelStyle}>SR Value Total</div>
                              <div className="detail-value" style={summaryValueStyle}>
                                10,250.00
                              </div>
                            </div>
                          </div>
                          {isExport && (
                            <div className="col-md-3">
                              <div style={summaryTileStyle}>
                                <div className="detail-label" style={summaryLabelStyle}>SR Value Total (INR)</div>
                                <div className="detail-value" style={summaryValueStyle}>
                                  8,52,325.00
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="col-md-3">
                            <div style={summaryTileStyle}>
                              <div className="detail-label" style={summaryLabelStyle}>Cal. Approved Value</div>
                              <div className="detail-value" style={summaryValueStyle}>
                                9,850.00
                              </div>
                            </div>
                          </div>
                          {isExport && (
                            <div className="col-md-3">
                              <div style={summaryTileStyle}>
                                <div className="detail-label" style={summaryLabelStyle}>Cal. Approved Value (INR)</div>
                                <div className="detail-value" style={summaryValueStyle}>
                                  8,19,225.00
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sample Information - Collapsible */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapseSampleInformation"
                aria-expanded="false"
                aria-controls="collapseSampleInformation"
              >
                <span>Sample Information</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div
            className="collapse viewDetCardPnlOutr"
            id="collapseSampleInformation"
          >
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 0 }}>
                <div className="p-3">
                  <div className="row g-3">
                    <div className="col-lg-3 col-md-4 col-sm-6">
                      <div className="mb-2">
                        <div className="detail-label">Sample Sent</div>
                        <div className="detail-value">
                          {srData.sampleInfo?.sampleSent || "No"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {srData.sampleInfo?.sampleSent === "Yes" && (
                    <div className="row g-3 mt-1">
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">Courier Agency</div>
                          <div className="detail-value">
                            {srData.sampleInfo?.courierAgency || "N/A"}
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">Courier Date</div>
                          <div className="detail-value">
                            {formatDate(srData.sampleInfo?.courierDate) || "N/A"}
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">Docket No.</div>
                          <div className="detail-value">
                            {srData.sampleInfo?.docketNo || "N/A"}
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">Sent To</div>
                          <div className="detail-value">
                            {srData.sampleInfo?.sentTo || "N/A"}
                          </div>
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="mb-2">
                          <div className="detail-label">Sample Details</div>
                          <div className="detail-value" style={{ whiteSpace: "pre-wrap" }}>
                            {srData.sampleInfo?.sampleDetails || "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Checklist Information - Collapsible */}
        {(srData.ppbChecklist || srData.prelamChecklist) && (
          <div className="viewDetwrpr mb-3">
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ margin: 0 }}>
                <div
                  className="section-heading d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseChecklistInfo"
                  aria-expanded="false"
                  aria-controls="collapseChecklistInfo"
                >
                  <span>Checklist Responses</span>
                  <i className="bi bi-chevron-down"></i>
                </div>
              </div>
            </div>
            <div
              className="collapse viewDetCardPnlOutr"
              id="collapseChecklistInfo"
            >
              <div className="viewDetCardPnlWrpr">
                <div className="card-panel" style={{ paddingTop: 15 }}>
                  {/* Plain Particle Board - Nested Collapsible */}
                  {srData.ppbChecklist && (
                    <div className="mb-3">
                      <div
                        className="section-heading d-flex justify-content-between align-items-center"
                        style={{
                          cursor: "pointer",
                          backgroundColor: "#eee",
                          padding: "12px 16px",
                          borderRadius: "4px",
                        }}
                        data-bs-toggle="collapse"
                        data-bs-target="#collapsePPB"
                        aria-expanded="false"
                        aria-controls="collapsePPB"
                      >
                        <span
                          className="innerColHdng"
                          style={{ fontSize: "16px", fontWeight: 500 }}
                        >
                          Plain Particle Board
                        </span>
                        <i className="bi bi-chevron-down innerColArrow"></i>
                      </div>
                      <div className="collapse mt-2" id="collapsePPB">
                        <div
                          className="p-3"
                          style={{ backgroundColor: "#fafbfc" }}
                        >
                          <div className="row">
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Printed Information About Brand
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.printedInfo || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Variant - Product Grade
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.variantProductGrade ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">Thickness</div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.thickness || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">Size</div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.size || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">Emission</div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.emission || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">Batch No.</div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.batchNo || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Date of Manufacturing (MFD)
                                </div>
                                <div className="detail-value">
                                  {formatDate(srData.ppbChecklist.mfdDate) ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-8">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Nature of Defect
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.natureOfDefect || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Material Used for Furniture/Interior
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist
                                    .materialUsedForFurniture || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Defective Size
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.defectiveSize || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Defective Quantity
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.defectiveQty || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Verified With Device
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.verifiedWithDevice ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Device Details
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.deviceDetails || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Right Fabrication Tools Used
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.rightFabricationTools ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Material Stacked
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.materialStacked || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Contact With Wall/Floor
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.contactWithWall || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  SR Noticed At
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.complaintNoticedAt ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Liability Expected
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.liabilityExpected ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Defects Noted On LR
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.defectsNotedOnLR ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Status of Complainer
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.statusOfComplainer ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Other Comments
                                </div>
                                <div className="detail-value">
                                  {srData.ppbChecklist.otherComments || "N/A"}
                                </div>
                              </div>
                            </div>

                            {/* Upload images of damage panel */}
                            <div className="col-12 mt-4">
                              <div
                                className="detail-label mb-3"
                                style={{ fontSize: "16px", fontWeight: "600" }}
                              >
                                Uploaded images of damage
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(auto-fill, minmax(200px, 1fr))",
                                  gap: "20px",
                                  padding: "10px 0",
                                }}
                              >
                                {[
                                  "https://via.placeholder.com/800x600?text=Damage+Image+1",
                                  "https://via.placeholder.com/800x600?text=Damage+Image+2",
                                  "https://via.placeholder.com/800x600?text=Damage+Image+3",
                                ].map((imageUrl, index) => (
                                  <a
                                    key={index}
                                    href={imageUrl}
                                    data-lightbox="ppb-damage"
                                    data-title={`Plain Particle Board - Damage Image ${
                                      index + 1
                                    }`}
                                    style={{
                                      position: "relative",
                                      display: "block",
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                      transition:
                                        "transform 0.2s, box-shadow 0.2s",
                                      backgroundColor: "#f8f9fa",
                                      textDecoration: "none",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform =
                                        "scale(1.05)";
                                      e.currentTarget.style.boxShadow =
                                        "0 4px 16px rgba(0,0,0,0.2)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform =
                                        "scale(1)";
                                      e.currentTarget.style.boxShadow =
                                        "0 2px 8px rgba(0,0,0,0.1)";
                                    }}
                                  >
                                    <img
                                      src={imageUrl}
                                      alt={`Damage ${index + 1}`}
                                      style={{
                                        width: "100%",
                                        height: "200px",
                                        objectFit: "cover",
                                        display: "block",
                                      }}
                                    />
                                    <div
                                      style={{
                                        position: "absolute",
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        padding: "8px",
                                        backgroundColor: "rgba(0,0,0,0.7)",
                                        color: "#fff",
                                        fontSize: "14px",
                                        textAlign: "center",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Image {index + 1}
                                    </div>
                                  </a>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Prelam - Nested Collapsible */}
                  {srData.prelamChecklist && (
                    <div className="mb-3">
                      <div
                        className="section-heading d-flex justify-content-between align-items-center"
                        style={{
                          cursor: "pointer",
                          backgroundColor: "#eee",
                          padding: "12px 16px",
                          borderRadius: "4px",
                        }}
                        data-bs-toggle="collapse"
                        data-bs-target="#collapsePrelam"
                        aria-expanded="false"
                        aria-controls="collapsePrelam"
                      >
                        <span
                          className="innerColHdng"
                          style={{ fontSize: "16px", fontWeight: 500 }}
                        >
                          Prelam
                        </span>
                        <i className="bi bi-chevron-down innerColArrow"></i>
                      </div>
                      <div className="collapse mt-2" id="collapsePrelam">
                        <div
                          className="p-3"
                          style={{ backgroundColor: "#fafbfc" }}
                        >
                          <div className="row">
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Printed Information About Brand
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.printedInfo || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Variant - Design Category
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.designCategory ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Variant - Product Grade
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.productGrade || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="mb-2">
                                <div className="detail-label">Thickness</div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.thickness || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="mb-2">
                                <div className="detail-label">Size</div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.size || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="mb-2">
                                <div className="detail-label">Design No.</div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.designNo || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="mb-2">
                                <div className="detail-label">Finish</div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.finish || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="mb-2">
                                <div className="detail-label">Emission</div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.emission || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="mb-2">
                                <div className="detail-label">Batch No.</div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.batchNo || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Date of Manufacturing (MFD)
                                </div>
                                <div className="detail-value">
                                  {formatDate(srData.prelamChecklist.mfdDate) ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Nature of Defect
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.natureOfDefect ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Used for Interior/Furniture
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.usedForInterior ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Defective Size
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.defectiveSize ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Defective Quantity
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.defectiveQty || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Verified With Device
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.verifiedWithDevice ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Device Details
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.deviceDetails ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Right Tools Used
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.rightTools || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Stacked Orientation
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.stackedOrientation ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Stored in Contact With
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.storedInContact ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  SR Noticed At
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.complaintNoticedAt ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Others (Specify)
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.othersSpecify ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Complainer Status
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.complainerStatus ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Liability Expected
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.liabilityExpected ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Defects During Unloading
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist
                                    .defectsDuringUnloading || "N/A"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="mb-2">
                                <div className="detail-label">
                                  Other Comments
                                </div>
                                <div className="detail-value">
                                  {srData.prelamChecklist.otherComments ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>

                            {/* Upload images of damage panel */}
                            <div className="col-12 mt-4">
                              <div
                                className="detail-label mb-3"
                                style={{ fontSize: "16px", fontWeight: "600" }}
                              >
                                Uploaded images of damage
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(auto-fill, minmax(200px, 1fr))",
                                  gap: "20px",
                                  padding: "10px 0",
                                }}
                              >
                                {[
                                  "https://via.placeholder.com/800x600?text=Prelam+Damage+1",
                                  "https://via.placeholder.com/800x600?text=Prelam+Damage+2",
                                  "https://via.placeholder.com/800x600?text=Prelam+Damage+3",
                                ].map((imageUrl, index) => (
                                  <a
                                    key={index}
                                    href={imageUrl}
                                    data-lightbox="prelam-damage"
                                    data-title={`Prelam - Damage Image ${
                                      index + 1
                                    }`}
                                    style={{
                                      position: "relative",
                                      display: "block",
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                      transition:
                                        "transform 0.2s, box-shadow 0.2s",
                                      backgroundColor: "#f8f9fa",
                                      textDecoration: "none",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform =
                                        "scale(1.05)";
                                      e.currentTarget.style.boxShadow =
                                        "0 4px 16px rgba(0,0,0,0.2)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform =
                                        "scale(1)";
                                      e.currentTarget.style.boxShadow =
                                        "0 2px 8px rgba(0,0,0,0.1)";
                                    }}
                                  >
                                    <img
                                      src={imageUrl}
                                      alt={`Prelam Damage ${index + 1}`}
                                      style={{
                                        width: "100%",
                                        height: "200px",
                                        objectFit: "cover",
                                        display: "block",
                                      }}
                                    />
                                    <div
                                      style={{
                                        position: "absolute",
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        padding: "8px",
                                        backgroundColor: "rgba(0,0,0,0.7)",
                                        color: "#fff",
                                        fontSize: "14px",
                                        textAlign: "center",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Image {index + 1}
                                    </div>
                                  </a>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Previous SRs - collapsible */}
        {previousComplaintsCount > 0 && (
          <div className="viewDetwrpr mb-3">
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ margin: 0 }}>
                <div
                  className="section-heading d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  data-bs-toggle="collapse"
                  data-bs-target="#collapsePreviousComplaints"
                  aria-expanded="true"
                  aria-controls="collapsePreviousComplaints"
                >
                  <span>Previous SRs</span>
                  <i className="bi bi-chevron-down"></i>
                </div>
              </div>
            </div>
            <div
              className="collapse show viewDetCardPnlOutr"
              id="collapsePreviousComplaints"
            >
              <div className="viewDetCardPnlWrpr">
                <div className="card-panel" style={{ paddingTop: 15 }}>
                  <div className="section-heading previous-complaints-heading" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
                    <span style={{ 
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      fontWeight: 600,
                      fontSize: "15px"
                    }}>
                      {previousComplaintsCount == 1
                        ? "Previous SR(s) exist for this invoice. Please review before proceeding"
                        : `Multiple previous SRs exist for this invoice (Total: ${previousComplaintsCount}). Please review before proceeding`}
                    </span>
                    {previousComplaintsCount > 1 && (
                      <button
                        type="button"
                        onClick={() => setShowPreviousComplaintsTable(!showPreviousComplaintsTable)}
                        style={{
                          background: showPreviousComplaintsTable 
                            ? "linear-gradient(135deg, #4caf50 0%, #45a049 100%)"
                            : "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                          border: "none",
                          color: "white",
                          fontWeight: 500,
                          fontSize: "14px",
                          borderRadius: "4px",
                          padding: "6px 16px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.3s ease",
                          boxShadow: "0 2px 8px rgba(0, 123, 255, 0.3)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 123, 255, 0.5)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 123, 255, 0.3)";
                        }}
                      >
                        <TableChartIcon fontSize="small" />
                        {showPreviousComplaintsTable ? "Hide SRs" : "View All SRs"}
                      </button>
                    )}
                  </div>
                  <div 
                    style={{ 
                      maxWidth: "100%", 
                      overflowX: "auto",
                      display: previousComplaintsCount === 1 || showPreviousComplaintsTable ? "block" : "none"
                    }}
                  >
                    <div className="table-responsive">
                      <table
                        ref={historyTableRef}
                        className="table table-striped table-bordered complaint-table srTablewrpr"
                        style={{ width: "100%" }}
                      >
                        <thead>
                          <tr>
                            <th className="text-center">SL No</th>
                            <th className="text-center">SR No</th>
                            <th className="text-center">SR Date</th>
                            <th className="text-start">Product(s) involved</th>
                            <th className="text-end">SR QTY</th>
                            <th className="text-end">SR VALUE</th>
                            <th className="text-center">
                              Action taken / Resolution status
                            </th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody></tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}



        {/* Service Request Query History - Collapsible */}
        <div className="viewDetwrpr sr-query-history-root mb-3">
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ margin: 0 }}>
                <div
                  className="section-heading sr-query-history-section-toggle d-flex justify-content-between align-items-center"
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseQueryHistory"
                  aria-expanded="false"
                  aria-controls="collapseQueryHistory"
                >
                  <span>Service Request Query History</span>
                  <i className="bi bi-chevron-down"></i>
                </div>
              </div>
            </div>
            <div
              className="collapse viewDetCardPnlOutr"
              id="collapseQueryHistory"
            >
              <div className="viewDetCardPnlWrpr">
                <div className="card-panel" style={{ paddingTop: 15 }}>
                  {queryHistoryFilter ? (
                    <div
                      className="d-flex justify-content-between align-items-center mb-3"
                      style={{
                        background: "#eef5ff",
                        border: "1px solid #cfe2ff",
                        borderRadius: 6,
                        padding: "8px 12px",
                      }}
                    >
                      <span style={{ fontSize: 13 }}>
                        Showing queries raised by{" "}
                        <strong>
                          {queryHistoryFilter.userName ||
                            queryHistoryFilter.userId ||
                            "selected user"}
                        </strong>
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={clearQueryHistoryFilter}
                      >
                        Show all
                      </button>
                    </div>
                  ) : null}
                  {loadingQueries ? (
                    <div className="sr-query-history-loading">
                      <p>Loading queries...</p>
                    </div>
                  ) : (
                    <div className="sr-query-history-list">
                      {visibleQueryHistory.map((display, index) => {
                        if (!display || typeof display !== "object") return null;
                        const isPending = display.status === "Pending";
                        const isResponded = display.status === "Responded";
                        const canRespond =
                          hasUserType("SE") &&
                          isPending &&
                          (String(display.raisedToUserId || "") ===
                            String(getCurrentUserId() ?? "") ||
                            String(display.raisedToUserId || "") ===
                              String(currentUserIdCode ?? ""));
                        const collapseId = `collapseQuery${display.queryId != null ? display.queryId : index}`;
                        const cardHeaderClass = isPending
                          ? "sr-query-history-card-header sr-query-history-card-header--pending"
                          : isResponded
                          ? "sr-query-history-card-header sr-query-history-card-header--responded"
                          : "sr-query-history-card-header sr-query-history-card-header--neutral";
                        const railNodeClass = isResponded
                          ? "sr-query-history-rail-node sr-query-history-rail-node--ok"
                          : isPending
                          ? "sr-query-history-rail-node sr-query-history-rail-node--pending"
                          : "sr-query-history-rail-node sr-query-history-rail-node--neutral";
                        const badgeMode = isPending
                          ? "sr-query-history-badge sr-query-history-badge--pending"
                          : isResponded
                          ? "sr-query-history-badge sr-query-history-badge--responded"
                          : "sr-query-history-badge sr-query-history-badge--neutral";
                        const respDateShown =
                          safeChild(display?.formattedResponseDate) ||
                          (isPending ? safeChild(display?.formattedQueryDate) : "");
                        const respTimeShown =
                          safeChild(display?.formattedResponseTime) ||
                          (isPending ? safeChild(display?.formattedQueryTime) : "");

                        return (
                          <div
                            key={display.queryId != null ? display.queryId : index}
                            className="sr-query-history-item"
                          >
                            <div className="sr-query-history-rail" aria-hidden="true">
                              {index < visibleQueryHistory.length - 1 ? (
                                <div className="sr-query-history-rail-line" />
                              ) : null}
                              <div className={railNodeClass}>
                                <i
                                  className={isResponded ? "bi bi-check" : "bi bi-question-circle"}
                                  aria-hidden="true"
                                />
                              </div>
                            </div>
                            <div className="sr-query-history-card">
                              <div
                                className={cardHeaderClass}
                                data-bs-toggle="collapse"
                                data-bs-target={`#${collapseId}`}
                                aria-expanded="true"
                                aria-controls={collapseId}
                              >
                              {isPending ? (
                                <div className="sr-query-history-header-main">
                                  <div className="sr-query-history-kicker">QUERY RAISED</div>
                                  <div className="sr-query-history-by-line">
                                    <span className="sr-query-history-meta-label sr-query-history-meta-label--upper">BY:</span>{" "}
                                    <span className="sr-query-history-strong-name">
                                      {safeChild(display?.raisedByName) || "Approver"}
                                      {safeChild(display?.raisedByRoleName)
                                        ? ` (${safeChild(display?.raisedByRoleName)})`
                                        : ""}
                                    </span>
                                    <span className="sr-query-history-sep"> | </span>
                                    <span>
                                      {safeChild(display?.raisedToUserName) || ""}
                                      {safeChild(display?.raisedToRoleName)
                                        ? ` (${safeChild(display?.raisedToRoleName)})`
                                        : ""}
                                    </span>
                                    <span className="sr-query-history-sep"> | </span>
                                    <span className="sr-query-history-date">
                                      {safeChild(display?.formattedQueryDate)}
                                    </span>
                                    <span className="sr-query-history-time">
                                      {" "}
                                      @ {safeChild(display?.formattedQueryTime)}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div className="sr-query-history-header-main">
                                  <span className="sr-query-history-meta-label">
                                    Query raised by:
                                  </span>{" "}
                                  <span className="sr-query-history-strong-name">
                                    {safeChild(display?.raisedByName) || "Approver"}
                                    {safeChild(display?.raisedByRoleName)
                                      ? ` (${safeChild(display?.raisedByRoleName)})`
                                      : ""}
                                  </span>
                                  {safeChild(display?.raisedToUserName) ? (
                                    <>
                                      <span className="sr-query-history-sep"> | </span>
                                      <span className="sr-query-history-meta-muted">
                                        {safeChild(display?.raisedToUserName)}
                                        {safeChild(display?.raisedToRoleName)
                                          ? ` (${safeChild(display?.raisedToRoleName)})`
                                          : ""}
                                      </span>
                                    </>
                                  ) : null}
                                  <span className="sr-query-history-sep"> | </span>
                                  <span className="sr-query-history-date">
                                    {safeChild(display?.formattedQueryDate)}
                                  </span>
                                  <span className="sr-query-history-time">
                                    {" "}
                                    @ {safeChild(display?.formattedQueryTime)}
                                  </span>
                                </div>
                              )}
                              <span className={badgeMode}>
                                {safeChild(display?.status) || "Pending"}
                              </span>
                              <i className="bi bi-chevron-down sr-query-history-chevron" aria-hidden="true" />
                            </div>

                            <div className="collapse show" id={collapseId}>
                              <div className="sr-query-history-card-body">
                                <div className="sr-query-history-field-block sr-query-history-field-block--query">
                                  <label
                                    className="sr-query-history-field-label sr-query-history-field-label--query"
                                  >
                                    Query:
                                    <span className="sr-query-history-field-sub">
                                      Query raised to{" "}
                                      <span className="sr-query-history-field-sub-em">
                                        {safeChild(display?.raisedToUserName) ||
                                          safeChild(display?.raisedToRoleName) ||
                                          "Sales Executive"}
                                      </span>
                                      {safeChild(display?.raisedToRoleName)
                                        ? ` (${safeChild(display?.raisedToRoleName)})`
                                        : ""}
                                    </span>
                                  </label>
                                  <textarea
                                    className="form-control sr-query-history-textarea-query"
                                    value={safeChild(display?.queryText)}
                                    readOnly
                                    disabled
                                    rows={3}
                                  />
                                </div>

                                {safeChild(display?.responseText) ? (
                                  <div className="sr-query-history-field-block sr-query-history-field-block--response">
                                    <label
                                      className="sr-query-history-field-label sr-query-history-field-label--response"
                                    >
                                      Response:
                                      <span className="sr-query-history-field-sub">
                                        Responded by{" "}
                                        <span className="sr-query-history-field-sub-em">
                                          {safeChild(display?.respondedByName) || "N/A"}
                                        </span>
                                        {respDateShown ? (
                                          <>
                                            {" | "}
                                            <span className="sr-query-history-date-inline">
                                              {respDateShown}
                                            </span>
                                            {" @ "}
                                            <span className="sr-query-history-time-inline">
                                              {respTimeShown}
                                            </span>
                                          </>
                                        ) : null}
                                      </span>
                                    </label>
                                    <textarea
                                      className="form-control sr-query-history-textarea-response"
                                      value={safeChild(display?.responseText)}
                                      readOnly
                                      disabled
                                      rows={3}
                                    />
                                  </div>
                                ) : (
                                  <div className="sr-query-history-field-block sr-query-history-field-block--response">
                                    <label
                                      className="sr-query-history-field-label sr-query-history-field-label--response"
                                    >
                                      Response:
                                      <span className="sr-query-history-field-sub">
                                        Responded by{" "}
                                        <span className="sr-query-history-field-sub-em">N/A</span>
                                        {(respDateShown || respTimeShown) ? (
                                          <>
                                            {" | "}
                                            <span className="sr-query-history-date-inline">
                                              {respDateShown || safeChild(display?.formattedQueryDate)}
                                            </span>
                                            {" @ "}
                                            <span className="sr-query-history-time-inline">
                                              {respTimeShown || safeChild(display?.formattedQueryTime)}
                                            </span>
                                          </>
                                        ) : null}
                                      </span>
                                    </label>
                                    <textarea
                                      className="form-control sr-query-history-textarea-response"
                                      value="N/A"
                                      readOnly
                                      disabled
                                      rows={3}
                                    />
                                  </div>
                                )}

                                {canRespond ? null : null}
                              </div>
                            </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Edit modals removed - View only mode for approvers */}
      {/* SR Checklist Modal removed */}
      <div
        className={`modal fade checklist-modal ${checklistOpen ? "show" : ""}`}
        id="checklistModal"
        ref={checklistModalRef}
        tabIndex="-1"
        aria-labelledby="checklistModalLabel"
        aria-hidden={!checklistOpen}
        style={{
          display: checklistOpen ? "block" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content checklist-modal-content">
            <div className="modal-header checklist-modal-header">
              <h5
                className="modal-title checklist-modal-title"
                id="checklistModalLabel"
              >
                SR Checklist
              </h5>
              <button
                type="button"
                className="btn-close checklist-modal-close"
                aria-label="Close"
                onClick={handleChecklistClose}
              ></button>
            </div>
            <div className="modal-body checklist-modal-body">
              {/* Product Information Section */}
              <div className="row g-3 mb-4">
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label checklist-form-label">
                    Item Category
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control"
                    value={checklistData.category}
                    readOnly
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label checklist-form-label">
                    Item Name
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control"
                    value={checklistData.productName}
                    readOnly
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label checklist-form-label">
                    Item Code
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control"
                    value={checklistData.productCode}
                    readOnly
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label checklist-form-label">
                    SR Qty
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control text-end"
                    value={checklistData.quantity}
                    readOnly
                  />
                </div>
              </div>

              {/* SR Lines Section */}
              <div className="checklist-sr-lines-section">
                <div className="checklist-sr-lines-content mt-3">
                  <div className="row g-3">
                    {complaintLines.map((line) => (
                      <React.Fragment key={line.id}>
                        <div className="col-12 col-sm-6 col-md-5">
                          <label className="form-label checklist-form-label">
                            SR Category
                          </label>
                          <select
                            className="form-select checklist-form-control"
                            value={line.type}
                            onChange={(e) =>
                              updateComplaintLine(
                                line.id,
                                "type",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select SR Category</option>
                            {loadingComplaintTypes ? (
                              <option value="" disabled>
                                Loading...
                              </option>
                            ) : complaintTypes.length === 0 ? (
                              <option value="" disabled>
                                No complaint types available
                              </option>
                            ) : (
                              complaintTypes.map((type, index) => {
                                const typeId =
                                  type.ComplaintTypeId ||
                                  type.complaintTypeId ||
                                  type.id ||
                                  index;
                                const typeName =
                                  type.ComplaintTypeName ||
                                  type.complaintTypeName ||
                                  type.name ||
                                  `Type ${index + 1}`;
                                return (
                                  <option key={typeId} value={String(typeId)}>
                                    {typeName}
                                  </option>
                                );
                              })
                            )}
                          </select>
                        </div>
                        <div className="col-12 col-sm-4 col-md-3">
                          <label className="form-label checklist-form-label">
                            Qty
                          </label>
                          <input
                            type="number"
                            className="form-control checklist-form-control text-end"
                            min="0"
                            value={line.qty}
                            onChange={(e) =>
                              updateComplaintLine(
                                line.id,
                                "qty",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="col-12 col-sm-12 col-md-2 d-flex gap-2 align-items-end justify-content-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-icon-checklist btn-add-checklist"
                            onClick={addComplaintLine}
                            title="Add Line"
                          >
                            <AddCircleOutlineIcon fontSize="medium" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-icon-checklist btn-remove-checklist"
                            onClick={() => removeComplaintLine(line.id)}
                            disabled={complaintLines.length === 1}
                            title="Remove Line"
                          >
                            <DeleteOutlineIcon fontSize="medium" />
                          </button>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer checklist-modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary checklist-btn-close"
                onClick={handleChecklistClose}
              >
                <CloseIcon fontSize="small" className="me-1" />
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Raise Query Modal - Bootstrap 5 */}
      {raiseQueryOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={raiseQuerySubmitting ? undefined : handleRaiseQueryClose}
          style={{ zIndex: 1040 }}
        ></div>
      )}
      <div
        className={`modal fade raise-query-modal ${
          raiseQueryOpen ? "show" : ""
        }`}
        id="raiseQueryModal"
        ref={raiseQueryModalRef}
        tabIndex="-1"
        aria-labelledby="raiseQueryModalLabel"
        aria-hidden={!raiseQueryOpen}
        style={{
          display: raiseQueryOpen ? "block" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div
            className="modal-content"
            style={{
              position: "relative",
              borderRadius: "12px",
              border: "none",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
            }}
          >
            {raiseQuerySubmitting && (
              <div
                role="status"
                aria-live="polite"
                aria-busy="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.88)",
                  zIndex: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "12px",
                  gap: "14px",
                }}
              >
                <div
                  className="spinner-border text-success"
                  style={{ width: "2.75rem", height: "2.75rem" }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: 600,
                  }}
                >
                  Submitting…
                </span>
              </div>
            )}
            {/* Modal Header */}
            <div
              className="modal-header"
              style={{
                borderBottom: "2px solid #f0f0f0",
                padding: "20px 24px",
                backgroundColor: "#fafafa",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <h5
                className="modal-title"
                id="raiseQueryModalLabel"
                style={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#1f1f1f",
                  margin: 0,
                }}
              >
                Raise Query
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => !raiseQuerySubmitting && handleRaiseQueryClose()}
                disabled={raiseQuerySubmitting}
                style={{
                  fontSize: "14px",
                  opacity: raiseQuerySubmitting ? 0.35 : 0.6,
                  pointerEvents: raiseQuerySubmitting ? "none" : "auto",
                }}
              ></button>
            </div>

            {/* Modal Body */}
            <div
              className="modal-body"
              style={{
                padding: "24px",
              }}
            >
              <style>
                {`
                  #raiseQueryModal .raise-query-select2-container-left .select2-selection__rendered {
                    text-align: left !important;
                    padding-left: 12px !important;
                  }
                  #raiseQueryModal .raise-query-select2-container-left .select2-selection__placeholder {
                    text-align: left !important;
                  }
                  #raiseQueryModal .select2-container--default .select2-selection--single .select2-selection__rendered {
                    text-align: left !important;
                    padding-left: 12px !important;
                  }
                  #raiseQueryModal .raise-query-select2-dropdown-left .select2-search__field,
                  #raiseQueryModal .raise-query-select2-dropdown-left .select2-results__option {
                    text-align: left !important;
                  }
                `}
              </style>
              {/* Raise Query For Section */}
              <div className="row" style={{ marginBottom: "24px" }}>
                <div className="col-12 col-md-8">
                  <label
                    className="form-label"
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#333",
                      marginBottom: "10px",
                      display: "block",
                      textAlign: "left",
                    }}
                  >
                    Raise Query for
                  </label>
                  <select
                    id="raiseQuerySelect"
                    className="form-select raise-query-select"
                    defaultValue={raiseQueryData.name}
                    disabled={raiseQuerySubmitting}
                    style={{
                      fontSize: "14px",
                      padding: "12px 16px",
                      border: "1px solid #d0d0d0",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      transition: "all 0.2s",
                      width: "100%",
                    }}
                  >
                    <option value="">Search for name...</option>
                    {nameOptions.map((option, index) => (
                      <option
                        key={option.id || index}
                        value={option.id || option.value || option.name}
                      >
                        {option?.name
                          ? option.name
                          : option?.label || option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Query Text Section */}
              <div>
                <label
                  className="form-label"
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#333",
                    marginBottom: "10px",
                    display: "block",
                    textAlign: "left",
                  }}
                >
                  Type your query <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <textarea
                  className="form-control"
                  rows="5"
                  value={raiseQueryData.query}
                  disabled={raiseQuerySubmitting}
                  onChange={(e) => {
                    setRaiseQueryData((prev) => ({
                      ...prev,
                      query: e.target.value,
                    }));
                  }}
                  placeholder="Enter your query here..."
                  style={{
                    resize: "vertical",
                    fontSize: "14px",
                    padding: "12px 16px",
                    border: "1px solid #d0d0d0",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    transition: "all 0.2s",
                    lineHeight: "1.5",
                    minHeight: "120px",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#007bff";
                    e.target.style.boxShadow =
                      "0 0 0 0.2rem rgba(0, 123, 255, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d0d0d0";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className="modal-footer"
              style={{
                borderTop: "2px solid #f0f0f0",
                padding: "16px 24px",
                backgroundColor: "#fafafa",
                borderRadius: "0 0 12px 12px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                type="button"
                className="btn"
                onClick={handleRaiseQueryClose}
                disabled={raiseQuerySubmitting}
                style={{
                  background: "linear-gradient(135deg, #e53935 0%, #c62828 100%)",
                  border: "none",
                  color: "#ffffff",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  boxShadow: "0 4px 15px rgba(229, 57, 53, 0.4)",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  padding: "8px 24px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: raiseQuerySubmitting ? 0.65 : 1,
                  cursor: raiseQuerySubmitting ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (raiseQuerySubmitting) return;
                  e.currentTarget.style.background = "linear-gradient(135deg, #ef5350 0%, #d32f2f 100%)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(229, 57, 53, 0.6)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #e53935 0%, #c62828 100%)";
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(229, 57, 53, 0.4)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <CancelIcon style={{ fontSize: "16px" }} />
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleRaiseQuerySubmit}
                disabled={raiseQuerySubmitting}
                style={{
                  background: "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  boxShadow: "0 4px 15px rgba(46, 125, 50, 0.4)",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  padding: "8px 24px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: raiseQuerySubmitting ? 0.85 : 1,
                  cursor: raiseQuerySubmitting ? "wait" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (raiseQuerySubmitting) return;
                  e.currentTarget.style.background = "linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(46, 125, 50, 0.6)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)";
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(46, 125, 50, 0.4)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <SendIcon style={{ fontSize: "16px" }} />
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Approve/Reject Modal - Matching Previous SRs Design */}
      {approveRejectOpen && (
        <div
          className="approve-reject-modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            overflow: "auto",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleApproveRejectClose();
            }
          }}
        >
          <div
            className="approve-reject-modal-container"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              width: "95%",
              maxWidth: "1400px",
              maxHeight: "90vh",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Modal Header */}
            <div
              className="approve-reject-modal-header"
              style={{
                padding: "20px",
                borderBottom: "1px solid #dee2e6",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px 8px 0 0",
                flexShrink: 0,
              }}
            >
              <h3 className="approve-reject-modal-title" style={{ 
                margin: 0, 
                color: "#333", 
                fontWeight: 600,
              }}>
                Approve / Reject Service Request
              </h3>
              <button
                type="button"
                onClick={handleApproveRejectClose}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6c757d",
                  padding: "0",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Modal Body */}
            <div
              className="approve-reject-modal-body"
              style={{
                padding: "20px",
                overflowY: "auto",
                overflowX: "hidden",
                flex: "1 1 auto",
                minHeight: 0,
                WebkitOverflowScrolling: "touch",
              }}
            >
              {/* Items Table Section */}
              <div style={{ marginBottom: "24px" }}>
                <div className="table-responsive" style={{ maxHeight: "500px", overflowX: "auto", overflowY: "auto" }}>
                  <table
                    className="table table-bordered"
                    style={{
                      width: "100%",
                      marginBottom: 0,
                      borderCollapse: "separate",
                      borderSpacing: 0,
                      fontSize: "13px",
                    }}
                  >
                    <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                      <tr>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "center", minWidth: "60px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>SNo.</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "left", minWidth: "140px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Product Category</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "left", minWidth: "120px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Plant Name</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "left", minWidth: "180px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Item Description</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "110px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Invoice Qty</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "center", minWidth: "80px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>UOM</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "130px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Invoice Unit Price</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "130px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Invoice value Total</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "120px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Total Price</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "left", minWidth: "140px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>SR Category</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "100px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>SR Qty</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "120px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>SR Value</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "120px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Approve Qty</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "130px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Approve Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approveData.items.length > 0 ? (
                        approveData.items.map((item, index) => (
                          <tr
                            key={item.id}
                            style={{
                              backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                              transition: "background-color 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#f0f7ff";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#ffffff" : "#fafafa";
                            }}
                          >
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "center", border: "1px solid #dee2e6", fontWeight: 500 }}>
                              {item.sno}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", border: "1px solid #dee2e6", fontWeight: 500 }}>
                              {item.productCategory}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", border: "1px solid #dee2e6", fontWeight: 500 }}>
                              {item.plantName}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", border: "1px solid #dee2e6", fontWeight: 500 }}>
                              {item.itemDescription}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                              {item.invoiceQty}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "center", border: "1px solid #dee2e6", fontWeight: 500 }}>
                              {item.uom}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                              {item.invoiceUnitPrice}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                              {item.invoiceValueTotal}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                              {item.totalPrice}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", border: "1px solid #dee2e6", fontWeight: 500 }}>
                              {item.srCategory || "N/A"}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                              {item.srQty}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                              {item.srValue}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                              {item.approvedQty || "0"}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                              {item.approvedValue}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="14"
                            className="text-center"
                            style={{
                              padding: "40px 16px",
                              color: "#999",
                              fontSize: "14px",
                              border: "1px solid #dee2e6",
                              fontWeight: 500,
                            }}
                          >
                            No items found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Factory intent & settlement (visible to all; editable for BCM when approving) */}
              <div
                style={{
                  marginBottom: "24px",
                  padding: "16px",
                  backgroundColor: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "12px",
                  }}
                >
                  Factory intent &amp; settlement
                  {isBcmUser() && (
                    <span style={{ fontWeight: 400, fontSize: "12px", color: "#64748b", marginLeft: "8px" }}>
                      (Required when you Approve — not required for Reject)
                    </span>
                  )}
                </div>
                <div className="row g-3">
                  {(() => {
                    const fiLabel = {
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#111827",
                      marginBottom: "6px",
                      display: "block",
                    };
                    const fiControl = (editable) => ({
                      width: "100%",
                      fontSize: "15px",
                      fontWeight: 500,
                      color: "#111827",
                      padding: "10px 14px",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      backgroundColor: editable ? "#ffffff" : "#f3f4f6",
                      boxSizing: "border-box",
                      minHeight: "44px",
                    });
                    const bcm = isBcmUser();
                    return (
                      <>
                        <div className="col-md-4">
                          <label className="form-label mb-0" style={fiLabel}>
                            Factory Intent No.{bcm ? " *" : ""}
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={approveData.factoryIntentNo}
                            onChange={(e) =>
                              setApproveData((prev) => ({ ...prev, factoryIntentNo: e.target.value }))
                            }
                            disabled={!bcm}
                            placeholder="—"
                            style={fiControl(bcm)}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label mb-0" style={fiLabel}>
                            Factory Intent Date{bcm ? " *" : ""}
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={approveData.factoryIntentDate}
                            onChange={(e) =>
                              setApproveData((prev) => ({ ...prev, factoryIntentDate: e.target.value }))
                            }
                            disabled={!bcm}
                            style={fiControl(bcm)}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label mb-0" style={fiLabel}>
                            Settlement Type{bcm ? " *" : ""}
                          </label>
                          <select
                            className="form-select"
                            value={approveData.settlementType}
                            onChange={(e) =>
                              setApproveData((prev) => ({ ...prev, settlementType: e.target.value }))
                            }
                            disabled={!bcm}
                            style={fiControl(bcm)}
                          >
                            <option value="">Select…</option>
                            <option value="Replacement">Replacement</option>
                            <option value="Credit Note">Credit Note</option>
                            <option value="STO/CN Details">STO/CN Details</option>
                            <option value="Replacement with Credit Note">Replacement with Credit Note</option>
                          </select>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Remarks Section */}
              <div style={{ marginTop: "24px" }}>
                <label
                  className="form-label"
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#333",
                    marginBottom: "10px",
                    display: "block",
                    textAlign: "left",
                  }}
                >
                  Remarks
                  <span
                    style={{
                      color: "#dc3545",
                      fontWeight: 600,
                      fontSize: "14px",
                      marginLeft: "4px",
                    }}
                  >
                    *
                  </span>
                </label>
                <textarea
                  className="form-control"
                  rows="5"
                  required
                  aria-required="true"
                  value={approveData.remarks}
                  onChange={(e) =>
                    setApproveData((prev) => ({
                      ...prev,
                      remarks: e.target.value,
                    }))
                  }
                  placeholder="Enter your remarks or comments here..."
                  style={{
                    resize: "vertical",
                    fontSize: "14px",
                    padding: "12px 16px",
                    border: "1px solid #d0d0d0",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    transition: "all 0.2s",
                    lineHeight: "1.5",
                    minHeight: "120px",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#007bff";
                    e.target.style.boxShadow =
                      "0 0 0 0.2rem rgba(0, 123, 255, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d0d0d0";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className="approve-reject-modal-footer"
              style={{
                borderTop: "1px solid #dee2e6",
                padding: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "0 0 8px 8px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                className="btn"
                onClick={handleApproveRejectClose}
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                  border: "2px solid #d0d0d0",
                  color: "#495057",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  fontSize: "13px",
                  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                  borderRadius: "6px",
                  textTransform: "uppercase",
                  padding: "10px 28px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 0, 0, 0.15)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)";
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(0, 0, 0, 0.1)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <CancelIcon style={{ fontSize: "18px" }} />
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleRejectSubmit}
                style={{
                  background: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  fontSize: "13px",
                  boxShadow: "0 4px 15px rgba(220, 53, 69, 0.5)",
                  borderRadius: "6px",
                  textTransform: "uppercase",
                  padding: "10px 28px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #dc3545 0%, #c82333 100%)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(220, 53, 69, 0.7)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #dc3545 0%, #c82333 100%)";
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(220, 53, 69, 0.5)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <CloseIcon style={{ fontSize: "18px" }} />
                Reject
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleApproveSubmit}
                style={{
                  background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  fontSize: "13px",
                  boxShadow: "0 4px 15px rgba(40, 167, 69, 0.5)",
                  borderRadius: "6px",
                  textTransform: "uppercase",
                  padding: "10px 28px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #28a745 0%, #20c997 100%)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(40, 167, 69, 0.7)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #28a745 0%, #20c997 100%)";
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(40, 167, 69, 0.5)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <CheckIcon style={{ fontSize: "18px" }} />
                {isDosAdminApprover ? "Assign" : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal - Bootstrap 5 */}
      {approveOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={handleApproveClose}
          style={{ zIndex: 1040 }}
        ></div>
      )}
      <div
        className={`modal fade raise-query-modal ${approveOpen ? "show" : ""}`}
        id="approveModal"
        ref={approveModalRef}
        tabIndex="-1"
        aria-labelledby="approveModalLabel"
        aria-hidden={!approveOpen}
        style={{
          display: approveOpen ? "block" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content checklist-modal-content">
            <div className="modal-header checklist-modal-header">
              <h5
                className="modal-title checklist-modal-title"
                id="approveModalLabel"
                style={{ fontSize: "16px" }}
              >
                Approve
              </h5>
              <button
                type="button"
                className="btn-close checklist-modal-close"
                aria-label="Close"
                onClick={handleApproveClose}
              ></button>
            </div>
            <div className="modal-body checklist-modal-body">
              <div className="table-responsive">
                <table
                  className="table table-bordered complaint-table"
                  style={{ width: "100%" }}
                >
                  <thead>
                    <tr>
                      <th>Item Category</th>
                      <th>Comp Qty</th>
                      <th>Comp Value</th>
                      <th>Change Value</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontSize: "14px" }}>
                    {approveData.items.length > 0 ? (
                      approveData.items.map((item) => (
                        <tr
                          key={item.id}
                          style={{ backgroundColor: "transparent" }}
                        >
                          <td style={{ textAlign: "left" }}>
                            {item.itemCategory}
                          </td>
                          <td className="text-end dt-numeric-col">
                            {item.compQty}
                          </td>
                          <td className="text-end dt-numeric-col">
                            {item.compValue}
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.changeValue}
                              onChange={(e) =>
                                handleChangeValueUpdate(item.id, e.target.value)
                              }
                              placeholder="Enter value"
                              style={{ textAlign: "right" }}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr style={{ backgroundColor: "transparent" }}>
                        <td colSpan="4" className="text-center">
                          No items found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4">
                <label
                  className="form-label checklist-form-label"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    display: "block",
                  }}
                >
                  Remarks
                </label>
                <textarea
                  className="form-control checklist-form-control"
                  rows="5"
                  value={approveData.remarks}
                  onChange={(e) =>
                    setApproveData((prev) => ({
                      ...prev,
                      remarks: e.target.value,
                    }))
                  }
                  placeholder="Enter remarks here..."
                  style={{
                    resize: "vertical",
                    minHeight: "120px",
                  }}
                />
              </div>
            </div>
            <div
              className="modal-footer checklist-modal-footer"
              style={{
                backgroundColor: "transparent",
                borderTop: "none",
                paddingTop: "0",
              }}
            >
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleApproveClose}
                style={{
                  marginRight: "8px",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  // Add submit logic here
                  console.log("Approve Submit clicked", approveData);
                  handleApproveClose();
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Product Details Modal Backdrop */}
      {productDetailsOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={handleProductDetailsClose}
          style={{ zIndex: 1040 }}
        ></div>
      )}

      {/* Product Details Modal - Bootstrap 5 */}
      <div
        className={`modal fade ${productDetailsOpen ? "show" : ""}`}
        id="productDetailsModal"
        ref={productDetailsModalRef}
        tabIndex="-1"
        aria-labelledby="productDetailsModalLabel"
        aria-hidden={!productDetailsOpen}
        style={{
          display: productDetailsOpen ? "block" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content overflow-hidden">
            <div className="modal-header">
              <h5 className="modal-title" id="productDetailsModalLabel">
                Product Details
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleProductDetailsClose}
              ></button>
            </div>
            <div className="modal-body">
              {selectedProductData && (
                <div className="card mb-0">
                  <div className="card-body">
                    <div className="row mb-1 align-items-center">
                      <div className="col-12 col-md-3 prodDetFntsze">
                        <strong>Product Name:</strong>
                      </div>
                      <div className="col-12 col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          value={
                            selectedProductData?.ProductName ||
                            selectedProductData?.productName ||
                            selectedProductData?.Productname ||
                            ""
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="row mb-1 align-items-center">
                      <div className="col-12 col-md-3 prodDetFntsze">
                        <strong>Product Code:</strong>
                      </div>
                      <div className="col-12 col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          value={
                            selectedProductData?.ProductCode ||
                            selectedProductData?.productCode ||
                            selectedProductData?.Productcode ||
                            ""
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="row mb-1 align-items-center">
                      <div className="col-12 col-md-3 prodDetFntsze">
                        <strong>Product Group:</strong>
                      </div>
                      <div className="col-12 col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          value={
                            selectedProductData?.ProductCategory ||
                            selectedProductData?.productCategory ||
                            selectedProductData?.Productcategory ||
                            ""
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="row mb-1 align-items-center">
                      <div className="col-12 col-md-3 prodDetFntsze">
                        <strong>Design Number:</strong>
                      </div>
                      <div className="col-12 col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          value={
                            selectedProductData?.DesignNumber ||
                            selectedProductData?.designNumber ||
                            ""
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="row mb-1 align-items-center">
                      <div className="col-12 col-md-3 prodDetFntsze">
                        <strong>Product Description:</strong>
                      </div>
                      <div className="col-12 col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          value={
                            selectedProductData?.ProductDescription ||
                            selectedProductData?.productDescription ||
                            ""
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="row mb-1 align-items-center">
                      <div className="col-12 col-md-3 prodDetFntsze">
                        <strong>Product Brand:</strong>
                      </div>
                      <div className="col-12 col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          value={
                            selectedProductData?.ProductBrand ||
                            selectedProductData?.productBrand ||
                            ""
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="row mb-1 align-items-center">
                      <div className="col-12 col-md-3 prodDetFntsze">
                        <strong>Product Finish:</strong>
                      </div>
                      <div className="col-12 col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          value={
                            selectedProductData?.ProductFinish ||
                            selectedProductData?.productFinish ||
                            ""
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="row mb-1 align-items-center">
                      <div className="col-12 col-md-3 prodDetFntsze">
                        <strong>Product Thickness (mm):</strong>
                      </div>
                      <div className="col-12 col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          value={
                            selectedProductData?.ProductThickness ||
                            selectedProductData?.productThickness ||
                            ""
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="row mb-1 align-items-center">
                      <div className="col-12 col-md-3 prodDetFntsze">
                        <strong>Size (mm):</strong>
                      </div>
                      <div className="col-12 col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          value={
                            selectedProductData?.Size ||
                            selectedProductData?.size ||
                            ""
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="row mb-1 align-items-center">
                      <div className="col-12 col-md-3 prodDetFntsze">
                        <strong>Material Group Code:</strong>
                      </div>
                      <div className="col-12 col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          value={
                            selectedProductData?.MaterialGroupCode ||
                            selectedProductData?.materialGroupCode ||
                            ""
                          }
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleProductDetailsClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Component Details Modal Backdrop */}
      {componentDetailsOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={handleComponentDetailsClose}
          style={{ zIndex: 1040 }}
        ></div>
      )}

      {/* Component Details Modal - Bootstrap 5 */}
      <div
        className={`modal fade checklist-modal ${componentDetailsOpen ? "show" : ""}`}
        id="componentDetailsModal"
        ref={componentDetailsModalRef}
        tabIndex="-1"
        aria-labelledby="componentDetailsModalLabel"
        aria-hidden={!componentDetailsOpen}
        style={{
          display: componentDetailsOpen ? "block" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content checklist-modal-content">
            <div className="modal-header checklist-modal-header">
              <h5
                className="modal-title checklist-modal-title"
                id="componentDetailsModalLabel"
              >
                Component Details
              </h5>
              <button
                type="button"
                className="btn-close-checklist checklist-modal-close"
                aria-label="Close"
                onClick={handleComponentDetailsClose}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloseIcon style={{ fontSize: "24px", color: "#666" }} />
              </button>
            </div>
            <div className="modal-body checklist-modal-body">
              {/* Row 1 */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-sm-6 col-md-4">
                  <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left" }}>
                    Material Group Desc.
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control component-details-input"
                    value={componentDetailsData.materialGroupDesc}
                    readOnly
                    disabled
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-4">
                  <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left" }}>
                    Component Category
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control component-details-input"
                    value={componentDetailsData.componentCategory}
                    readOnly
                    disabled
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-4">
                  <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left" }}>
                    Component List
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control component-details-input"
                    value={componentDetailsData.componentList}
                    readOnly
                    disabled
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-sm-6 col-md-4">
                  <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left" }}>
                    Component Qty.
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="form-control checklist-form-control component-details-input component-qty-input"
                    value={componentDetailsData.componentQty}
                    readOnly
                    disabled
                    style={{ textAlign: "left", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-4">
                  <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left" }}>
                    Component Issue
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control component-details-input"
                    value={componentDetailsData.componentIssue}
                    readOnly
                    disabled
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-4">
                  <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left" }}>
                    Material Code (Plant Team)
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control component-details-input"
                    value={componentDetailsData.doorMaterialCode}
                    readOnly
                    disabled
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
              </div>

              {/* Row 3 */}
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left" }}>
                    Component Issue Remarks
                  </label>
                  <textarea
                    rows="3"
                    className="form-control checklist-form-control component-details-input"
                    value={componentDetailsData.componentIssueRemarks}
                    readOnly
                    disabled
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  ></textarea>
                </div>
              </div>
            </div>
            <div className="modal-footer checklist-modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary checklist-btn-cancel"
                onClick={handleComponentDetailsClose}
              >
                <CancelIcon fontSize="small" className="me-1" />
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Response Query Modal Backdrop */}
      {responseQueryOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={responseQuerySubmitting ? undefined : handleResponseQueryClose}
          style={{ zIndex: 1040 }}
        ></div>
      )}

      {/* Response Query Modal - Bootstrap 5 */}
      <div
        className={"modal fade" + (responseQueryOpen ? " show" : "")}
        id="responseQueryModal"
        ref={responseQueryModalRef}
        tabIndex="-1"
        aria-labelledby="responseQueryModalLabel"
        aria-hidden={!responseQueryOpen}
        style={{
          display: responseQueryOpen ? "block" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div
            className="modal-content"
            style={{
              position: "relative",
              borderRadius: "12px",
              border: "none",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
            }}
          >
            {responseQuerySubmitting && (
              <div
                role="status"
                aria-live="polite"
                aria-busy="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.88)",
                  zIndex: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "12px",
                  gap: "14px",
                }}
              >
                <div
                  className="spinner-border text-success"
                  style={{ width: "2.75rem", height: "2.75rem" }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: 600,
                  }}
                >
                  Submitting…
                </span>
              </div>
            )}
            {/* Modal Header */}
            <div
              className="modal-header"
              style={{
                borderBottom: "2px solid #f0f0f0",
                padding: "20px 24px",
                backgroundColor: "#fafafa",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <h5
                className="modal-title"
                id="responseQueryModalLabel"
                style={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#1f1f1f",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <ReplyIcon style={{ fontSize: "20px", color: "#ff8c00" }} />
                Response to Query
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() =>
                  !responseQuerySubmitting && handleResponseQueryClose()
                }
                disabled={responseQuerySubmitting}
                style={{
                  opacity: responseQuerySubmitting ? 0.35 : undefined,
                  pointerEvents: responseQuerySubmitting ? "none" : "auto",
                }}
              ></button>
            </div>

            {/* Modal Body */}
            <div
              className="modal-body"
              style={{
                padding: "24px",
                maxHeight: "70vh",
                overflowY: "auto",
              }}
            >
              {selectedQuery && (
                <>
                  {/* Query Details */}
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "16px",
                      borderRadius: "8px",
                      marginBottom: "20px",
                      borderLeft: "4px solid #ff8c00",
                    }}
                  >
                    <div style={{ marginBottom: "12px" }}>
                      <strong style={{ color: "#495057" }}>Query from: </strong>
                      <span style={{ color: "#666" }}>
                        {safeChild(selectedQuery?.raisedByName) || "Approver"}
                      </span>
                    </div>
                    <div style={{ marginBottom: "12px" }}>
                      <strong style={{ color: "#495057" }}>Query Date: </strong>
                      <span style={{ color: "#666" }}>
                        {safeChild(selectedQuery?.formattedRaisedOnForModal) || "N/A"}
                      </span>
                    </div>
                    <div>
                      <strong style={{ color: "#495057" }}>Query: </strong>
                      <p
                        style={{
                          color: "#333",
                          marginTop: "8px",
                          marginBottom: 0,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {safeChild(selectedQuery?.queryText) || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Response Text Section */}
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      className="form-label"
                      style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#333",
                        marginBottom: "10px",
                        display: "block",
                      }}
                    >
                      Your Response <span style={{ color: "red" }}>*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows="6"
                      value={responseQueryData.responseText}
                      disabled={responseQuerySubmitting}
                      onChange={(e) => {
                        setResponseQueryData((prev) => ({
                          ...prev,
                          responseText: e.target.value,
                        }));
                      }}
                      placeholder="Enter your response here..."
                      style={{
                        resize: "vertical",
                        fontSize: "14px",
                        padding: "12px 16px",
                        border: "1px solid #d0d0d0",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        transition: "all 0.2s",
                        lineHeight: "1.5",
                        minHeight: "150px",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#ff8c00";
                        e.target.style.boxShadow =
                          "0 0 0 0.2rem rgba(255, 140, 0, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d0d0d0";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>

                  {/* File Attachments Section */}
                  <div style={{ marginBottom: "20px" }}>
                    <FileUploadPanelLayout
                      title="Attachments (if required)"
                      helperText="Choose files to attach with your response"
                      formatNote="( Multiple files allowed )"
                      onChange={handleFileAttachment}
                      disabled={responseQuerySubmitting}
                      chooseStyle={{
                        opacity: responseQuerySubmitting ? 0.6 : 1,
                        pointerEvents: responseQuerySubmitting ? "none" : "auto",
                      }}
                      files={responseQueryData.attachments.map((a, i) =>
                        a instanceof File
                          ? {
                              id: `legacy-${i}-${a.name}-${a.size}`,
                              file: a,
                              name: a.name,
                              size: a.size,
                              type: a.type,
                            }
                          : { ...a, id: a.id ?? `row-${i}` }
                      )}
                      onOpen={(fileItem) => {
                        const f = fileItem.file || fileItem;
                        if (f instanceof File) {
                          const url = URL.createObjectURL(f);
                          window.open(url, "_blank");
                          setTimeout(() => {
                            try {
                              URL.revokeObjectURL(url);
                            } catch (_) {}
                          }, 60000);
                        }
                      }}
                      onRemove={removeAttachment}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div
              className="modal-footer"
              style={{
                borderTop: "2px solid #f0f0f0",
                padding: "16px 24px",
                backgroundColor: "#fafafa",
                borderRadius: "0 0 12px 12px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                type="button"
                className="btn"
                onClick={handleResponseQueryClose}
                disabled={responseQuerySubmitting}
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                  border: "1px solid #d0d0d0",
                  color: "#495057",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  padding: "8px 24px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: responseQuerySubmitting ? 0.65 : 1,
                  cursor: responseQuerySubmitting ? "not-allowed" : "pointer",
                }}
              >
                <CancelIcon style={{ fontSize: "16px" }} />
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleResponseQuerySubmit}
                disabled={responseQuerySubmitting}
                style={{
                  background: "linear-gradient(135deg, #ff8c00 0%, #ff6b00 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  boxShadow: "0 4px 15px rgba(255, 140, 0, 0.4)",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  padding: "8px 24px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: responseQuerySubmitting ? 0.85 : 1,
                  cursor: responseQuerySubmitting ? "wait" : "pointer",
                }}
              >
                <SendIcon style={{ fontSize: "16px" }} />
                Submit Response
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Previous SRs detailed view modal */}
      {showPreviousComplaintsDetailModal && (
        <div
          className="previous-srs-modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            overflow: "auto",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPreviousComplaintsDetailModal(false);
              setSelectedComplaintIndex(null);
            }
          }}
        >
          <div
            className="previous-srs-modal-container"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              width: "95%",
              maxWidth: "1100px",
              maxHeight: "90vh",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Modal Header */}
            <div
              className="previous-srs-modal-header"
              style={{
                borderBottom: "1px solid #dee2e6",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px 8px 0 0",
                flexShrink: 0,
              }}
            >
              <h3 className="previous-srs-modal-title" style={{ 
                margin: 0, 
                color: "#333", 
                fontWeight: 600,
              }}>
                Previous SRs - Detailed View
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowPreviousComplaintsDetailModal(false);
                  setSelectedComplaintIndex(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6c757d",
                  padding: "0",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Modal Body */}
            <div
              className="previous-srs-modal-body"
              style={{
                overflowY: "auto",
                overflowX: "hidden",
                flex: "1 1 auto",
                minHeight: 0,
                WebkitOverflowScrolling: "touch",
              }}
            >
              {previousComplaintsData.length === 0 || selectedComplaintIndex === null ? (
                <div style={{ textAlign: "center", padding: "24px", color: "#6c757d" }}>
                  No previous SRs data available.
                </div>
              ) : (
                (() => {
                  const complaint = previousComplaintsData[selectedComplaintIndex];
                  if (!complaint) return null;
                  const invoiceDisplay =
                    complaint.invoiceNumber ??
                    complaint.InvoiceNumber ??
                    complaint.invoiceNo ??
                    complaint.InvoiceNo ??
                    srData?.invoiceNumber ??
                    srData?.invoiceNo ??
                    "";
                  const invoiceDisplayStr =
                    invoiceDisplay != null && invoiceDisplay !== undefined
                      ? String(invoiceDisplay).trim()
                      : "";
                  const currentStatus = String(
                    complaint.statusName || complaint.StatusName || ""
                  ).trim();
                  const finalActionTaken =
                    currentStatus.toLowerCase() === "closed" ? "Closed" : "Open";
                  return (
                    <div className="previous-srs-details-card">
                      <h4 className="previous-srs-details-title">SR Details</h4>

                      <div className="row g-2">
                        <div className="col-md-3">
                          <label className="input-label">SR Number</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={complaint.srNumber || complaint.SRNumber || "N/A"}
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Invoice Number</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={invoiceDisplayStr || "N/A"}
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">SR Date</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.srDate || complaint.SRDate
                                ? (() => {
                                    const date = new Date(
                                      complaint.srDate || complaint.SRDate
                                    );
                                    const day = String(date.getDate()).padStart(2, "0");
                                    const month = String(date.getMonth() + 1).padStart(
                                      2,
                                      "0"
                                    );
                                    const year = date.getFullYear();
                                    return `${day}-${month}-${year}`;
                                  })()
                                : "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">SR Type</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.complaintType ||
                              complaint.ComplaintType ||
                              "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Product Name & Code</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.productsInvolved ||
                              complaint.ProductsInvolved ||
                              "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">SR Quantity</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.srQuantity !== undefined
                                ? complaint.srQuantity.toString()
                                : complaint.SRQuantity !== undefined
                                  ? complaint.SRQuantity.toString()
                                  : "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Approved Quantity</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.approvedQuantity !== undefined &&
                              complaint.approvedQuantity !== null
                                ? complaint.approvedQuantity.toString()
                                : complaint.ApprovedQuantity !== undefined &&
                                    complaint.ApprovedQuantity !== null
                                  ? complaint.ApprovedQuantity.toString()
                                  : "0"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Approved Value</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.approvedValue !== undefined &&
                              complaint.approvedValue !== null
                                ? `₹${parseFloat(complaint.approvedValue).toLocaleString(
                                    "en-IN",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}`
                                : complaint.ApprovedValue !== undefined &&
                                    complaint.ApprovedValue !== null
                                  ? `₹${parseFloat(
                                      complaint.ApprovedValue
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`
                                  : "₹0.00"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">SR Value</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.srValue !== undefined
                                ? `₹${parseFloat(complaint.srValue).toLocaleString(
                                    "en-IN",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}`
                                : complaint.SRValue !== undefined
                                  ? `₹${parseFloat(complaint.SRValue).toLocaleString(
                                      "en-IN",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}`
                                  : "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Final Action Taken</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={finalActionTaken}
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Current / Final Status</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={currentStatus || "N/A"}
                            readOnly
                          />
                        </div>

                        <div className="col-12">
                          <label className="input-label">Latest Comment</label>
                          <textarea
                            className="form-control form-control-light"
                            value={
                              String(
                                complaint.latestComment ||
                                  complaint.LatestComment ||
                                  ""
                              ).trim() || "N/A"
                            }
                            readOnly
                            rows="2"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>

            {/* Modal Footer */}
            <div
              className="previous-srs-modal-footer"
              style={{
                borderTop: "1px solid #dee2e6",
                display: "flex",
                justifyContent: "flex-end",
                backgroundColor: "#f8f9fa",
                borderRadius: "0 0 8px 8px",
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                className="btn-close-prev-sr"
                onClick={() => {
                  setShowPreviousComplaintsDetailModal(false);
                  setSelectedComplaintIndex(null);
                }}
                style={{
                  background: "linear-gradient(135deg, #6c757d 0%, #5a6268 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 500,
                  borderRadius: "4px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(108, 117, 125, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewApproverServiceRequest;
